import { all, takeLatest, call, put, select, delay } from 'redux-saga/effects';
import { v1 as uuidv1 } from 'uuid';
import axios from 'axios';
import { add, compareAsc, isAfter } from 'date-fns';

import notification from '~/services/notification';
import { toDecimals } from '~/utils/format';
import { formatDate, formatMessage, formatNumber } from '~/i18n/translate';
import { calculateCommissionInfo } from '~/utils/calculateCommission';
import {
  ERROR_ON_SEND_ORDER,
  ISA_ERROR,
  ORDER_RECOVERY_KEY,
  ORDER_TYPE,
  PAYMENT_OPTION_TYPE,
  REASON_ACTIONS,
  REASON_TYPE,
  SALE_TYPE,
  SHIPPING_TYPE,
  USER_ROLE
} from '~/constants';
import {
  ClientInterface,
  ConfigurationInterface,
  LoadTypeInterface,
  MarginControlInterface,
  PaymentOptionInterface,
  PaymentPlanInterface,
  ProductQuotaInterface,
  ProductSale,
  PromotionInterface,
  ReasonInterface,
  SubsidiaryInterface,
  TributacaoInterface,
  UserInterface
} from '@e3labs/isa-data-dictionary';
import ActionTypes from '~/types/action-types';
import { AppStateType } from '../root-reducer';

import {
  OrderCreateInterface,
  OrderItemCreateInterface,
  OrderItemPostInterface,
  OrderPostInterface,
  OrderPromotionInterface,
  PixPaymentProps,
  ProductModalProps,
  PromotionWithItemsInterface,
  LastOrderProductDetailsInterface
} from './order-interface';
import {
  sort,
  getLoadTypes,
  getSubsidiaryStocks,
  getReasons,
  getPriceDifference,
  getUserSubsidiaries,
  comparePrices,
  recalculatePrices,
  isPromotionGroupValidator,
  validateMargin,
  validateMultiplesSubsidiariesProduct,
  calculateTypeOperation,
  getTotalsKit,
  marginControl,
  totalizers,
  meProductRestrictions,
  getMargin,
  getMarginTotals,
  validateProductListBonification,
  groupIntoChunks,
  updatePendencies,
  getLastOrderProductDetails,
  checkBlockComboKit,
  validateBatch,
  setPaymentOptionsAndPlans,
  checkProductIsBelowMinimumPrice,
  checkProductIsAboveDiscountAllowed,
  calculateTotalMaxDiscount
} from './order-utils';
import { getPromotionValid, validatePromotions } from '../promotion/promotion-utils';
import {
  INITIALIZE_REQUEST,
  SET_CLIENT_REQUEST,
  SET_ORDER_TYPE_REQUEST,
  SET_SHIPPING_TYPE_REQUEST,
  GET_SHIPPING_REQUEST,
  SET_SUBSIDIARY_REQUEST,
  SET_SUBSIDIARY_STOCK_REQUEST,
  SET_PAYMENT_OPTION_REQUEST,
  SET_PAYMENT_PLAN_REQUEST,
  SET_PAYMENT_PLAN_GROUP_REQUEST,
  SET_LOAD_TYPE_REQUEST,
  ADD_PRODUCT_CART_REQUEST,
  REMOVE_PRODUCT_CART_REQUEST,
  LOAD_PRODUCT_REQUEST,
  UPDATE_PRODUCT_REQUEST,
  ORDER_TYPE_SEND,
  VALIDATE_ORDER_REQUEST,
  UPDATE_QUANTITY_REQUEST,
  UPDATE_SOLDPRICE_REQUEST,
  CHECK_QUANTITY_MULTIPLE,
  UPDATE_DISCOUNT_REQUEST,
  SET_PRODUCT_PACKING,
  SET_PRODUCT_PROMOTION,
  SET_BONIFICATED_REQUEST,
  SET_SHOW_MODAL_PRODUCT_REQUEST,
  SEND_ORDER,
  ORDER_STATUS,
  SET_ORDER_TYPE_BONIFICATED_REQUEST,
  VALIDATE_PASSWORD_MODAL_REASON,
  UPDATE_QUANTITY_TOTAL_UNIT_REQUEST,
  GET_PRODUCT_PROMOTION_KIT_REQUEST,
  ADD_PROMOTION_KIT_REQUEST,
  GET_PROMOTION_KIT_LIST_REQUEST,
  UPDATE_PROMOTION_KIT_REQUEST,
  SET_ORDER_BONIFICATION_REQUEST,
  SET_PRODUCT_COMPLEMENT,
  GET_ALTERNATIVE_PRODUCT_REQUEST,
  SPLIT_ORDER_VALIDATION_REQUEST,
  APPLY_DISCOUNT_TO_ALL_ITEMS_REQUEST,
  CALCULATE_BALANCE_REQUEST,
  GET_RESTRICTIONS_REQUEST,
  SET_SUBSIDIARY_PRODUCT_STOCK_REQUEST,
  SET_SUBSIDIARY_STOCK_RESTRICTIONS_REQUEST,
  UPDATE_UNITARY_VALUE_REQUEST
} from './order-constants';
import * as orderActions from './order-create-actions';
import domainService from '~/services/modules/domain/domain-service';
import userService from '~/services/modules/user/user-service';
import clientService from '../client/client-service';
import reasonService from '../reason/reason-service';
import loadTypeService from '../load-type/load-type-service';
import productService from '../product/product-service';
import sponsoredService from '../sponsored/sponsored-service';
import orderService from './order-service';
import normalizeError from '~/utils/normalize-error';
import api from '~/services/api';
import { combinePromotions, formatOrder, selectorPaymentOptions, selectorPaymentPlans } from './order-create-selectors';
import { getOrderTypeSend } from '~/utils/functions';
import promotionService from '../promotion/promotion-service';
import { selectorConfiguration } from '../configuration/configuration-selectors';
import { ModalType } from '~/types/modal-types';
import { ProductStateListInterface } from '../product/product-interface';
import { AvailablePromotionsParamsProps } from '../promotion/promotion-interface';
import { UserAuthInterface } from '../auth/auth-interface';
import { ExpiryStockProductProps } from '../client/client-interface';
import { calcPercDiscount } from '~/utils/calcPercDiscount';
import { getTax } from '../tributacao/tributacao-service';
import { getPendingPromotions } from '~/pages/main/Order/OrderCreate/components/PromotionRequirementModal/utils';

let cancel: any;
const CancelToken = axios.CancelToken;

export function* getAsyncPriceTable(subsidiary: SubsidiaryInterface, client: ClientInterface) {
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
  try {
    const { data: clientPrices } = yield call(domainService.getClientPrices, {
      skip: 0,
      limit: 0,
      clientExternalCode: client?.externalCode,
      subsidiaryExternalCode: subsidiary?.externalCode
    });
    yield put(
      orderActions.updateSelects({
        clientPrices
      })
    );
    return (
      clientPrices?.[0]?.priceTableCode ||
      client?.priceTableCode ||
      subsidiary?.defaultPriceTableCode ||
      configuration?.defaultPriceTableCode
    );
  } catch (e) {
    yield put({ type: ISA_ERROR, e });
    return client?.priceTableCode || subsidiary?.defaultPriceTableCode || configuration?.defaultPriceTableCode;
  }
}

export function* initialize({
  payload: { id, budget, copy, bonification, searchParams, complementary, clientIdToKeep }
}: ActionTypes<{
  id: string;
  budget: boolean;
  copy: boolean;
  bonification: boolean;
  searchParams: string;
  complementary: boolean;
  clientIdToKeep?: string;
}>) {
  try {
    if (!id || !copy || !bonification || !searchParams || !complementary) {
      yield put(orderActions.clearOrder());
    }

    yield put(orderActions.setLoadingData(true));

    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
    const { team, userSubsidiaries, externalCode, tenantID, userRole, accountBalance }: UserInterface = yield select(
      (state: AppStateType) => state.auth.user
    );

    let [
      { data: subsidiaries },
      { data: paymentPlans },
      { data: paymentOptions },
      { data: loadTypes },
      { data: reasons },
      { data: orderTypes },
      { data: deliveries },
      { data: variables },
      { data: users },
      { data: me },
      { data: providerDelivery },
      { data: vendors },
      { data: commissions },
      { data: bonificationTypes }
    ] = yield all([
      call(domainService.getSubsidiaries, { userSubsidiaryFilter: userSubsidiaries ?? [] }),
      call(domainService.getPaymentPlans),
      call(domainService.getPaymentOptions),
      call(loadTypeService.getAll),
      call(reasonService.getAll),
      call(domainService.getOrderTypes),
      call(orderService.getDeliveries, {}),
      call(domainService.getVariables),
      call(userService.getUsers),
      call(userService.getMe),
      call(domainService.getProviderDelivery),
      call(userService.getUsers),
      call(userService.getCommissions, externalCode),
      call(domainService.getBonificationTypes)
    ]);

    const parsedMe = meProductRestrictions(me);

    loadTypes = [
      {
        _id: '0',
        isActive: true,
        code: 'NA',
        name: 'Sem Carga',
        deliveryType: '',
        maximumDeliveryQuantity: 0,
        minimumWeightDelivery: 0,
        minimumWeightLoad: 0,
        maximumWeightLoad: 0
      },
      ...loadTypes
    ];

    paymentPlans = [...paymentPlans].filter((x: PaymentPlanInterface) => x.enabled);
    loadTypes = [...loadTypes].filter((x: LoadTypeInterface) => x.isActive);

    let orderTypesFiltered = orderTypes?.filter(orderType => orderType?.isActive);
    let marginControlColors = configuration?.useMarginControlByColor ? configuration?.marginControlByColor : [];

    if (team) {
      orderTypesFiltered = [
        ...orderTypesFiltered?.filter(orderType =>
          orderType?.team
            ? orderType?.team?.length === 0
              ? orderType
              : orderType?.team?.includes(team?._id)
            : orderType
        )
      ];
    }

    if (userSubsidiaries?.length > 0) {
      orderTypesFiltered = [
        ...orderTypesFiltered?.filter(orderType =>
          orderType?.subsidiaries
            ? orderType?.subsidiaries?.length === 0
              ? orderType
              : userSubsidiaries?.some(item => orderType?.subsidiaries?.includes(item))
            : orderType
        )
      ];
    }

    const deliveriesFiltered = deliveries?.filter(delivery => delivery.active === true);
    // const subsidiariesFiltered = getUserSubsidiaries(subsidiaries, userSubsidiaries);

    const shippingType = configuration?.freightSelected === 'fob' ? SHIPPING_TYPE.FREE : SHIPPING_TYPE.PAID;
    let subsidiary = sort(subsidiaries, false)[0];
    const subsidiaryStocks = getSubsidiaryStocks(subsidiaries, subsidiary);

    const hidePaymentPlanExternalCodes = configuration?.hidePaymentPlanExternalCodes;

    const selects = {
      subsidiaries: sort(subsidiaries, false),
      subsidiaryStocks: sort(subsidiaryStocks, false),
      subsidiariesCharge: [],
      paymentPlans: sort(paymentPlans, false, hidePaymentPlanExternalCodes) as PaymentPlanInterface[],
      paymentOptions: sort(paymentOptions, true, hidePaymentPlanExternalCodes) as PaymentOptionInterface[],
      clientPrices: [],
      reasons,
      orderTypes: sort(orderTypesFiltered),
      bonificationTypes: sort(bonificationTypes),
      deliveries: sort(deliveriesFiltered),
      variables: sort(variables),
      users: sort(users)?.map(({ name, externalCode }) => ({ externalCode, name })),
      providerDelivery: sort(providerDelivery, false),
      vendors: sort(
        vendors.filter((user: any) => user.userRole === USER_ROLE.VENDOR),
        false
      )
    };
    const subsidiaryStock = subsidiaryStocks[0];

    let data;

    const targetOrderType = bonification ? 'bonification' : ORDER_TYPE.NORMAL;
    const defaultOrdeType =
      sort(orderTypesFiltered)?.find(item => item?.type === targetOrderType) || orderTypesFiltered?.[0];

    const defaultBonificationType = bonificationTypes.length > 0 ? bonificationTypes[0].externalCode : null;

    const orderVinculatedExternalNumber = searchParams.includes('orderVinculatedExternalNumber')
      ? searchParams.split('orderVinculatedExternalNumber=')[1]
      : null;

    const childOrderExternalNumber = searchParams.includes('parentInternalCode')
      ? searchParams.split('parentInternalCode=')[1]
      : null;

    const isOrderRecovery = budget || copy ? null : JSON.parse(localStorage.getItem(ORDER_RECOVERY_KEY) ?? null);

    if (isOrderRecovery) {
      isOrderRecovery.orderDetails.creationDate = new Date();

      let subsidiaries = selects?.subsidiaries;

      if (isOrderRecovery?.client?.subsidiaries?.length > 0) {
        subsidiaries = selects?.subsidiaries?.filter(subsidiary =>
          isOrderRecovery?.client?.subsidiaries?.includes(subsidiary?.externalCode)
        );

        if (subsidiaries && !subsidiaries?.find(item => item?.externalCode === subsidiary?.externalCode)) {
          yield put(orderActions.removeProductRequest(isOrderRecovery?.orderItems));
          notification.warning(formatMessage('orderRemoveItemsSubsidiaryChangedMessage'));
          isOrderRecovery.orderDetails.subsidiary = subsidiaries[0];
        }
      }

      yield put(
        orderActions.initializeSuccess({
          data: isOrderRecovery,
          selects: {
            ...selects,
            loadTypes: getLoadTypes(loadTypes, shippingType),
            subsidiaries
          },
          selectsDefault: { ...selects, subsidiaries, defaultUserSubsidiaries: userSubsidiaries, loadTypes }
        })
      );

      yield put(orderActions.setOrderTypeRequest(isOrderRecovery?.orderType?._id));
      yield put(orderActions.calculateBalanceRequest());
      return;
    }

    if (!id) {
      data = {
        orderDetails: {
          orderType: defaultOrdeType?.type ?? ORDER_TYPE.NORMAL,
          shippingType,
          subsidiary,
          subsidiaryStock,
          paymentOption: null,
          paymentPlan: null,
          paymentPlanGroup: {
            paymentPlan1: null,
            paymentPlan2: null
          },
          bonificationApprovalSwitch: false,
          providerDeliveryExternalCode: null,
          bonificationTypeValue: defaultBonificationType,
          creationDate: new Date()
        },
        budget: {
          budgetID: new Date().getTime(),
          effectedSale: false,
          expirationDate: add(new Date(), { days: 2 }),
          isBudget: false
        },
        load: configuration?.useLoadControl
          ? {
              loadType: loadTypes[0] ?? null
            }
          : null,
        orderType: defaultOrdeType ?? null,
        bonificationTypeValue: defaultBonificationType,
        userExternalCode: externalCode,
        marginControlColors,
        restrictions: {
          user: parsedMe?.user?.productRestrictions,
          team: parsedMe?.team?.productRestrictions
        },
        commissions: commissions || []
      };
      yield put(
        orderActions.initializeSuccess({
          data,
          selects: {
            ...selects,
            loadTypes: getLoadTypes(loadTypes, shippingType)
          },
          selectsDefault: { ...selects, subsidiaries, defaultUserSubsidiaries: userSubsidiaries, loadTypes }
        })
      );

      yield put(orderActions.setOrderTypeRequest(defaultOrdeType?._id));
    } else {
      const { paymentOptions, paymentPlans } = selects;
      const { data: order } = yield call(orderService.getOrder, id);

      const { data: client } = yield call(clientService.getClient, order.client._id);

      order.client = client;
      order.orderDetails = {
        ...order.orderDetails,
        ...setPaymentOptionsAndPlans({
          bonification,
          paymentOptions,
          paymentPlans,
          paymentOptionExternalCode: order.orderDetails.paymentOptionExternalCode,
          paymentPlanExternalCode: order.orderDetails.paymentPlanExternalCode,
          usePaymentPlansGroup: order.orderDetails.usePaymentPlansGroup,
          paymentPlanGroup1ExternalCode: order.orderDetails.paymentPlanGroup1ExternalCode,
          paymentPlanGroup2ExternalCode: order.orderDetails.paymentPlanGroup2ExternalCode
        })
      };

      const { data: subsidiariesCharge } = yield call(domainService.getSubsidiariesCharge, client.externalCode);

      yield put(
        orderActions.updateSelects({
          subsidiariesCharge
        })
      );

      // if (client?.subsidiaries?.length) {
      //   selects.subsidiaries = selects.subsidiaries.filter(subsidiary =>
      //     client.subsidiaries.includes(subsidiary.externalCode)
      //   );
      // }

      let newSubsidiariesStocks = null;
      let newSubsidiaryStock = null;
      let newSubsidiaries = null;

      if (client?.subsidiaries?.length > 0) {
        newSubsidiaries = selects?.subsidiaries?.filter(subsidiary =>
          client?.subsidiaries?.includes(subsidiary?.externalCode)
        );

        subsidiary = selects?.subsidiaries?.find(subsidiary =>
          newSubsidiaries?.some(item => item?.externalCode === subsidiary?.externalCode)
        );

        newSubsidiariesStocks = getSubsidiaryStocks(selects?.subsidiaries, subsidiary) ?? null;

        newSubsidiaryStock = newSubsidiariesStocks?.find(
          subsidiaryStock => subsidiaryStock.externalCode === order.orderDetails?.subsidiaryStock
        );
      }

      const orderDetailSubsidiaryStockExternalCode = order.orderDetails?.subsidiaryStock;
      let subsidiaryStockOrderDetails = null;

      if (orderDetailSubsidiaryStockExternalCode) {
        subsidiaryStockOrderDetails = selects?.subsidiaries?.find(
          subsidiaryStock => subsidiaryStock.externalCode === orderDetailSubsidiaryStockExternalCode
        );
      }

      let { data: loadTypes } = yield call(loadTypeService.getAll);

      loadTypes = [
        {
          _id: '0',
          isActive: true,
          code: 'NA',
          name: 'Sem Carga',
          deliveryType: '',
          maximumDeliveryQuantity: 0,
          minimumWeightDelivery: 0,
          minimumWeightLoad: 0,
          maximumWeightLoad: 0
        },
        ...loadTypes
      ];

      let loadTypesParsed = getLoadTypes(loadTypes, shippingType);

      const selectedType = loadTypesParsed.find(loadType => loadType._id === data?.load?.typeId) ?? {
        _id: '0',
        isActive: true,
        code: 'NA',
        name: 'Sem Carga',
        deliveryType: '',
        maximumDeliveryQuantity: 0,
        minimumWeightDelivery: 0,
        minimumWeightLoad: 0,
        maximumWeightLoad: 0
      };

      const priceTableCode = yield call(getAsyncPriceTable, subsidiary, order.client);

      if ((copy || budget) && order.orderItems?.length > 0) {
        yield call(
          [orderService, orderService.refreshOrderItems],
          order,
          subsidiary,
          configuration,
          priceTableCode,
          paymentPlans.find(paymentPlan => paymentPlan.externalCode === order.orderDetails?.paymentPlanExternalCode),
          tenantID
        );
      }
      const products = order.orderItems;
      const BATCH_SIZE = 5;
      const productChunks = groupIntoChunks(products, BATCH_SIZE);
      const results = [];

      for (let i = 0; i < productChunks.length; i += BATCH_SIZE) {
        const currentBatch = productChunks.slice(i, i + BATCH_SIZE);
        const batchCalls = currentBatch.map(productChunk =>
          call(promotionService.getSpecificsPromotions, {
            client: order.client,
            products: productChunk,
            subsidiaryExternalCode: subsidiary?.externalCode ?? order.orderDetails?.subsidiaryExternalCode,
            paymentPlanExternalCode: order.orderDetails?.paymentPlanExternalCode,
            priceTableCode,
            sectionExternalCode: order?.orderDetails?.section?.externalCode,
            orderType: order?.orderDetails?.orderType,
            loadType: selectedType,
            shippingType
          } as AvailablePromotionsParamsProps)
        );
        const batchResults = yield all(batchCalls);
        results.push(...batchResults);
      }

      const availablePromotions = results.map(result => result.data).flat();

      //const orderValidated = yield select(validateBudgetCopy(order, availablePromotions));

      const orderWithPromotions = {
        ...order,
        orderDetails: {
          ...order.orderDetails,
          subsidiary,
          subsidiaryStock: newSubsidiaryStock
        },
        orderItems: order.orderItems.map(item => {
          let promotionsForProduct: any[] = [];

          for (const promoObj of availablePromotions) {
            const promoData = promoObj[item.productExternalCode];
            if (promoData) {
              promotionsForProduct = promotionsForProduct.concat(promoData);
            }
          }

          return {
            ...item,
            promotions: promotionsForProduct
          };
        })
      };

      const _totalizers = totalizers({
        orderItems: orderWithPromotions?.orderItems,
        precision: 2,
        tenantId: tenantID
      });

      const marginControlItems = marginControl({
        financialCostTotal: _totalizers.financialCostTotal,
        productsTotal: _totalizers.productsTotal,
        productsTotalBonificated: _totalizers.productsTotalBonificated,
        configuration
      });

      let marginControlCalc = {
        ...marginControlItems,
        totalMarginPercentage: marginControlItems?.marginPercentage,
        totalMarginValue: marginControlItems?.marginValue
      };

      data = yield select(
        formatOrder(
          orderWithPromotions,
          selects,
          copy,
          bonification,
          complementary,
          childOrderExternalNumber,
          orderVinculatedExternalNumber,
          true,
          userRole,
          commissions,
          client,
          priceTableCode,
          accountBalance
        )
      );

      yield put(
        orderActions.initializeSuccess({
          data: {
            ...data,
            orderDetails: {
              ...data.orderDetails,
              subsidiaryStock: subsidiaryStockOrderDetails
            },
            orderNumber: order?.orderNumber,
            load: {
              loadType: selectedType ?? null
            },
            totalsMargin: {
              ...marginControlCalc
            },
            marginControlColors,
            restrictions: {
              user: parsedMe?.user?.productRestrictions,
              team: parsedMe?.team?.productRestrictions
            },
            commissions: commissions || []
          },
          selects: {
            ...selects,
            loadTypes: loadTypesParsed,
            subsidiaries: newSubsidiaries,
            subsidiaryStocks: newSubsidiariesStocks
          },
          selectsDefault: { ...selects, loadTypes }
        })
      );

      // atualiza o state para poder fazer a validação de bonificados
      const updatedState = yield select(state => state.order.form.data);

      const newReasonsSelects = yield getReasons(configuration, selects.reasons, order.client, subsidiary);

      const newPaymentPlansSelects = yield select(
        selectorPaymentPlans(
          order.client,
          data.orderDetails?.paymentOption,
          newReasonsSelects.reasons,
          defaultOrdeType,
          configuration.hidePaymentPlanExternalCodes
        )
      );

      const newPaymentOptionsSelects = yield select(selectorPaymentOptions(order.client));

      if (
        bonification &&
        newPaymentPlansSelects?.paymentPlans?.length === 0 &&
        newPaymentPlansSelects?.paymentPlan === null
      ) {
        yield put(
          orderActions.updateSelects({
            paymentOptions: [],
            paymentPlans: []
          })
        );

        return notification.error('Não existe nenhum plano de pagamento cadastrado para o tipo de pedido bonificação');
      }
      yield put(orderActions.calculateBalanceRequest());
      yield put(
        orderActions.updateSelects({
          ...selects,
          paymentOptions: newPaymentOptionsSelects.paymentOptions,
          paymentPlans: newPaymentPlansSelects.paymentPlans,
          ...(data?.orderType?.subsidiaries?.length && {
            subsidiaries: selects?.subsidiaries?.filter(subsidiary =>
              data?.orderType?.subsidiaries.includes(subsidiary?.externalCode)
            )
          }),
          paymentPlan: newPaymentPlansSelects.paymentPlan
        })
      );
    }

    if (
      bonification &&
      configuration?.allowVinculatedBonification &&
      !orderVinculatedExternalNumber &&
      !childOrderExternalNumber
    ) {
      yield put(orderActions.setOpenModalBonification({ show: true }));
    }
    if (clientIdToKeep) {
      yield call(setClient, { _id: clientIdToKeep });
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(orderActions.setLoadingData(false));
  }
}

export function* setClient({ _id }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));
  try {
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state?.configuration);
    const { selects, selectsDefault, data } = yield select((state: AppStateType) => state?.order?.form);
    const order: OrderCreateInterface = { ...data };
    const user: UserInterface = yield select((state: AppStateType) => state.auth.user);

    let subsidiary = order?.orderDetails?.subsidiary as SubsidiaryInterface;

    let subsidiaries = selects?.subsidiaries;
    let subsidiariesStocks = selects?.subsidiaryStocks ?? null;

    if (!_id) {
      yield put(
        orderActions.setClientSuccess({
          client: null,
          paymentOption: null,
          paymentPlan: null
        })
      );

      yield put(orderActions.updateSelects({ subsidiaries: selectsDefault?.defaultUserSubsidiaries }));
      toast.close();
      return;
    }

    let { data: client }: { data: ClientInterface } = yield call(clientService.getClient, _id);

    if (client?.subsidiaries?.length > 0) {
      subsidiaries = selects?.subsidiaries?.filter(subsidiary =>
        client?.subsidiaries?.includes(subsidiary?.externalCode)
      );

      subsidiary = selectsDefault?.subsidiaries?.find(subsidiary =>
        subsidiaries?.some(item => item?.externalCode === subsidiary?.externalCode)
      );

      subsidiariesStocks = getSubsidiaryStocks(selectsDefault?.subsidiaries, subsidiary) ?? null;
    }

    const [
      { data: ratings },
      { data: nextVisitClients },
      { data: questionary },
      { data: subsidiariesCharge },
      { data: custumizableParameters }
    ] = yield all([
      call(clientService.getAllRatings, {
        clientExternalCode: client?.externalCode
      }),
      call(clientService.getClientsRoute),
      call(clientService.getQuestionary),
      call(domainService.getSubsidiariesCharge, client.externalCode),
      call(userService.getCustumizableParameters)
    ]);

    yield put(
      orderActions.updateSelects({
        subsidiariesCharge
      })
    );

    const clientVisits = nextVisitClients.map(nextVisit => {
      return {
        externalCode: nextVisit.clientExternalCode,
        dateNextVisit: nextVisit.nextVisit
      };
    });

    client = {
      ...client,
      nextVisit: clientVisits.find(item => item.externalCode === client.externalCode)
    };

    const keepLastPaymentOptionPaymentPlan = custumizableParameters.find(
      param => param.key === 'keepLastPaymentOptionPaymentPlan'
    );
    let defaultPaymentOptionCode = client?.paymentOptionExternalCode || null;
    let defaultPaymentPlanCode = client?.paymentPlanExternalCode || null;

    if (JSON.parse(keepLastPaymentOptionPaymentPlan?.value ?? 'false')) {
      const { data: lastOrderDetail } = yield call(orderService.getLastOrderDetail, client?.externalCode);
      if (lastOrderDetail) {
        defaultPaymentOptionCode = lastOrderDetail.paymentOptionExternalCode;
        defaultPaymentPlanCode = lastOrderDetail.paymentPlanExternalCode;
      }
    }

    const { paymentOption, paymentOptions } = yield select(selectorPaymentOptions(client, defaultPaymentOptionCode));

    const { reasons, blockingReasons } = yield getReasons(configuration, selects.reasons, client, subsidiary);

    const { paymentPlan, paymentPlanGroup, paymentPlans } = yield select(
      selectorPaymentPlans(
        client,
        paymentOption,
        reasons,
        order?.orderType,
        configuration.hidePaymentPlanExternalCodes,
        defaultPaymentPlanCode
      )
    );

    const blockOrder = reasons?.some((reason: any) => reason?.actions?.orders === REASON_ACTIONS.DENY);

    const anlyCashAllowed =
      reasons?.some((reason: any) => reason?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED) ?? false;

    // if (!!order.orderItems.length) {
    //   yield put(orderActions.clearAllCart());

    //   yield notification.confirm(
    //     formatMessage('commonLabelWarning'),
    //     formatMessage('orderWarningClientChanged'),
    //     formatMessage('commonLabelOK'),
    //     '',
    //     false
    //   );
    // }

    if (blockOrder) {
      const validateReasonDenialUserAndSubsidiary = localReason => {
        if (localReason?.actions?.orders !== REASON_ACTIONS.DENY) return false;
        const { userExternalCode, subsidiariesExternalCodes, subsidiaryExternalCode } = localReason || {};
        return (
          (!userExternalCode?.length || userExternalCode.includes(user.externalCode)) &&
          (!subsidiariesExternalCodes?.length || subsidiariesExternalCodes.includes(subsidiary.externalCode)) &&
          (!subsidiaryExternalCode || subsidiaryExternalCode === subsidiary.externalCode)
        );
      };
      if (validateReasonDenialUserAndSubsidiary(blockingReasons?.[REASON_TYPE.CLIENTE_BLOCK])) {
        notification.warning(
          formatMessage('orderClientBlockNewOrders', {
            blockedDate: formatDate(client?.blockedDate),
            description: blockingReasons[REASON_TYPE.CLIENTE_BLOCK]?.description
          })
        );
        return;
      } else if (
        blockingReasons?.[REASON_TYPE.NON_PAYMENT]?.actions?.orders === REASON_ACTIONS.DENY &&
        (!blockingReasons?.[REASON_TYPE.NON_PAYMENT]?.userExternalCode ||
          blockingReasons?.[REASON_TYPE.NON_PAYMENT]?.userExternalCode?.length === 0 ||
          blockingReasons?.[REASON_TYPE.NON_PAYMENT]?.userExternalCode?.find(item => item === user.externalCode)) &&
        (!blockingReasons?.[REASON_TYPE.NON_PAYMENT]?.subsidiaryExternalCode ||
          blockingReasons?.[REASON_TYPE.NON_PAYMENT]?.subsidiaryExternalCode === subsidiary.externalCode) &&
        (!blockingReasons?.[REASON_TYPE.NON_PAYMENT]?.subsidiariesExternalCodes ||
          blockingReasons?.[REASON_TYPE.NON_PAYMENT]?.subsidiariesExternalCodes.length === 0 ||
          blockingReasons?.[REASON_TYPE.NON_PAYMENT]?.subsidiariesExternalCodes?.find(
            item => item === subsidiary.externalCode
          ))
      ) {
        notification.warning(
          formatMessage('orderClientBlockCreditExceeded', {
            description: blockingReasons[REASON_TYPE.NON_PAYMENT]?.description
          })
        );
        return;
      } else if (
        blockingReasons?.[REASON_TYPE.CREDIT_EXCEEDED]?.orders === REASON_ACTIONS.DENY &&
        (blockingReasons?.[REASON_TYPE.CREDIT_EXCEEDED]?.userExternalCode.length === 0 ||
          blockingReasons?.[REASON_TYPE.CREDIT_EXCEEDED]?.userExternalCode.find(item => item === user.externalCode)) &&
        !blockingReasons?.[REASON_TYPE.CREDIT_EXCEEDED]?.subsidiaryExternalCode &&
        blockingReasons?.[REASON_TYPE.CREDIT_EXCEEDED]?.subsidiariesExternalCodes.length === 0
      ) {
        notification.warning(
          formatMessage('orderClientBlockNonPayment', {
            description: blockingReasons[REASON_TYPE.CREDIT_EXCEEDED]?.description
          })
        );
        return;
      } else if (
        blockingReasons?.[REASON_TYPE.SUBSIDIARY_BLOCKED]?.orders === REASON_ACTIONS.DENY &&
        (blockingReasons?.[REASON_TYPE.SUBSIDIARY_BLOCKED]?.userExternalCode.length === 0 ||
          blockingReasons?.[REASON_TYPE.SUBSIDIARY_BLOCKED]?.userExternalCode.find(
            item => item === user.externalCode
          )) &&
        !blockingReasons?.[REASON_TYPE.SUBSIDIARY_BLOCKED]?.subsidiaryExternalCode &&
        blockingReasons?.[REASON_TYPE.SUBSIDIARY_BLOCKED]?.subsidiariesExternalCodes.length === 0
      ) {
        notification.warning(
          formatMessage('orderClientBlockSubsidiary', {
            description: blockingReasons[REASON_TYPE.SUBSIDIARY_BLOCKED]?.description
          })
        );
        return;
      } else if (
        blockingReasons?.[REASON_TYPE.SALES_RESTRICTION]?.orders === REASON_ACTIONS.DENY &&
        (blockingReasons?.[REASON_TYPE.SALES_RESTRICTION]?.userExternalCode.length === 0 ||
          blockingReasons?.[REASON_TYPE.SALES_RESTRICTION]?.userExternalCode.find(
            item => item === user.externalCode
          )) &&
        !blockingReasons?.[REASON_TYPE.SALES_RESTRICTION]?.subsidiaryExternalCode &&
        blockingReasons?.[REASON_TYPE.SALES_RESTRICTION]?.subsidiariesExternalCodes.length === 0
      ) {
        notification.warning(
          formatMessage('orderClientBlockSalesRestriction', {
            description: blockingReasons[REASON_TYPE.SALES_RESTRICTION]?.description
          })
        );
        return;
      } else if (
        blockingReasons?.[REASON_TYPE.VISIT_DATE]?.orders === REASON_ACTIONS.DENY &&
        (blockingReasons?.[REASON_TYPE.VISIT_DATE]?.userExternalCode.length === 0 ||
          blockingReasons?.[REASON_TYPE.VISIT_DATE]?.userExternalCode.find(item => item === user.externalCode)) &&
        !blockingReasons?.[REASON_TYPE.VISIT_DATE]?.subsidiaryExternalCode &&
        blockingReasons?.[REASON_TYPE.VISIT_DATE]?.subsidiariesExternalCodes.length === 0
      ) {
        notification.warning(
          formatMessage('orderClientBlockNextVisit', {
            description: blockingReasons[REASON_TYPE.VISIT_DATE]?.description
          })
        );
        return;
      }

      if (blockingReasons[REASON_TYPE.VISIT_DATE]?.actions?.orders === REASON_ACTIONS.DENY) {
        toast.close();

        if (
          blockingReasons[REASON_TYPE.VISIT_DATE]?.token?.active &&
          (blockingReasons[REASON_TYPE.VISIT_DATE]?.token?.userExternalCode?.length === 0 ||
            blockingReasons[REASON_TYPE.VISIT_DATE]?.token?.userExternalCode?.find(item => item === user?.externalCode))
        ) {
          yield put(
            orderActions.setShowModalReason({
              show: true,
              data: blockingReasons[REASON_TYPE.VISIT_DATE]
            })
          );
        } else {
          toast.close();
          yield put(
            orderActions.setClientSuccess({
              client: null,
              paymentOption: null,
              paymentPlan: null
            })
          );

          return;
        }
      }
    } else if (anlyCashAllowed) {
      if (blockingReasons[REASON_TYPE.CLIENTE_BLOCK]?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED) {
        notification.warning(
          formatMessage('orderClientBlockAnlyCashWithDate', {
            blockedDate: formatDate(client?.blockedDate),
            description: blockingReasons[REASON_TYPE.CLIENTE_BLOCK]?.description
          })
        );
      } else if (
        blockingReasons[REASON_TYPE.CREDIT_EXCEEDED]?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED ||
        blockingReasons[REASON_TYPE.NON_PAYMENT]?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED
      ) {
        notification.warning(
          formatMessage('orderClientBlockAnlyCash', {
            description: blockingReasons[REASON_TYPE.CREDIT_EXCEEDED]
              ? blockingReasons[REASON_TYPE.CREDIT_EXCEEDED]?.description
              : blockingReasons[REASON_TYPE.NON_PAYMENT]?.description
          })
        );
      } else if (
        blockingReasons[REASON_TYPE.SUBSIDIARY_BLOCKED]?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED
      ) {
        notification.warning(
          formatMessage('orderSubsidiaryBlockAnlyCash', {
            description: blockingReasons[REASON_TYPE.SUBSIDIARY_BLOCKED]?.description
          })
        );
      } else if (blockingReasons[REASON_TYPE.VISIT_DATE]?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED) {
        notification.warning(
          formatMessage('orderClientBlockNextVisitAnlyCash', {
            description: blockingReasons[REASON_TYPE.VISIT_DATE]?.description
          })
        );
      } else if (blockingReasons[REASON_TYPE.SALES_RESTRICTION]?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED) {
        notification.warning(
          formatMessage('orderSubsidiariesBlockAnlyCash', {
            description: blockingReasons[REASON_TYPE.SALES_RESTRICTION]?.description
          })
        );
      }
    }

    let orderTypesFiltered = selectsDefault?.orderTypes;

    const reason: ReasonInterface = selects?.reasons?.find(
      x => x.externalCode === (client?.blockReason ?? configuration?.blockReason)
    );

    if (
      (!client?.subsidizedSale && !configuration?.useBonificationSale && reason) ||
      (!client?.subsidizedSale &&
        !configuration?.useBonificationSale &&
        reason?.actions?.bonifications !== REASON_ACTIONS.ALLOW)
    ) {
      orderTypesFiltered = [...orderTypesFiltered.filter(item => item?.type !== ORDER_TYPE.BONIFICATION)];
    }

    const priceTableCode = yield call(getAsyncPriceTable, subsidiary, client);

    const { data: sponsoredProducts } = yield call(sponsoredService.getAvailableOrderSponsored, {
      priceTableCode,
      subsidiaryExternalCode: subsidiary?.externalCode ?? order?.orderDetails?.subsidiary?.externalCode
    });

    yield put(
      orderActions.updateSelects({
        paymentOptions,
        paymentPlans,
        orderTypes: orderTypesFiltered,
        sponsoredProducts: sponsoredProducts,
        subsidiaries,
        subsidiaryStocks: subsidiariesStocks
      })
    );

    let defaultDelivery;

    if (client?.deliveryProviderExternalCode) {
      defaultDelivery = client?.deliveryProviderExternalCode;
    } else {
      defaultDelivery = configuration?.defaultDeliveryControl;
    }

    const vendorDefault =
      user?.userRole === USER_ROLE.TELEMARKETING
        ? selects?.users?.find(user => user?.externalCode === client?.mainVendorExternalCode)
        : undefined;
    yield put(
      orderActions.updateOrder({
        ...order,
        client: {
          ...client,
          ratings,
          questionary
        },
        delivery: selects?.deliveries?.find(delivery => delivery?.externalCode === defaultDelivery) ?? null,
        orderDetails: {
          ...order.orderDetails,
          orderType: order?.orderType?.type ?? order?.orderDetails?.orderType ?? ORDER_TYPE.NORMAL,
          deliveryExternalCode: defaultDelivery ?? null,
          priceTableCode,
          paymentOption,
          paymentPlan,
          paymentPlanGroup,
          subsidiary,
          subsidiaryStock: subsidiariesStocks?.[0] ?? null,
          clientGovernmentalCode: client?.governmentalCode ?? null,
          ...(vendorDefault && { vendor: vendorDefault })
        }
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* checkRequiredBasket() {
  const { selectsDefault, data } = yield select((state: AppStateType) => state?.order?.form);
  const typeBasket = selectsDefault?.orderTypes?.find(type => type.type === 'basket');
  if (!typeBasket) {
    return true;
  }
}

export function* setOrderType({ id }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
  const { userSubsidiaries } = yield select((state: AppStateType) => state?.auth?.user);
  const { selects, selectsDefault, data } = yield select((state: AppStateType) => state?.order?.form);

  let isModal = data?.orderItems?.length > 0;

  if (isModal) {
    const { isConfirmed } = yield notification.confirm(
      formatMessage('commonLabelWarning'),
      formatMessage('orderWarningCartClean')
    );

    if (!isConfirmed) {
      return;
    }
  }

  const orderType = selectsDefault?.orderTypes?.find(type => type._id === id);

  let newSubsidiaries: SubsidiaryInterface[] = [];

  if (orderType?.subsidiaries?.length === 0) {
    newSubsidiaries = getUserSubsidiaries(selectsDefault.subsidiaries, userSubsidiaries);
  } else {
    newSubsidiaries =
      userSubsidiaries?.length > 0
        ? selects?.subsidiaries?.filter(subsidiary => userSubsidiaries.includes(subsidiary?.externalCode))
        : selects?.subsidiaries?.filter(subsidiary => orderType?.subsidiaries.includes(subsidiary?.externalCode));
  }

  const newSubsidiary = !!newSubsidiaries.find(
    subsidiary => subsidiary.externalCode === data?.orderDetails?.subsidiary?.externalCode
  )
    ? data?.orderDetails?.subsidiary
    : newSubsidiaries[0];

  try {
    yield put(orderActions.setOrderBonificationSuccess(null));

    yield put(orderActions.setOrderTypeSuccess({ orderType, newSubsidiary, newSubsidiaries }));

    const { reasons } = yield getReasons(configuration, selects?.reasons, data?.cliente, newSubsidiary);
    const { paymentOption, paymentOptions } = yield select(selectorPaymentOptions(data?.client));

    const { paymentPlans } = yield select(
      selectorPaymentPlans(
        data?.client,
        paymentOption,
        reasons,
        data?.orderType,
        configuration.hidePaymentPlanExternalCodes
      )
    );

    if (orderType?.type === ORDER_TYPE.BONIFICATION && configuration?.allowVinculatedBonification) {
      yield put(orderActions.setOpenModalBonification({ show: true }));
      const newDefaultBonificationType = selects?.bonificationTypes?.filter(
        item => item.order == orderType.externalCode
      )[0];
      yield put(orderActions.setBonificationTypeRequest(newDefaultBonificationType?.externalCode));
    }

    yield put(
      orderActions.updateSelects({
        paymentPlans,
        paymentOptions
      })
    );

    yield put(orderActions.setPaymentOptionRequest(paymentOption));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* setOrderBonification({ payload }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

  try {
    if (
      configuration?.bonificationApproval &&
      configuration?.allowBonificationOnPendingOrders &&
      configuration?.allowVinculatedBonification
    ) {
      yield put(
        orderActions.setOrderBonificationSuccess({
          vinculatedOrder: payload,
          parentInternalCode: payload?.internalCode
        })
      );

      yield put(orderActions?.setBonificationBound(true));
    } else {
      yield put(
        orderActions.setOrderBonificationSuccess({
          orderVinculatedExternalNumber: payload?.orderNumber,
          vinculatedOrder: payload
        })
      );
    }

    yield put(orderActions.setOpenModalBonification({ show: false }));

    notification.success(
      formatMessage('orderCreateModalBonificationMessageSuccess', { orderNumber: payload?.orderNumber })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* setShippingType({ shippingType }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));
  try {
    const { selectsDefault, data } = yield select((state: AppStateType) => state?.order?.form);
    const order: any = { ...data };

    const loadTypes = getLoadTypes(selectsDefault?.loadTypes, shippingType);
    yield put(
      orderActions.updateSelects({
        loadTypes
      })
    );

    yield put(
      orderActions.updateOrder({
        ...order,
        orderDetails: {
          ...order.orderDetails,
          shippingType
        },
        load: loadTypes[0]
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* setSubsidiary({ subsidiary }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
    const { selectsDefault, data, selects } = yield select((state: AppStateType) => state?.order?.form);
    const order: OrderCreateInterface = { ...data };

    let isModal = order?.orderItems?.length;

    if (isModal) {
      const { isConfirmed } = yield notification.confirm(
        formatMessage('commonLabelWarning'),
        formatMessage('orderWarningCartCleanSubsidiary')
      );

      if (!isConfirmed) {
        return;
      }
    }

    const client = order?.client as ClientInterface;
    const subsidiaryStocks = getSubsidiaryStocks(selectsDefault?.subsidiaries, subsidiary);
    const priceTableCode = yield call(getAsyncPriceTable, subsidiary, client);

    const { reasons, blockingReasons } = yield getReasons(configuration, selects.reasons, client, subsidiary);

    const blockOrder = reasons?.some((reason: any) => reason?.actions?.orders === REASON_ACTIONS.DENY);

    if (blockOrder) {
      if (blockingReasons[REASON_TYPE.SUBSIDIARY_BLOCKED]?.actions?.orders === REASON_ACTIONS.DENY) {
        notification.warning(
          formatMessage('orderClientBlockSubsidiary', {
            description: blockingReasons[REASON_TYPE.SUBSIDIARY_BLOCKED]?.description
          })
        );

        toast.close();

        yield put(
          orderActions.updateSelects({
            paymentOptions: [],
            subsidiaryStocks: [],
            sponsoredProducts: []
          })
        );

        yield put(
          orderActions.setClientSuccess({
            client: null,
            paymentOption: null,
            paymentPlan: null
          })
        );

        return;
      }
    }

    yield put(
      orderActions.updateOrder({
        ...order,
        orderDetails: {
          ...order.orderDetails,
          priceTableCode,
          subsidiary,
          subsidiaryStock:
            subsidiaryStocks?.find(subStock => subsidiary?.externalCode === subStock?.externalCode) ??
            subsidiaryStocks[0] ??
            null
        }
      })
    );

    if (order?.orderItems?.length) {
      const recalculatedPrices = yield call(recalculatePrices, {
        client,
        subsidiary: order?.orderDetails?.subsidiary,
        subsidiaryStock: order?.orderDetails?.subsidiaryStock,
        paymentPlan: order?.orderDetails?.paymentPlan,
        paymentOption: order?.orderDetails?.paymentOption,
        paymentByGroup: order?.orderDetails?.paymentPlanGroup,
        shippingType: order?.orderDetails?.shippingType,
        loadType: order?.orderDetails.orderType,
        orderItems: order?.orderItems,
        orderType: order?.orderType?.type
      });
      const updateProductsInCart = recalculatedPrices.map(item => call(updateProduct, { payload: item }));
      yield all(updateProductsInCart);
    }

    const { paymentOption, paymentOptions } = yield select(selectorPaymentOptions(data?.client));

    const { data: sponsoredProducts } = yield call(sponsoredService.getAvailableOrderSponsored, {
      priceTableCode,
      subsidiaryExternalCode: subsidiary?.externalCode
    });

    yield put(
      orderActions.updateSelects({
        paymentOptions,
        subsidiaryStocks,
        sponsoredProducts
      })
    );

    yield put(orderActions.setPaymentOptionRequest(paymentOption));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* setSubsidiaryStock({ subsidiaryStock }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    yield put(orderActions.setSubsidiaryStockSuccess(subsidiaryStock));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* setPaymentOption({ paymentOption }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
    const { selects, data } = yield select((state: AppStateType) => state?.order?.form);

    const { orderItems, orderDetails, orderType }: OrderCreateInterface = data;

    const { client }: ClientInterface = yield select((state: AppStateType) => state?.order?.form?.data);

    let isModal = orderItems?.length && orderDetails?.paymentOption !== null;
    const isOrderRecovery = JSON.parse(localStorage.getItem(ORDER_RECOVERY_KEY) ?? null);

    if (isModal && !isOrderRecovery) {
      const { isConfirmed } = yield notification.confirm(
        formatMessage('commonLabelWarning'),
        formatMessage('orderWarningCartCleanPaymentOption')
      );

      if (!isConfirmed) {
        return;
      }
    }

    localStorage?.removeItem(ORDER_RECOVERY_KEY);

    if (client) {
      if (paymentOption === null || paymentOption === undefined || !paymentOption) {
        yield put(orderActions.setPaymentOptionSuccess(null));
        yield put(orderActions.setPaymentPlanSuccess(null));
        yield put(orderActions.setPaymentPlanGroupSuccess(null));
        return;
      }

      const { reasons } = yield getReasons(configuration, selects.reasons, client, orderDetails?.subsidiary);

      const { paymentPlan, paymentPlanGroup, paymentPlans } = yield select(
        selectorPaymentPlans(
          client,
          paymentOption,
          reasons,
          data?.orderType,
          configuration.hidePaymentPlanExternalCodes
        )
      );

      if (paymentPlans?.length === 0 && !paymentPlan) {
        notification.warning('Nenhum plano de pagamento foi encontrado.');
      }

      if (paymentOption) yield put(orderActions.setPaymentOptionSuccess(paymentOption));
      if (paymentPlan) {
        yield put(orderActions.setPaymentPlanSuccess(paymentPlan));
      } else {
        yield put(orderActions.setPaymentPlanSuccess(null));
      }
      if (paymentPlanGroup) yield put(orderActions.setPaymentPlanGroupSuccess(paymentPlanGroup));

      yield put(
        orderActions.updateSelects({
          paymentPlans: paymentPlans ?? []
        })
      );

      if (paymentPlan?.providerRestriction?.length) {
        yield put(orderActions.clearAllCart());

        if (isModal) {
          yield put(orderActions.clearAllCart());
        }
      } else if (orderItems.length) {
        const recalculatedPrices = yield call(recalculatePrices, {
          client,
          subsidiary: orderDetails?.subsidiary,
          subsidiaryStock: orderDetails?.subsidiaryStock,
          paymentPlan,
          paymentOption,
          paymentByGroup: paymentPlanGroup ?? orderDetails?.paymentPlanGroup,
          shippingType: orderDetails?.shippingType,
          loadType: orderDetails.orderType,
          orderItems,
          orderType: orderType?.type
        });
        const updateProductsInCart = recalculatedPrices.map(item => call(updateProduct, { payload: item }));
        yield all(updateProductsInCart);
      }
      toast.close();
    }
    toast.close();
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* setPaymentPlan({ paymentPlan }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const { data } = yield select((state: AppStateType) => state?.order?.form);

    const { client }: ClientInterface = yield select((state: AppStateType) => state?.order?.form?.data);

    const { orderItems, orderDetails, orderType }: OrderCreateInterface = yield select(
      (state: AppStateType) => state?.order?.form?.data
    );

    let isModal = orderItems?.length && orderDetails?.paymentPlan !== null;

    if (isModal) {
      const { isConfirmed } = yield notification.confirm(
        formatMessage('commonLabelWarning'),
        formatMessage('orderWarningCartCleanPaymentPlan')
      );

      if (!isConfirmed) {
        return;
      }
    }
    if (client) {
      if (paymentPlan === null) {
        yield put(orderActions.setPaymentOptionSuccess(null));
        yield put(orderActions.setPaymentPlanSuccess(null));
        yield put(orderActions.setPaymentPlanGroupSuccess(null));

        yield put(
          orderActions.updateSelects({
            paymentPlans: []
          })
        );

        if (isModal) {
          yield put(orderActions.clearAllCart());
        }
      } else {
        if (data.orderDetails.paymentPlan?.providerRestriction?.length) {
          yield put(orderActions.clearAllCart());

          if (isModal) {
            yield put(orderActions.clearAllCart());
          }
        } else {
          yield put(orderActions.setPaymentPlanSuccess(paymentPlan));
          if (orderItems.length) {
            const recalculatedPrices = yield call(recalculatePrices, {
              client,
              subsidiary: orderDetails?.subsidiary,
              subsidiaryStock: orderDetails?.subsidiaryStock,
              paymentPlan,
              paymentOption: orderDetails?.paymentOption,
              paymentByGroup: orderDetails?.paymentPlanGroup,
              shippingType: orderDetails?.shippingType,
              loadType: orderDetails.orderType,
              orderItems: orderItems,
              orderType: orderType?.type
            });

            const updateProductsInCart = recalculatedPrices.map(item => call(updateProduct, { payload: item }));
            yield all(updateProductsInCart);
          }

          toast.close();
        }
      }
    }
    toast.close();
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* setPaymentPlanGroup({ paymentPlan, key }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const { orderItems, orderDetails }: OrderCreateInterface = yield select(
      (state: AppStateType) => state.order.form.data
    );

    let isModal = orderItems?.length && orderDetails?.paymentPlanGroup?.[`paymentPlan${key}`] !== null;

    if (isModal) {
      const { isConfirmed } = yield notification.confirm(
        formatMessage('commonLabelWarning'),
        formatMessage('orderWarningCartClean')
      );

      if (!isConfirmed) {
        return;
      }
    }

    if (paymentPlan?.providerRestriction?.length) {
      yield put(orderActions.clearAllCart());

      if (isModal) {
        notification.warning(formatMessage('orderWarningCartWasCleared'));
      }
    }

    yield put(
      orderActions.setPaymentPlanGroupSuccess({
        ...orderDetails?.paymentPlanGroup,
        [`paymentPlan${key}`]: paymentPlan
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* setLoadType({ loadType }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    yield put(orderActions.setLoadTypeSuccess(loadType));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* getProductModal({ payload }: any) {
  const {
    data: { orderItems },
    selects
  }: { data: { orderItems: OrderItemCreateInterface[] }; selects: any } = yield select(
    (state: AppStateType) => state.order?.form
  );

  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  let modalProps: ModalType<ProductModalProps> = payload;

  yield put(orderActions.setLoadingData(true));

  try {
    if (payload?.data) {
      const showModalProductVariant: ModalType<ProductModalProps> = yield select(
        (state: AppStateType) => state.order?.form?.showModalProductVariant
      );

      if (!modalProps.show) {
        yield put(
          orderActions.setShowModalProductDetail({
            show: false,
            data: null
          })
        );

        yield put(
          orderActions.setShowModalProductVariant({
            show: false,
            data: null
          })
        );

        return;
      }

      const isAlreadyInCart = !!orderItems.filter(
        orderItem => orderItem?.externalCode === modalProps?.data?.externalCode
      ).length;

      const isSponsored =
        modalProps.data?.isSponsored ??
        !!selects?.sponsoredProducts?.find(product => product?.externalCode === modalProps?.data?.externalCode);
      modalProps = {
        ...modalProps,
        data: {
          ...modalProps.data,
          ...showModalProductVariant?.data,
          availableQuantity: payload?.data?.availableQuantity,
          isEdit: !!modalProps?.data?.cartId,
          isSponsored
        }
      };

      if (!showModalProductVariant.show) {
        if (isAlreadyInCart && !modalProps.data?.cartId) {
          return yield put(orderActions.setShowModalProductVariant(modalProps));
        }
      }

      yield put(
        orderActions.setShowModalProductVariant({
          show: false,
          data: null
        })
      );

      yield put(orderActions.setShowModalProductDetail(modalProps));
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(orderActions.setLoadingData(false));
    toast.close();

    if (!modalProps.show) {
      yield put(
        orderActions.setShowModalProductDetail({
          show: false,
          data: null
        })
      );

      yield put(
        orderActions.setShowModalProductVariant({
          show: false,
          data: null
        })
      );

      return;
    }
  }
}

export function* addProductCart({ orderItem }: ReturnType<typeof orderItem>) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));
  try {
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
    const user: UserAuthInterface = yield select((state: AppStateType) => state.auth.user);
    const { orderDetails, client, orderType, orderItems, commissions } = yield select(
      (state: AppStateType) => state.order?.form?.data
    );
    const { selects } = yield select((state: AppStateType) => state?.order?.form);

    const blockOrder = selects?.reasons?.find(
      (reason: any) => reason?.actions?.orders === REASON_ACTIONS.DENY && reason?.type === REASON_TYPE.CLIENT_REGULATION
    );

    let soldPrice = toDecimals(orderItem?.soldPrice ?? 0, configuration?.decimals ?? 2);

    //! If the product is a packing CX and has a promotional price, calculate the sold price based on the packing quantity
    //! (https://e3labs.atlassian.net/browse/ISAV2-1504)
    // if (orderItem?.packing?.packingUnit === 'CX' && orderItem?.promotion?.promotionalPrice) {
    //   soldPrice = toDecimals(
    //     orderItem?.promotion?.promotionalPrice * orderItem?.packing?.packingQuantity,
    //     configuration?.decimals ?? 2
    //   );
    // }

    const promotionStock = orderItem?.promotion?.batchCode;
    if (promotionStock) {
      const itemStock = orderItem?.productBatch?.find(product => product.batchCode === promotionStock);
      if (itemStock) {
        if (orderItem.soldQuantity > itemStock.quantity) {
          yield notification.confirm(
            formatMessage('commonLabelWarning'),
            formatMessage('orderWarningProductBatchPromotionQuantity', {
              promotionName: orderItem?.promotion?.promotionName,
              batchCode: itemStock.batchCode,
              quantity: itemStock.quantity
            }),
            formatMessage('commonLabelOK'),
            '',
            false
          );
          return;
        }
      } else {
        notification.warning(
          `A promoção ${orderItem?.promotion?.promotionName} não é do lote do produto, a promoção só pode ser aplicada para estoque ${promotionStock}`
        );
        return;
      }
    }

    if (blockOrder) {
      const regulations = client?.clientRegulations
        ?.filter(clientRegulation =>
          orderItem?.productRegulations?.some(productRegulation => productRegulation?.code === clientRegulation?.code)
        )
        ?.map(regulation => {
          return {
            ...regulation,
            isInvalid:
              regulation?.validDate !== undefined
                ? regulation?.validDate
                  ? isAfter(new Date(), new Date(regulation.expirationDate))
                  : false
                : isAfter(new Date(), new Date(regulation.expirationDate))
          };
        });

      if (!regulations || regulations?.length === 0) {
        notification.warning(formatMessage('orderClientBlockRegulationEmpty'));
        return;
      }

      if (regulations?.some(regulation => regulation?.isInvalid)) {
        notification.warning(
          formatMessage('orderClientBlockRegulation', {
            description: blockOrder?.description
          })
        );
        return;
      }
    }

    if (configuration?.blockSaleWithoutPromotion && !orderItem.promotion) {
      yield notification.confirm(
        formatMessage('commonLabelWarning'),
        formatMessage('orderWarningProductWithoutPromotion'),
        formatMessage('commonLabelOK'),
        '',
        false
      );

      return;
    }

    if (configuration?.splitBonusOrder && orderItem?.bonificated && !orderItem?.orderType) {
      yield notification.confirm(
        formatMessage('commonLabelWarning'),
        'Informar o tipo do pedido quando o for bonificação. Verifique as atualizações e tente novamente!',
        formatMessage('commonLabelOK'),
        '',
        false
      );

      return;
    }

    const validatedProductBonification = yield call(validateProductListBonification, {
      orderItems: orderItem,
      orderTypeProps: orderType,
      subsidiary: orderDetails?.subsidiary
    });

    if (validatedProductBonification) {
      return;
    }

    const cartId = yield call(uuidv1);
    let minimumPrice = orderItem?.minimumPrice;

    let maxDiscountPercentage = orderItem?.maxDiscountPercentage ?? orderItem?.price?.maxDiscountPercentage ?? 0;

    const promotionsMaxDiscountPercentage = orderItem?.promotions?.[0]?.discountPercentage ?? 0;
    const maxDiscountTotal = Math.max(maxDiscountPercentage, promotionsMaxDiscountPercentage);

    let promotionMaxDiscountPercentage = 0;
    let totalDiscount = 0;

    if (orderItem?.promotion) {
      promotionMaxDiscountPercentage =
        orderItem?.promotion?.maxDiscountPercentage ?? orderItem?.promotion?.discountPercentage ?? 0;

      minimumPrice = toDecimals(
        orderItem.promotion.fixedPrice ?? orderItem.promotion.minimumPrice,
        configuration.decimals ?? 2
      );

      orderItem.promotion.priceDifference = getPriceDifference(orderItem, configuration.decimals ?? 2);
      if (orderItem?.promotion?.typeBalance) {
        totalDiscount = calculateTotalMaxDiscount({
          applyCombinedDiscount: configuration?.applyCombinedDiscount,
          applySequentialDiscountsForMinimumPrice: configuration?.applySequentialDiscountsForMinimumPrice,
          promotionDiscount: orderItem?.promotion?.discountPercentage,
          maxDiscount: maxDiscountPercentage,
          baseValue: orderItem?.baseValue,
          decimals: configuration?.decimals
        });
        minimumPrice = toDecimals((orderItem.baseValue * (100 - totalDiscount)) / 100, configuration.decimals ?? 2);
      }
    }

    let isPromotionGroupItem = isPromotionGroupValidator(orderItem?.promotion);

    let parsedPromotion = {
      ...orderItem?.promotion,
      isPromotionGroup: isPromotionGroupItem
    };

    let financialCost = parsedPromotion?.financialCost ?? 0;

    if (financialCost === 0) {
      financialCost = toDecimals(orderItem?.stock?.financialCost, configuration?.decimals ?? 2);
    }

    if (parsedPromotion?.includeFinancialPercentage) {
      financialCost = financialCost + (financialCost * orderItem?.percentualRate) / 100;
    }

    let margin = getMarginTotals({
      allowProfitabilityMarkup: configuration.allowProfitabilityMarkup,
      financialCost: financialCost * orderItem?.soldQuantity,
      total: soldPrice * orderItem?.soldQuantity,
      precision: configuration.decimals,
      orderItems
    });

    // const validate: { marginPercentage: number } & MarginControlInterface = validateMargin({
    //   configuration,
    //   orderItem,
    //   orderItems,
    //   promotion: parsedPromotion,
    //   tenantID: user.tenantID,
    //   paymentPlan: orderDetails?.paymentPlan
    // });

    // let marginControl = {
    //   ...validate,
    //   totalMarginPercentage: validate?.marginPercentage,
    //   totalMarginValue: validate?.marginValue
    // };

    const legalEntityOrLegalPerson = client?.governmentalCode
      ? client?.governmentalCode?.length > 14
        ? 'J'
        : 'F'
      : null;

    const { data: restrictions } = yield call(productService.getSalesRestriction, {
      activityExternalCode: client?.activityExternalCode ?? null,
      legalEntityOrLegalPerson,
      minSaleValue: 0,
      barCode: orderItem?.barCode ?? null,
      brandExternalCode: orderItem?.brandExternalCode ?? null,
      clientExternalCode: client?.externalCode ?? null,
      departmentExternalCode: orderItem?.departmentExternalCode ?? null,
      orderTypeExternalCode: orderType?.externalCode ?? null,
      paymentOptionExternalCode: orderDetails?.paymentOption?.externalCode ?? null,
      paymentPlanExternalCode: orderDetails?.paymentPlan?.externalCode ?? null,
      priceTableCode: orderDetails?.priceTableCode ?? null,
      productExternalCode: orderItem?.externalCode ?? null,
      providerExternalCode: orderItem?.providerExternalCode ?? null,
      roleExternalCode: user?.roleExternalCode ?? null,
      sectionExternalCode: orderItem?.sectionExternalCode ?? null,
      squareExternalCode: client?.squareExternalCode ?? null,
      subsidiaryExternalCode: orderDetails?.subsidiary?.externalCode ?? null,
      userExternalCode: user?.externalCode ?? null,
      shipping: orderDetails?.shippingType === SHIPPING_TYPE.FREE ? 'F' : 'C'
    });

    if (
      restrictions?.length > 0 &&
      restrictions?.some(restriction => restriction?.minSaleValue === 0 || restriction?.minSaleValue === null)
    ) {
      const errorMessage = {
        message: restrictions[0].restrictionReason
          ? formatMessage('orderWarningProductRestrictionWithReason', {
              productExternalCode: orderItem?.externalCode,
              restrictionExternalCode: restrictions[0].externalCode,
              restrictionReason: restrictions[0].restrictionReason
            })
          : formatMessage('orderWarningProductRestrictionWithoutReason', {
              productExternalCode: orderItem?.externalCode,
              restrictionExternalCode: restrictions[0].externalCode
            })
      };

      throw errorMessage;
    }

    let totalPrice = soldPrice * orderItem?.soldQuantity;

    const isBelowMinimumPrice = checkProductIsBelowMinimumPrice({
      isPromotionKit: orderItem?.promotion?.isPromotionKit,
      soldPrice,
      minimumPrice,
      acceptNegativeBalance: configuration?.acceptNegativeBalance && user?.accountBalance,
      decimals: configuration?.decimals
    });

    let commission = orderItem?.commission ?? null;

    if (configuration?.allowUserCommission && !orderItem?.commission) {
      commission = calculateCommissionInfo({
        allowUserCommission: configuration?.allowUserCommission,
        commissions: commissions || [],
        discountPercentage: orderItem?.discountPercentage || 0,
        promotionCommission: orderItem?.promotion?.comissionPromo ?? 0,
        soldPrice,
        soldQuantity: orderItem?.soldQuantity || 0,
        decimals: configuration?.decimals || 2
      });
    }

    const newOrderItem = {
      ...orderItem,

      // product: {
      //   ...orderItem?.product,
      //   barCode: orderItem?.packingInfos?.barCode ?? orderItem?.packing?.barCode ?? orderItem?.product?.barCode,
      //   isBelowMinimumPrice,
      //   soldPriceWithPacking: soldPrice,
      //   assistedSaleType: orderItem?.assistedSaleType ?? null
      // },
      barCode: orderItem?.packingInfos?.barCode ?? orderItem?.packing?.barCode ?? orderItem?.barCode,
      assistedSaleType: orderItem?.assistedSaleType ?? null,
      isBelowMinimumPrice,
      isAboveDiscountAllowed: checkProductIsAboveDiscountAllowed({
        discountPercentage: orderItem.discountPercentage,
        promotionTypeBalance: orderItem?.promotion?.typeBalance,
        totalDiscount,
        promotionMaxDiscountPercentage: orderItem?.promotion ? promotionMaxDiscountPercentage : 0,
        maxDiscountTotal,
        acceptNegativeBalance: configuration?.acceptNegativeBalance && user.accountBalance
      }),
      isDiscountApprovalRequired: configuration?.useDiscountControl
        ? orderItem.promotion
          ? orderItem.discountPercentage > (orderItem.promotion?.discountPercentage || 0) &&
            orderItem.discountPercentage <=
              ((orderItem.promotion?.maxDiscountPercentage || 0) ?? (orderItem.promotion?.discountPercentage || 0))
          : orderItem.discountPercentage > orderItem?.price?.discountPercentage &&
            orderItem.discountPercentage <=
              (orderItem?.price?.maxDiscountPercentage ?? orderItem?.price?.discountPercentage)
        : false,
      isPromotionGroup: isPromotionGroupItem ?? false,
      isPromotionKit: orderItem?.promotion?.isPromotionKit ?? false,
      financialCost,
      soldPrice,
      totalPrice,
      soldPriceWithPacking: soldPrice,
      totalPriceWithPacking: totalPrice,
      commission: {
        commissionType: commission?.commissionType,
        externalCode: commission?.externalCode,
        finalDiscountPercent: commission?.finalDiscountPercent,
        initialDiscountPercent: commission?.initialDiscountPercent,
        percentage: commission?.percentage,
        userExternalCode: commission?.userExternalCode,
        userName: commission?.userName,
        value: commission?.value
      },
      isRecommendation: orderItem?.isRecommendation ?? false,
      brandExternalCode: orderItem?.brandExternalCode ?? null
    };
    yield put(
      orderActions.setShowModalProductDetail({
        show: false,
        data: null
      })
    );

    yield put(orderActions.addProductSuccess({ ...newOrderItem, cartId }));
    yield put(orderActions.updateOrderMargin(margin));

    yield call(notification.success, formatMessage('orderWarningProductAdd'));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* removeProductCart({ payload }: any) {
  try {
    const toast: any = notification.loading(formatMessage('commonLoadingMessage'));
    const { tenantID } = yield select((state: AppStateType) => state.auth.user);
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
    const { orderItems }: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);

    const isPayloadArray = Array.isArray(payload);

    const hasBonificatedItems = orderItems.some(item => item.bonificated);

    // Bonificação ABS https://e3labs.atlassian.net/browse/ISAV2-559
    // Caso não funcionar o bonificado normal, verificar o find
    if (hasBonificatedItems) {
      const promotionExternalCode = payload?.promotion?.externalCode;

      const findBonificationItem = orderItems?.find(
        item =>
          item?.parentPromotionExternalCode === promotionExternalCode && item?.externalCode === payload?.externalCode
      );

      if (findBonificationItem) {
        const removeInCart = [payload, findBonificationItem];

        for (const item of removeInCart) {
          yield put(orderActions.removeProductSuccess(item.cartId, item, tenantID, configuration));
        }

        toast.close();

        yield call(notification.success, formatMessage('orderWarningProductRemove'));

        return;
      }
    }

    if (isPayloadArray) {
      for (const item of payload) {
        yield put(orderActions.removeProductSuccess(item.cartId, item, tenantID, configuration));
      }
    } else {
      yield put(orderActions.removeProductSuccess(payload.cartId, payload, tenantID, configuration));
    }

    toast.close();

    yield call(notification.success, formatMessage('orderWarningProductRemove'));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* loadProduct({ payload, setInCart }: any) {
  const configuration: ConfigurationInterface = yield select(selectorConfiguration);
  const order: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);
  const { onlyWithStock, onlyWithPromotion, orderBy, clientMix }: ProductStateListInterface = yield select(
    (state: AppStateType) => state.product.list
  );

  const { selectsDefault } = yield select((state: AppStateType) => state?.order?.form);

  const { orderDetails, client }: OrderCreateInterface = yield select(
    (state: AppStateType) => state?.order?.form?.data
  );

  try {
    yield put(orderActions.setLoadingProductData(true));

    if (cancel !== undefined) cancel();
    const cancelToken = new CancelToken(function executor(c) {
      cancel = c;
    });

    let orderItem: OrderItemCreateInterface;

    if (!payload?.cartId) {
      try {
        const { isRecommendation: isRecommendationPayload, isSponsored, parentPromotionExternalCode } = payload;

        const {
          tabulatedPrice,
          availableQuantity,
          _id,
          externalCode: productExternalCode,
          isRecommendation: isRecommendationProduct
        } = payload;

        const user: UserInterface = yield select((state: AppStateType) => state?.auth?.user);

        const isRecommendation = isRecommendationPayload ?? isRecommendationProduct ?? false;

        const productBaseValue = toDecimals(payload?.baseValue, configuration?.decimals ?? 2);
        let baseValue = productBaseValue;
        let discount = false;
        let discountPercentage = 0;
        let soldPrice = baseValue;
        let packing = null;
        let soldQuantity = payload?.soldQuantity ?? (payload?.bonificated ? payload?.quantity : packing ? 1 : 0);

        const lastOrderProductDetails: LastOrderProductDetailsInterface | null = yield call(
          getLastOrderProductDetails,
          client?.externalCode,
          productExternalCode
        );

        const priceTableCode = order?.orderDetails?.priceTableCode ?? null;

        const filters = {
          clientExternalCode: order?.client?.externalCode ?? null,
          subsidiaryExternalCode: order?.orderDetails?.subsidiary?.externalCode ?? null,
          governmentalCode: order?.client?.governmentalCode ?? null,
          applyTax: order?.client?.applyTax ? order?.client?.applyTax : null,
          applyTax2: configuration?.taxation ? order?.client?.applyTax2 : null,
          applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : null,
          finalConsumer: order?.client?.finalConsumer ?? null,
          usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
          clientActivityExternalCode: order?.client?.activityExternalCode ?? null,
          subsidiaryStockExternalCode:
            payload?.subsidiaryStock?.externalCode ?? order?.orderDetails?.subsidiaryStock?.externalCode ?? null,
          paymentPlanExternalCode: order?.client?.usePaymentPlansGroup
            ? [
                order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
                order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
              ].join(',')
            : order?.orderDetails?.paymentPlan?.externalCode,
          priceTableCode,
          onlyWithStock,
          onlyWithPromotion,
          clientMix,
          loadType: order?.load?.loadType?.code ?? null,
          shippingType: order?.orderDetails?.shippingType ?? null,
          clientGroupExternalCode: order?.client?.clientGroupExternalCode ?? null,
          productPaymentPlansGroup: order?.client?.paymentPlanGroup1ExternalCode
            ? order?.client?.paymentPlanGroup1ExternalCode
            : order?.client?.paymentPlanGroup2ExternalCode
            ? order?.client?.paymentPlanGroup2ExternalCode
            : null,
          orderBy: orderBy[0],
          orderAsc: orderBy[1],
          orderType: order?.orderType?.type
        };

        const [{ data: availablePromotions }, { data: quota }] = yield all([
          call(promotionService.getAvailablePromotions, {
            client,
            product: {
              ...payload,
              productBaseValue
            },
            subsidiaryExternalCode: orderDetails.subsidiary?.externalCode,
            paymentPlanExternalCode: orderDetails.paymentPlan?.externalCode,
            priceTableCode,
            clientGroupExternalCode: client?.clientGroupExternalCode ?? null,
            orderType: orderDetails?.orderType,
            ...filters
          } as AvailablePromotionsParamsProps),
          call(productService.getProductQuota, {
            clientExternalCode: client?.externalCode ?? null,
            productExternalCode: productExternalCode ?? null,
            subsidiaryExternalCode: orderDetails?.subsidiary?.externalCode ?? null
          })
        ]);

        const promotions: OrderPromotionInterface[] = yield validatePromotions(
          availablePromotions,
          soldPrice,
          configuration.decimals
        );

        const { data: product } = yield call(productService.getById, {
          _id,
          subsidiaryExternalCode: orderDetails.subsidiary?.externalCode,
          usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
          applyTax: order?.client?.applyTax ? order?.client?.applyTax : undefined,
          applyTax2: configuration?.taxation ? order?.client?.applyTax2 : undefined,
          applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : undefined,
          discountPercentage,
          decimals: configuration?.decimals ?? 2,
          subsidiaryStockExternalCode:
            payload?.subsidiaryStock?.externalCode ?? orderDetails?.subsidiaryStock?.externalCode ?? null,
          comparesMinimumPriceWithST: configuration?.comparesMinimumPriceWithST,
          paymentPlanExternalCodes: order?.client?.usePaymentPlansGroup
            ? [
                order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
                order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
              ].join(',')
            : order?.orderDetails?.paymentPlan?.externalCode,
          priceTableCode,
          useTagFocus: configuration?.useTagFocus,
          state: client?.commercialInfo?.state,
          taxation: configuration?.taxation,
          clientDiscountPercentage: client?.discountPercentage ?? 0,
          governmentalCode: client?.governmentalCode,
          finalConsumer: client?.finalConsumer ?? null
        });

        let availableQuantityProduct = availableQuantity ?? 0;

        const packings = product?.packing ?? [];

        const oldPackingProduct = order?.orderItems?.find(
          item => item?.externalCode === payload?.externalCode
        )?.packing;

        const availablePackings =
          oldPackingProduct && packings?.filter(pack => pack?.barCode !== oldPackingProduct?.barCode);

        if (oldPackingProduct && product?.packing?.length > 1) {
          packing = availablePackings?.[0] ?? packings?.[0] ?? null;
        } else {
          packing = packings?.[0] ?? null;
        }

        soldQuantity =
          payload?.soldQuantity ??
          (payload?.bonificated
            ? payload?.quantity
            : packing
            ? 1
            : product.multiplesSubsidiary
            ? product?.multiplesSubsidiary?.quantityMultiple
            : product?.quantityMultiple ?? 1);

        let barCode = packing?.barCode ?? product?.packing?.[0]?.barCode ?? product?.barCode ?? null;

        if (packing) {
          soldPrice = toDecimals(
            baseValue * packing.packingQuantity * (packing?.priceFactor ?? 1),
            configuration?.decimals ?? 2
          );
        }

        let promotion: OrderPromotionInterface = yield getPromotionValid(promotions, soldQuantity);
        discountPercentage = promotion?.discountPercentage ?? 0;

        let loadingAvailableQuantityProduct = configuration?.allowProductExpiry ? true : false;

        const price =
          product?.price ??
          product?.prices?.find(
            price =>
              price.priceTableCode === orderDetails?.priceTableCode &&
              price.productExternalCode === product?.externalCode
          );
        const stock =
          product?.stock ??
          product?.stocks?.find(
            stock =>
              stock?.productExternalCode === product?.externalCode &&
              stock?.subsidiaryExternalCode === orderDetails?.subsidiary?.externalCode
          );

        const maximumPrice = toDecimals(
          baseValue + (baseValue * configuration?.maximumSalePercentage) / 100,
          configuration?.decimals ?? 2
        );

        let maxDiscountPercentage = product?.maxDiscountPercentage ?? price?.maxDiscountPercentage ?? 0;

        let minimumPrice =
          product?.baseValue * (packing?.priceFactor ?? 1) * (packing?.packingQuantity ?? 1) -
          (product?.baseValue * (packing?.priceFactor ?? 1) * (packing?.packingQuantity ?? 1) * maxDiscountPercentage) /
            100;

        if (promotions?.length > 0) {
          const minimumPriceTable = toDecimals(
            (baseValue *
              (packing?.priceFactor ?? 1) *
              (packing?.packingQuantity ?? 1) *
              (100 - maxDiscountPercentage)) /
              100,
            configuration?.decimals
          );

          const minimumPricePromotion = promotions?.reduce((acc, cur) => {
            const promotionalPrice = toDecimals(
              (baseValue *
                (packing?.priceFactor ?? 1) *
                (packing?.packingQuantity ?? 1) *
                (100 - cur.discountPercentage)) /
                100,
              configuration?.decimals
            );

            return acc < promotionalPrice ? acc : promotionalPrice;
          }, Infinity);

          minimumPrice = [
            minimumPricePromotion,
            configuration.useDiscountControl ? minimumPriceTable : product.baseValue
          ].sort((a, b) => a - b)[0];
        } else {
          minimumPrice = toDecimals(
            baseValue * (packing?.priceFactor ?? 1) * (packing?.packingQuantity ?? 1) -
              (baseValue * (packing?.priceFactor ?? 1) * (packing?.packingQuantity ?? 1) * maxDiscountPercentage) / 100,
            configuration?.decimals ?? 2
          );
        }

        if (promotion?.typeBalance) {
          const totalDiscount = calculateTotalMaxDiscount({
            applyCombinedDiscount: configuration?.applyCombinedDiscount,
            applySequentialDiscountsForMinimumPrice: configuration?.applySequentialDiscountsForMinimumPrice,
            promotionDiscount: orderItem?.promotion?.discountPercentage,
            maxDiscount: maxDiscountPercentage,
            baseValue: orderItem?.baseValue,
            decimals: configuration?.decimals
          });
          minimumPrice = toDecimals(
            (orderItem?.baseValue *
              (packing?.priceFactor ?? 1) *
              (packing?.packingQuantity ?? 1) *
              (100 - totalDiscount)) /
              100,
            configuration.decimals ?? 2
          );
        }

        const paymentPlanSelected = client?.usePaymentPlansGroup
          ? orderDetails?.paymentPlanGroup?.[`paymentPlan${product?.paymentPlansGroup}`]
          : orderDetails?.paymentPlan;

        const percentualRate = paymentPlanSelected?.financialPercentageRate ?? 0;
        let financialCost = price?.includeFinancialPercentage
          ? toDecimals(
              price?.financialCost + (price?.financialCost * percentualRate) / 100,
              configuration?.decimals ?? 2
            )
          : toDecimals(stock?.financialCost, configuration?.decimals ?? 2) ?? 0;

        if (financialCost === 0 || financialCost === undefined) {
          const findFinancialCost = product?.stocks?.find(
            stock => stock.subsidiaryExternalCode === orderDetails?.subsidiary?.externalCode
          );

          financialCost = toDecimals(
            orderItem?.stock?.financialCost || findFinancialCost?.financialCost,
            configuration?.decimals ?? 2
          );
        }

        // let margin = yield getMargin(soldPrice, financialCost, configuration.decimals);

        const totalPrice = toDecimals(soldPrice * soldQuantity, configuration?.decimals ?? 2);
        let margin = yield getMarginTotals({
          allowProfitabilityMarkup: configuration.allowProfitabilityMarkup,
          financialCost,
          total: totalPrice,
          precision: configuration.decimals
        });

        if (
          configuration?.useMarginControlByColor &&
          configuration?.marginControlByColor &&
          (user?.useMarginControlByColor === undefined ? true : user?.useMarginControlByColor)
        ) {
          const marginCalcByColor = configuration?.marginControlByColor
            ? configuration?.marginControlByColor?.filter(
                margin =>
                  totalPrice >= margin?.initialValue &&
                  totalPrice <= margin?.finalValue &&
                  margin?.priceTableCode?.includes(orderDetails?.priceTableCode)
              )
            : [];

          if (marginCalcByColor?.length > 0 && price?.priceTableCode === marginCalcByColor[0]?.priceTableCode) {
            margin = {
              ...margin,
              color: marginCalcByColor[0]?.color,
              action: marginCalcByColor[0]?.action,
              priceTableCode: marginCalcByColor[0]?.priceTableCode
            };
          }
        }

        const totalUnity =
          product?.typeOperation && product?.unitQuantity !== product?.unitQuantityBox
            ? calculateTypeOperation(
                Number(productBaseValue),
                product.typeOperation === 'M' ? 'M' : 'D',
                product.unitQuantityBox,
                configuration
              )
            : 0;

        //! se for bonificado, adicionar a promoção do pai no item, ele esta vindo no payload
        const bonificatedPromotion = payload?.bonificated ? payload?.promotion : undefined;

        let tributacao = null;
        if (configuration?.taxation) {
          tributacao = getTax({
            client,
            configuration,
            paymentPlan: paymentPlanSelected,
            product,
            shippingType: order?.orderDetails?.shippingType,
            soldPrice,
            soldQuantity,
            tenantID: user.tenantID,
            // @ts-ignore
            tributacao: product.tributacao?.data,
            vendorDiscountPercentage: discountPercentage
          });
        }
        const unitaryValue = orderService.buildUnitaryValue({
          baseValue: productBaseValue,
          discount: discountPercentage,
          precision: configuration?.decimals ?? 2,
          priceFactor: packing?.priceFactor ?? 1,
          promotion
        });

        if (product) {
          orderItem = {
            ...orderItem,
            ...product,
            baseValue: productBaseValue,
            bonificated: payload?.bonificated ?? false,
            quota,
            // @ts-ignore
            tributacao,
            price,
            totalUnity,
            stock: {
              ...stock,
              financialCost
            },
            parentPromotionExternalCode,
            ...(bonificatedPromotion && {
              promotion: bonificatedPromotion
            }),
            assistedSaleType: order?.orderDetails?.orderType === ORDER_TYPE.ASSISTED_SALE_TYPE ? 'RI' : undefined,
            barCode,
            unit: packing?.packingUnit ?? product?.unit,
            packing,
            margin,
            // @ts-ignore
            tributacao,
            packingInfos: {
              ...(packing
                ? {
                    unit: packing.packingUnit,
                    type: packing.packingType,
                    availableQuantity: toDecimals(availableQuantityProduct / packing?.packingQuantity, 2),
                    barCode: packing.barCode
                  }
                : {
                    unit: product.unit,
                    type: product.unit,
                    availableQuantity: availableQuantityProduct,
                    barCode: product.barCode
                  }),
              packingPrice: baseValue * packing?.packingQuantity * (packing?.priceFactor ?? 1),
              basePackingPrice: baseValue * packing?.packingQuantity * (packing?.priceFactor ?? 1),
              unitaryValue: productBaseValue
            },
            soldQuantity,
            soldPrice,
            totalPrice,
            boxPrice: product?.typeOperation ? 0 : null,
            tabulatedPrice,
            financialCost,
            percentualRate,
            promotions,
            discount,
            minimumPrice,
            maximumPrice,
            availableQuantity: availableQuantityProduct,
            discountPercentage,
            maxDiscountPercentage,
            priorityPolice: false,
            isRecommendation,
            isSponsored: isSponsored ? true : false,
            isPromotion: false,
            isBelowMinimumPrice: false,
            isDiscountApprovalRequired: false,
            isPromotionGroup: false,
            loadingAvailableQuantityProduct,
            lastOrderProductDetails,
            subsidiaryStock: payload?.subsidiaryStock ?? null,
            unitaryValue,
            packings
          };

          yield put(orderActions.loadProductSuccess(orderItem));

          if (product?.multiplesSubsidiary?.subsidiaryExternalCode) {
            const newOrderItem = validateMultiplesSubsidiariesProduct({
              product,
              orderItem,
              configuration
            });

            yield put(orderActions.loadProductSuccess(newOrderItem));
          }

          if (promotion) {
            yield put(orderActions.setPromotion(promotion, soldQuantity));
          }

          yield put(orderActions.setLoadingProductData(false));

          if (configuration?.allowProductExpiry) {
            const orderItem = yield select((state: AppStateType) => state.order.form?.orderItem);

            try {
              const { data: expiryStockProduct }: { data: ExpiryStockProductProps[] } = yield call(
                clientService.getClientExpiryStockProducts,
                {
                  productExternalCode: product?.externalCode,
                  subsidiaryExternalCode: orderDetails.subsidiary?.externalCode,
                  productExpiry: client?.productExpiry
                },
                {
                  cancelToken
                }
              );

              if (expiryStockProduct.length > 0) {
                availableQuantityProduct = expiryStockProduct.reduce((acc, item) => acc + item.quantity, 0);
              }
            } catch (e) {
              availableQuantityProduct = availableQuantity ?? 0;
            }

            let parsedOrderItem = {
              ...orderItem,
              availableQuantity: availableQuantityProduct,
              loadingAvailableQuantityProduct: false,
              packingInfos: {
                ...orderItem?.packingInfos,
                ...(packing
                  ? {
                      unit: packing.packingUnit,
                      type: packing.packingType,
                      availableQuantity: toDecimals(availableQuantityProduct / packing?.packingQuantity, 2),
                      barCode: packing.barCode
                    }
                  : {
                      unit: product.unit,
                      type: product.unit,
                      availableQuantity: availableQuantityProduct,
                      barCode: product.barCode
                    })
              }
            };

            yield put(orderActions.loadProductSuccess(parsedOrderItem));
          }

          if (setInCart) {
            yield call(addProductCart, { orderItem });
          }
        }
      } catch (error) {
        yield put({ type: ISA_ERROR, error });
      } finally {
        yield put(orderActions.setLoadingProductData(false));
      }
    } else {
      try {
        const user: UserInterface = yield select((state: AppStateType) => state?.auth?.user);

        const { _id, externalCode: productExternalCode, availableQuantity } = payload;

        let maxDiscountPercentage = payload?.price?.maxDiscountPercentage ?? 0;

        let availableQuantityProduct = availableQuantity ?? 0;
        let loadingAvailableQuantityProduct = configuration?.allowProductExpiry ? true : false;

        const priceTableCode = order?.orderDetails?.priceTableCode ?? null;
        const discountPercentage = payload?.discountPercentage ?? 0;

        const [{ data: product }, { data: quota }] = yield all([
          call(productService.getById, {
            _id,
            subsidiaryExternalCode: orderDetails.subsidiary?.externalCode,
            usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
            applyTax: order?.client?.applyTax ? order?.client?.applyTax : undefined,
            applyTax2: configuration?.taxation ? order?.client?.applyTax2 : undefined,
            applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : undefined,
            taxation: configuration?.taxation,
            // discountPercentage,
            decimals: configuration?.decimals ?? 2,
            subsidiaryStockExternalCode:
              payload?.product?.subsidiaryStockExternalCode ??
              payload?.product?.subsidiaryStock?.externalCode ??
              payload?.subsidiaryStock?.externalCode ??
              orderDetails?.subsidiaryStock?.externalCode ??
              null,
            comparesMinimumPriceWithST: configuration?.comparesMinimumPriceWithST,
            paymentPlanExternalCodes: order?.client?.usePaymentPlansGroup
              ? [
                  order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
                  order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
                ].join(',')
              : order?.orderDetails?.paymentPlan.externalCode,
            priceTableCode,
            clientDiscountPercentage: client?.discountPercentage ?? 0,
            governmentalCode: client?.governmentalCode,
            finalConsumer: client?.finalConsumer ?? null,
            state: client?.commercialInfo?.state
          }),
          call(productService.getProductQuota, {
            clientExternalCode: client?.externalCode ?? null,
            productExternalCode: productExternalCode ?? null,
            subsidiaryExternalCode: orderDetails?.subsidiary?.externalCode ?? null
          })
        ]);

        availableQuantityProduct = product?.availableQuantity ?? availableQuantityProduct;

        let barCode =
          payload?.packing?.barCode ?? payload?.barCode ?? product?.packing?.[0]?.barCode ?? product?.barCode ?? null;

        //! Se estiver errado olhar esse dps dnv
        // const productBaseValue = toDecimals(payload?.product?.product?.baseValue, configuration?.decimals ?? 2);
        // const productBaseValue = toDecimals(payload?.product?.baseValue, configuration?.decimals ?? 2);

        let baseValue = toDecimals(product?.baseValue, configuration?.decimals ?? 2);
        let soldPrice =
          payload?.soldPrice ??
          toDecimals(
            toDecimals(baseValue * (1 - payload?.discountPercentage / 100), configuration?.decimals ?? 2) *
              (payload?.packing?.packingQuantity ?? 1) *
              (payload?.packing?.priceFactor ?? 1),
            configuration?.decimals ?? 2
          );

        const filters = {
          clientExternalCode: order?.client?.externalCode ?? null,
          subsidiaryExternalCode: order?.orderDetails?.subsidiary?.externalCode ?? null,
          governmentalCode: order?.client?.governmentalCode ?? null,
          applyTax: order?.client?.applyTax ? order?.client?.applyTax : null,
          applyTax2: configuration?.taxation ? order?.client?.applyTax2 : null,
          applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : null,
          finalConsumer: order?.client?.finalConsumer ?? null,
          usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
          clientActivityExternalCode: order?.client?.activityExternalCode ?? null,
          subsidiaryStockExternalCode:
            payload?.subsidiaryStock?.externalCode ?? order?.orderDetails?.subsidiaryStock?.externalCode ?? null,
          paymentPlanExternalCode: order?.client?.usePaymentPlansGroup
            ? [
                order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
                order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
              ].join(',')
            : order?.orderDetails?.paymentPlan?.externalCode,
          priceTableCode,
          onlyWithStock,
          onlyWithPromotion,
          clientMix,
          loadType: order?.load?.loadType?.code ?? null,
          shippingType: order?.orderDetails?.shippingType ?? null,
          clientGroupExternalCode: order?.client?.clientGroupExternalCode ?? null,
          productPaymentPlansGroup: order?.client?.paymentPlanGroup1ExternalCode
            ? order?.client?.paymentPlanGroup1ExternalCode
            : order?.client?.paymentPlanGroup2ExternalCode
            ? order?.client?.paymentPlanGroup2ExternalCode
            : null,
          orderBy: orderBy[0],
          orderAsc: orderBy[1],
          orderType: order?.orderType?.type
        };

        const { data: availablePromotions } = yield call(promotionService.getAvailablePromotions, {
          client,
          product: {
            ...payload,
            productBaseValue: baseValue
          },
          orderType: orderDetails?.orderType,
          subsidiaryExternalCode: orderDetails.subsidiary?.externalCode,
          paymentPlanExternalCode: orderDetails.paymentPlan?.externalCode,
          priceTableCode,
          clientGroupExternalCode: client?.clientGroupExternalCode ?? null,
          ...filters
        } as AvailablePromotionsParamsProps);

        const promotions: OrderPromotionInterface[] = yield validatePromotions(
          availablePromotions,
          soldPrice,
          configuration.decimals
        );

        let promotion: OrderPromotionInterface = payload?.promotion?.promotionExternalCode
          ? promotions.find(p => p?.externalCode === payload?.promotion?.promotionExternalCode)
          : promotions?.find(p => p?.externalCode === payload?.promotion?.externalCode);

        const packing = payload?.packing ?? product?.packing?.[0] ?? null;

        let maximumPrice =
          payload?.maximumPrice ||
          payload?.maximumPrice ||
          toDecimals(
            baseValue + (baseValue * configuration?.maximumSalePercentage) / 100,
            configuration?.decimals || 2
          );

        // let minimumPrice = toDecimals(payload?.product?.minimumPrice, configuration?.decimals ?? 2);
        let minimumPrice = toDecimals(product?.baseValue, configuration?.decimals ?? 2);

        if (promotions?.length > 0) {
          const minimumPriceTable = toDecimals(
            (baseValue * (100 - maxDiscountPercentage)) / 100,
            configuration?.decimals
          );

          const minimumPricePromotion = promotions?.reduce((menor, atual) => {
            return atual?.minimumPrice < menor?.minimumPrice ? atual : menor;
          });

          minimumPrice = [
            minimumPricePromotion?.minimumPrice,
            configuration.useDiscountControl ? minimumPriceTable : baseValue
          ].sort((a, b) => a - b)[0];
        }

        if (promotion?.typeBalance) {
          const totalDiscount = calculateTotalMaxDiscount({
            applyCombinedDiscount: configuration?.applyCombinedDiscount,
            applySequentialDiscountsForMinimumPrice: configuration?.applySequentialDiscountsForMinimumPrice,
            promotionDiscount: promotion?.discountPercentage,
            maxDiscount: maxDiscountPercentage,
            baseValue: baseValue,
            decimals: configuration?.decimals
          });
          minimumPrice = toDecimals((baseValue * (100 - totalDiscount)) / 100, configuration.decimals ?? 2);
        }

        let isPromotionGroupItem = isPromotionGroupValidator(payload?.promotion) ?? false;

        const totalUnity =
          product?.typeOperation && product?.unitQuantity !== product?.unitQuantityBox
            ? calculateTypeOperation(
                Number(payload.baseValue),
                product.typeOperation === 'M' ? 'M' : 'D',
                product.unitQuantityBox,
                configuration
              )
            : 0;

        let subsidiaryStock = null;

        if (configuration?.useSubsidiaryStockByProduct) {
          subsidiaryStock = payload?.product?.subsidiaryStock ?? payload?.subsidiaryStock;
        }

        let tributacao = null;
        if (configuration?.taxation) {
          tributacao = getTax({
            client,
            configuration,
            paymentPlan: orderDetails.paymentPlan,
            product,
            shippingType: order?.orderDetails?.shippingType,
            soldPrice,
            soldQuantity: payload?.soldQuantity ?? 1,
            tenantID: user.tenantID,
            // @ts-ignore
            tributacao: payload?.tributacao?.data,
            vendorDiscountPercentage: payload?.discountPercentage
          });
        }

        const unitaryValue = orderService.buildUnitaryValue({
          baseValue,
          discount: discountPercentage,
          precision: configuration?.decimals ?? 2,
          priceFactor: packing?.priceFactor ?? 1,
          promotion
        });

        orderItem = {
          ...payload,
          ...product,
          multiplesSubsidiary: product?.multiplesSubsidiary?.subsidiaryExternalCode
            ? product?.multiplesSubsidiary
            : undefined,
          // @ts-ignore
          tributacao,
          totalUnity,
          quota,
          promotion,
          promotions,
          barCode,
          soldPrice,
          soldPriceWithPacking: soldPrice,
          unit: packing?.packingUnit ?? product?.unit,
          unitQuantity: packing?.packingQuantity ?? product?.unitQuantity,
          assistedSaleType: payload?.product?.assistedSaleType,
          baseValue,
          availableQuantity: availableQuantityProduct,
          packing,
          packingInfos: {
            ...(packing && ((Array.isArray(packing) && packing.length > 0) || !Array.isArray(packing))
              ? {
                  unit: packing.packingUnit,
                  type: packing.packingType,
                  availableQuantity: toDecimals(availableQuantityProduct / packing?.packingQuantity, 2),
                  barCode: packing.barCode
                }
              : {
                  unit: product.unit,
                  type: product.unit,
                  availableQuantity: availableQuantityProduct,
                  barCode: product.barCode
                }),
            packingPrice: soldPrice,
            basePackingPrice: payload?.packingInfos?.basePackingPrice ?? baseValue,
            unitaryValue: payload?.packingInfos?.unitaryValue ?? baseValue
          },
          // @ts-ignore
          tributacao,
          isEdit: true,
          maxDiscountPercentage,
          minimumPrice,
          maximumPrice,
          totalPrice: soldPrice * payload?.soldQuantity,
          isPromotionGroup: isPromotionGroupItem ?? false,
          loadingAvailableQuantityProduct,
          productObservation: payload?.productObservation ? payload?.productObservation : '',
          subsidiaryStock,
          unitaryValue
        };

        yield put(orderActions.loadProductSuccess(orderItem));
        yield put(orderActions.setLoadingProductData(false));

        if (configuration?.allowProductExpiry) {
          try {
            const { data: expiryStockProduct }: { data: ExpiryStockProductProps[] } = yield call(
              clientService.getClientExpiryStockProducts,
              {
                productExternalCode: product?.externalCode,
                subsidiaryExternalCode: orderDetails.subsidiary?.externalCode,
                productExpiry: client?.productExpiry
              }
            );

            if (expiryStockProduct.length > 0) {
              availableQuantityProduct = expiryStockProduct.reduce((acc, item) => acc + item.quantity, 0);
            }
          } catch (e) {
            availableQuantityProduct = availableQuantity ?? 0;
          }

          const orderItem = yield select((state: AppStateType) => state.order.form?.orderItem);
          let parsedOrderItem = {
            ...orderItem,
            availableQuantity: availableQuantityProduct,
            loadingAvailableQuantityProduct: false,
            barCode: packing?.barCode ?? product?.barCode ?? '0',
            packingInfos: {
              ...orderItem?.packingInfos,
              ...(packing
                ? {
                    availableQuantity: toDecimals(availableQuantityProduct / packing?.packingQuantity, 2)
                  }
                : {
                    availableQuantity: availableQuantityProduct
                  })
            }
          };

          yield put(orderActions.loadProductSuccess(parsedOrderItem));
        }
      } catch (error) {
        yield put({ type: ISA_ERROR, error });
      } finally {
        yield put(orderActions.setLoadingProductData(false));
      }
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(orderActions.setLoadingProductData(false));
  }
}

export function* setBonificated({ payload }: any) {
  const { orderItem } = yield select((state: AppStateType) => state?.order?.form);

  const newOrderItem = {
    ...orderItem,
    bonificated: !!payload,
    itemBonification: payload
  };

  yield put(orderActions.loadProductSuccess(newOrderItem));
}

export function* setOrderTypeBonificated({ payload }: any) {
  const { orderItem } = yield select((state: AppStateType) => state?.order?.form);

  const newOrderItem = {
    ...orderItem,
    orderType: payload,
    orderTypeExternalCode: payload?.externalCode
  };

  yield put(orderActions.loadProductSuccess(newOrderItem));
}

export function* updateQuantity({ id, payload }: any) {
  try {
    let promotions: PromotionInterface[];

    const { tenantID, useMarginControlByColor }: UserAuthInterface = yield select(
      (state: AppStateType) => state?.auth.user
    );
    const { orderItem }: { orderItem: OrderItemCreateInterface } = yield select(
      (state: AppStateType) => state?.order?.form
    );
    const { orderDetails, client, orderItems }: OrderCreateInterface = yield select(
      (state: AppStateType) => state?.order?.form?.data
    );
    const configuration: ConfigurationInterface = yield select(selectorConfiguration);

    const user: UserAuthInterface = yield select((state: AppStateType) => state?.auth.user);

    if (!orderItem) {
      const targetOrderItem = orderItems?.find(item => item?.cartId === id);

      const minBatchExpiryDays = client?.minimumBatchExpiryDate ?? configuration?.defaultBatchExpiryDate;
      const { isValidBatch } = yield validateBatch({
        subsidiaryExternalCode: orderDetails?.subsidiary?.externalCode,
        orderItem: {
          ...targetOrderItem,
          soldQuantity: payload
        },
        minBatchExpiryDays
      });

      if (!isValidBatch) {
        return;
      }
    }

    const validate: { marginPercentage: number } & MarginControlInterface = validateMargin({
      configuration,
      orderItem,
      orderItems,
      promotion: orderItem?.promotion,
      tenantID,
      paymentPlan: orderDetails?.paymentPlan
    });

    let marginControl = {
      ...validate,
      totalMarginPercentage: validate?.marginPercentage,
      totalMarginValue: validate?.marginValue
    };

    if (
      configuration?.useMarginControlByColor &&
      configuration?.marginControlByColor &&
      (useMarginControlByColor === undefined ? true : useMarginControlByColor)
    ) {
      marginControl = {
        ...marginControl,
        color: validate?.color ? validate?.color : undefined,
        action: validate?.action ? validate?.action : undefined,
        priceTableCode: validate?.priceTableCode ? validate?.priceTableCode : undefined
      };
    }

    if (!id) {
      const { quota } = orderItem;

      if (payload > quota.quota) {
        const errorMessage: { message: string } = { message: messageQuota(quota) };

        throw errorMessage;
      }

      let soldPrice = orderItem?.soldPrice;
      let minimumPrice = orderItem?.minimumPrice;

      if (configuration.useAutomaticPricingRetailOrWholesale) {
        const paymentPlan = client?.usePaymentPlansGroup
          ? orderDetails?.paymentPlanGroup?.[`paymentPlan${orderItem?.paymentPlansGroup}`]
          : orderDetails?.paymentPlan;
        const priceNumber = paymentPlan?.priceNumber ?? 1;

        const minimumProductWholeSaleQuantity = orderItem?.price?.minimumProductWholeSaleQuantity;
        const priceWholeSale = orderItem?.price[`priceWholeSale${priceNumber === 1 ? '' : priceNumber}`];
        const fixedPrice = orderItem?.promotion?.fixedPrice;

        if (minimumProductWholeSaleQuantity && payload >= minimumProductWholeSaleQuantity && !fixedPrice) {
          soldPrice = toDecimals(
            priceWholeSale * (orderItem?.packing?.priceFactor ?? 1) * (orderItem?.packing?.packingQuantity ?? 1),
            configuration.decimals ?? 2
          );
          minimumPrice = toDecimals(
            priceWholeSale * (orderItem?.packing?.priceFactor ?? 1) * (orderItem?.packing?.packingQuantity ?? 1),
            configuration.decimals ?? 2
          );
        }

        if ((minimumProductWholeSaleQuantity && payload < minimumProductWholeSaleQuantity) || fixedPrice) {
          soldPrice = toDecimals(
            (fixedPrice ?? orderItem?.promotion?.minimumPrice ?? orderItem?.baseValue) *
              (orderItem?.packing?.priceFactor ?? 1) *
              (orderItem?.packing?.packingQuantity ?? 1),
            configuration.decimals ?? 2
          );
          minimumPrice = toDecimals(
            (fixedPrice ?? orderItem?.promotion?.minimumPrice ?? orderItem?.baseValue) *
              (orderItem?.packing?.priceFactor ?? 1) *
              (orderItem?.packing?.packingQuantity ?? 1),
            configuration.decimals ?? 2
          );
        }
      } else {
        soldPrice =
          orderItem?.soldPrice ||
          toDecimals(orderItem?.promotion?.minimumPrice ?? orderItem?.baseValue, configuration.decimals ?? 2);
      }

      promotions = yield validatePromotions(orderItem?.promotions ?? [], soldPrice, configuration.decimals);
      const promotion: PromotionInterface = yield getPromotionValid(promotions, payload, orderItem.promotion);
      let tributacao = null;
      if (configuration?.taxation) {
        tributacao = getTax({
          client,
          configuration,
          paymentPlan: orderDetails?.paymentPlan,
          product: orderItem as unknown as ProductSale,
          shippingType: orderDetails?.shippingType,
          soldPrice,
          soldQuantity: payload,
          tenantID: user.tenantID,
          // @ts-ignore
          tributacao: orderItem?.tributacao?.data,
          vendorDiscountPercentage: orderItem.discountPercentage
        });
      }

      const newOrderItem = {
        ...orderItem,
        // @ts-ignore
        tributacao,
        soldPrice,
        promotions,
        soldQuantity: payload,
        totalPrice: toDecimals(soldPrice, configuration.decimals ?? 2) * payload,
        totalPriceWithPacking: toDecimals(soldPrice, configuration.decimals ?? 2) * payload,
        minimumPrice
      };

      yield put(orderActions.loadProductSuccess(newOrderItem));

      if (
        (promotions?.some(promo => promo.instantApplyDiscount) && orderItem.promotion?._id !== promotion?._id) ||
        (orderItem.promotion?._id && !promotion)
      ) {
        yield put(orderActions.setPromotion(promotion, payload));
      }
    } else {
      let orderItemChangeCart = orderItems?.find(item => item?.cartId === id);

      let minimumPrice = orderItemChangeCart?.minimumPrice ?? orderItem?.minimumPrice ?? null;
      let soldPrice = orderItemChangeCart?.soldPrice ?? orderItem?.soldPrice ?? null;
      let soldPriceWithPacking = orderItemChangeCart?.soldPrice ?? orderItem?.soldPriceWithPacking ?? null;

      const { data: quota } = yield call(productService.getProductQuota, {
        clientExternalCode: client?.externalCode ?? null,
        productExternalCode: orderItem?.externalCode ?? orderItemChangeCart?.externalCode ?? null,
        subsidiaryExternalCode: orderDetails?.subsidiary?.externalCode ?? null
      });

      if (payload > quota?.quota) {
        const errorMessage: { message: string } = { message: messageQuota(quota) };

        throw errorMessage;
      }

      promotions = yield validatePromotions(
        orderItem?.promotions ?? orderItemChangeCart?.promotions ?? [],
        orderItem?.soldPrice ?? orderItemChangeCart?.soldPrice,
        configuration.decimals
      );

      if (payload === 0) {
        const { isConfirmed } = yield notification.confirm(
          formatMessage('commonLabelWarning'),
          formatMessage('orderWarningProductQuantityBellow')
        );

        if (isConfirmed) {
          yield put(orderActions.removeProductRequest(orderItem?.cartId ? orderItem : orderItemChangeCart));
          return;
        } else {
          return;
        }
      }

      let newOrderItem;
      if (orderItemChangeCart && !orderItem) {
        let isBelowMinimumPrice = orderItemChangeCart?.isBelowMinimumPrice;

        if (configuration.useAutomaticPricingRetailOrWholesale) {
          const paymentPlan = client?.usePaymentPlansGroup
            ? orderDetails?.paymentPlanGroup?.[`paymentPlan${orderItem?.paymentPlansGroup}`]
            : orderDetails?.paymentPlan;
          const priceNumber = paymentPlan?.priceNumber ?? 1;

          const minimumProductWholeSaleQuantity = orderItemChangeCart?.price?.minimumProductWholeSaleQuantity;
          const priceWholeSale = orderItemChangeCart?.price[`priceWholeSale${priceNumber === 1 ? '' : priceNumber}`];
          const fixedPrice = orderItemChangeCart?.promotion?.fixedPrice;

          if (minimumProductWholeSaleQuantity && payload >= minimumProductWholeSaleQuantity && !fixedPrice) {
            soldPrice = toDecimals(
              priceWholeSale *
                (orderItemChangeCart?.packing?.packingQuantity ??
                  orderItemChangeCart?.packing?.[0]?.packingQuantity ??
                  1) *
                (orderItemChangeCart?.packing?.priceFactor ?? orderItemChangeCart?.packing?.[0]?.priceFactor ?? 1),
              configuration.decimals ?? 2
            );
            soldPriceWithPacking = toDecimals(soldPrice, configuration.decimals ?? 2);
            minimumPrice = toDecimals(
              priceWholeSale *
                (orderItemChangeCart?.packing?.packingQuantity ??
                  orderItemChangeCart?.packing?.[0]?.packingQuantity ??
                  1) *
                (orderItemChangeCart?.packing?.priceFactor ?? orderItemChangeCart?.packing?.[0]?.priceFactor ?? 1),
              configuration.decimals ?? 2
            );
          }

          if ((minimumProductWholeSaleQuantity && payload < minimumProductWholeSaleQuantity) || fixedPrice) {
            const price =
              fixedPrice ||
              orderItemChangeCart?.price[`price${priceNumber === 1 ? '' : priceNumber}`] ||
              orderItem?.price[`price${priceNumber === 1 ? '' : priceNumber}`];

            soldPrice = toDecimals(price, configuration.decimals ?? 2);
            soldPriceWithPacking = toDecimals(price, configuration.decimals ?? 2);
            minimumPrice = toDecimals(price, configuration.decimals ?? 2);
          }
        } else {
          const paymentPlan = client?.usePaymentPlansGroup
            ? orderDetails?.paymentPlanGroup?.[`paymentPlan${orderItem?.paymentPlansGroup}`]
            : orderDetails?.paymentPlan;
          const priceNumber = paymentPlan?.priceNumber ?? 1;

          const price =
            soldPrice ||
            orderItemChangeCart?.price[`price${priceNumber === 1 ? '' : priceNumber}`] ||
            orderItem?.price[`price${priceNumber === 1 ? '' : priceNumber}`];

          soldPrice = toDecimals(price, configuration.decimals ?? 2);
          minimumPrice = toDecimals(price, configuration.decimals ?? 2);
          soldPriceWithPacking = toDecimals(price, configuration.decimals ?? 2);
        }

        if (orderItemChangeCart?.discountPercentage > 0) {
          soldPrice = toDecimals(
            toDecimals(
              orderItemChangeCart?.baseValue * (1 - orderItemChangeCart?.discountPercentage / 100),
              configuration?.decimals ?? 2
            ) *
              (orderItemChangeCart?.packing?.packingQuantity ??
                orderItemChangeCart?.packing?.[0]?.packingQuantity ??
                1) *
              (orderItemChangeCart?.packing?.priceFactor ?? orderItemChangeCart?.packing?.[0]?.priceFactor ?? 1),
            configuration?.decimals ?? 2
          );
          minimumPrice = toDecimals(orderItemChangeCart?.minimumPrice, configuration.decimals ?? 2);
          isBelowMinimumPrice = checkProductIsBelowMinimumPrice({
            isPromotionKit: orderItem?.promotion?.isPromotionKit,
            soldPrice,
            minimumPrice,
            acceptNegativeBalance: configuration?.acceptNegativeBalance && user.accountBalance,
            decimals: configuration?.decimals
          });
        }

        let tributacao = null;
        if (configuration?.taxation) {
          tributacao = getTax({
            client,
            configuration,
            paymentPlan: orderDetails?.paymentPlan,
            product: orderItemChangeCart as unknown as ProductSale,
            shippingType: orderDetails?.shippingType,
            soldPrice,
            soldQuantity: payload,
            tenantID: user.tenantID,
            // @ts-ignore
            tributacao: orderItemChangeCart?.tributacao?.data,
            vendorDiscountPercentage: orderItemChangeCart.discountPercentage
          });
        }

        newOrderItem = {
          ...orderItemChangeCart,
          // @ts-ignore
          tributacao,
          soldQuantity: payload,
          isBelowMinimumPrice,
          minimumPrice,
          totalPrice: toDecimals(soldPrice * payload, configuration.decimals ?? 2),
          totalPriceWithPacking: toDecimals(soldPrice * payload, configuration.decimals ?? 2),
          promotions,
          quota,
          soldPrice
        };

        if (
          (!!promotions?.length && orderItemChangeCart?.promotion && !orderItem) ||
          (promotions?.some(promotion => promotion.instantApplyDiscount) && !orderItem)
        ) {
          const { isConfirmed } = yield notification.confirm(
            formatMessage('commonLabelWarning'),
            formatMessage('orderWarningProductEditWithPromotion')
          );

          if (isConfirmed) {
            yield put(orderActions.updateProductRequest(newOrderItem));
            return;
          } else {
            return;
          }
        }
        yield put(orderActions.updateProductRequest(newOrderItem));
      } else {
        let minimumPrice = orderItem?.minimumPrice;

        if (!minimumPrice) {
          minimumPrice = orderItem?.baseValue - (orderItem?.baseValue * 0) / 100;
        }

        let soldPrice = null;
        let soldPriceWithPacking = orderItem?.soldPriceWithPacking;
        let totalPrice = null;
        let totalPriceWithPacking = null;

        if (configuration.useAutomaticPricingRetailOrWholesale) {
          const paymentPlan = client?.usePaymentPlansGroup
            ? orderDetails?.paymentPlanGroup?.[`paymentPlan${orderItem?.paymentPlansGroup}`]
            : orderDetails?.paymentPlan;
          const priceNumber = paymentPlan?.priceNumber ?? 1;

          const minimumProductWholeSaleQuantity = orderItem?.price?.minimumProductWholeSaleQuantity;
          const priceWholeSale = orderItem?.price[`priceWholeSale${priceNumber === 1 ? '' : priceNumber}`];
          const fixedPrice = orderItemChangeCart?.promotion?.fixedPrice;

          if (minimumProductWholeSaleQuantity && payload >= minimumProductWholeSaleQuantity && !fixedPrice) {
            soldPrice = toDecimals(
              priceWholeSale * (orderItem?.packing?.priceFactor ?? 1) * (orderItem?.packing?.packingQuantity ?? 1),
              configuration.decimals ?? 2
            );
            totalPrice = toDecimals(soldPrice * payload, configuration.decimals ?? 2);
            minimumPrice = toDecimals(
              priceWholeSale *
                priceWholeSale *
                (orderItem?.packing?.priceFactor ?? 1) *
                (orderItem?.packing?.packingQuantity ?? 1),
              configuration.decimals ?? 2
            );
          }

          if ((minimumProductWholeSaleQuantity && payload < minimumProductWholeSaleQuantity) || fixedPrice) {
            const paymentPlan = client?.usePaymentPlansGroup
              ? orderDetails?.paymentPlanGroup?.[`paymentPlan${orderItem?.paymentPlansGroup}`]
              : orderDetails?.paymentPlan;
            const priceNumber = paymentPlan?.priceNumber ?? 1;
            const price = fixedPrice || orderItem?.price[`price${priceNumber === 1 ? '' : priceNumber}`];

            soldPrice = toDecimals(price, configuration.decimals ?? 2);
            soldPriceWithPacking = toDecimals(price, configuration.decimals ?? 2);
            totalPrice = toDecimals(price * payload, configuration.decimals ?? 2);
            totalPriceWithPacking = toDecimals(price * payload, configuration.decimals ?? 2);
            minimumPrice = toDecimals(price, configuration.decimals ?? 2);
          }
        } else {
          const paymentPlan = client?.usePaymentPlansGroup
            ? orderDetails?.paymentPlanGroup?.[`paymentPlan${orderItem?.paymentPlansGroup}`]
            : orderDetails?.paymentPlan;
          const priceNumber = paymentPlan?.priceNumber ?? 1;
          const price = orderItem?.soldPrice || orderItem?.price[`price${priceNumber === 1 ? '' : priceNumber}`];

          soldPrice = toDecimals(price, configuration.decimals ?? 2);
          soldPriceWithPacking = toDecimals(price, configuration.decimals ?? 2);
          totalPrice = toDecimals(price * payload, configuration.decimals ?? 2);
          totalPriceWithPacking = toDecimals(price * payload, configuration.decimals ?? 2);
          minimumPrice = toDecimals(price, configuration.decimals ?? 2);
        }

        const isBelowMinimumPrice = checkProductIsBelowMinimumPrice({
          isPromotionKit: orderItem?.promotion?.isPromotionKit,
          soldPrice: soldPriceWithPacking || soldPrice,
          minimumPrice,
          acceptNegativeBalance: configuration?.acceptNegativeBalance && user.accountBalance,
          decimals: configuration?.decimals
        });

        const maximumPrice = orderItem?.maximumPrice;

        let tributacao = null;
        if (configuration?.taxation) {
          tributacao = getTax({
            client,
            configuration,
            paymentPlan: orderDetails?.paymentPlan,
            product: orderItem as unknown as ProductSale,
            shippingType: orderDetails?.shippingType,
            soldPrice,
            soldQuantity: payload,
            tenantID: user.tenantID,
            // @ts-ignore
            tributacao: orderItem?.tributacao?.data,
            vendorDiscountPercentage: orderItem.discountPercentage
          });
        }
        const promotion: PromotionInterface = yield getPromotionValid(promotions, payload, orderItem.promotion);

        newOrderItem = {
          ...orderItem,
          // @ts-ignore
          tributacao,
          soldQuantity: payload,
          isBelowMinimumPrice,
          totalPrice,
          totalPriceWithPacking: totalPrice,
          soldPriceWithPacking: soldPrice,
          promotions,
          quota,
          soldPrice,
          maximumPrice,
          isPromotion: !!promotion,
          promotion: promotion || undefined
        };

        if (
          (promotions?.some(promo => promo.instantApplyDiscount) && orderItem.promotion?._id !== promotion?._id) ||
          (orderItem.promotion?._id && !promotion)
        ) {
          yield put(orderActions.setPromotion(promotion, payload));
        }

        yield put(orderActions.loadProductSuccess(newOrderItem));
      }
    }
  } catch (error: any) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function messageQuota(quota: ProductQuotaInterface): string {
  const message: string[] = [];
  message.push(
    formatMessage('orderWarningProductQuota1', {
      productExternalCode: quota.productExternalCode
    })
  );
  if (quota.clientExternalCode) {
    message.push(
      formatMessage('orderWarningProductQuota2', {
        clientExternalCode: quota.clientExternalCode
      })
    );
  }
  message.push(
    formatMessage('orderWarningProductQuota3', {
      subsidiaryExternalCode: quota.subsidiaryExternalCode,
      quota: quota.quota
    })
  );
  return message.join(' ');
}

export function* updateSoldPrice({ payload }: any) {
  const { orderItem } = yield select((state: AppStateType) => state?.order?.form);
  const { decimals } = yield select((state: AppStateType) => state?.configuration);
  const user: UserAuthInterface = yield select((state: AppStateType) => state?.auth.user);

  const { orderDetails, client }: OrderCreateInterface = yield select(
    (state: AppStateType) => state?.order?.form?.data
  );

  const configuration: ConfigurationInterface = yield select(selectorConfiguration);

  if (payload >= 0) {
    const baseValueWithPacking =
      orderItem?.packing?.packingQuantity > 1
        ? toDecimals(
            orderItem?.packing?.packingQuantity * (orderItem?.baseValue ?? orderItem?.baseValue),
            decimals ?? 2
          )
        : 0;
    const newDiscountPercentage = toDecimals(
      (((baseValueWithPacking || (orderItem?.baseValue ?? orderItem?.baseValue)) - payload) /
        (baseValueWithPacking || (orderItem?.baseValue ?? orderItem?.baseValue))) *
        100,
      decimals || 2
    );
    /* const newPackingPriceDiscount = toDecimals(
      (baseValueWithPacking || (orderItem?.baseValue ?? orderItem?.baseValue)) *
        ((100 - toDecimals(newDiscountPercentage, decimals ?? 2)) / 100),
      decimals || 2
    ); */

    let margin = yield getMargin(payload, orderItem?.financialCost, configuration.decimals);

    if (
      configuration?.useMarginControlByColor &&
      configuration?.marginControlByColor &&
      (user?.useMarginControlByColor === undefined ? true : user?.useMarginControlByColor)
    ) {
      const marginCalcByColor = configuration?.marginControlByColor
        ? configuration?.marginControlByColor?.filter(
            margin => payload >= margin?.initialValue && payload <= margin?.finalValue
          )
        : [];

      if (marginCalcByColor?.length > 0 && orderItem?.price?.priceTableCode === marginCalcByColor[0]?.priceTableCode) {
        margin = {
          ...margin,
          color: marginCalcByColor[0]?.color,
          action: marginCalcByColor[0]?.action,
          priceTableCode: marginCalcByColor[0]?.priceTableCode
        };
      }
    }

    let totalUnity = 0;

    if (orderItem?.typeOperation && orderItem?.unitQuantity !== orderItem?.unitQuantityBox) {
      totalUnity = calculateTypeOperation(payload, orderItem?.typeOperation, orderItem?.unitQuantityBox, configuration);
    }
    let tributacao = null;
    if (configuration?.taxation) {
      tributacao = getTax({
        client,
        configuration,
        paymentPlan: orderDetails?.paymentPlan,
        product: orderItem,
        shippingType: orderDetails?.shippingType,
        soldPrice: payload,
        soldQuantity: orderItem?.soldQuantity,
        tenantID: user.tenantID,
        // @ts-ignore
        tributacao: orderItem?.tributacao?.data,
        vendorDiscountPercentage: newDiscountPercentage
      });
    }

    const unitaryValue = orderService.buildUnitaryValue({
      baseValue: orderItem?.baseValue,
      discount: newDiscountPercentage,
      precision: configuration.decimals ?? 2,
      promotion: orderItem?.promotion,
      priceFactor: orderItem?.packing?.priceFactor ?? 1
    });

    const newOrderItem = {
      ...orderItem,
      tributacao,
      totalUnity,
      baseValueWithPacking,
      margin,
      soldPrice: payload,
      soldPriceWithPacking: payload,
      totalPrice: toDecimals(orderItem?.soldQuantity * payload, configuration.decimals ?? 2),
      discountPercentage: toDecimals(newDiscountPercentage ?? 0, configuration?.decimals ?? 2),
      discount: newDiscountPercentage > 0 ? true : false,
      // @ts-ignore
      tributacao,
      unitaryValue
    };

    yield put(orderActions.loadProductSuccess(newOrderItem));
  }
}

export function* updateDiscount({ payload, id }: any) {
  const { orderItem: orderItemBase } = yield select((state: AppStateType) => state?.order?.form);

  const { orderDetails, client, orderItems, commissions }: OrderCreateInterface = yield select(
    (state: AppStateType) => state?.order?.form?.data
  );
  const configuration: ConfigurationInterface = yield select(selectorConfiguration);

  const user: UserAuthInterface = yield select((state: AppStateType) => state?.auth.user);

  const orderItem = id ? orderItems.find(item => item.cartId === id) : orderItemBase;

  if (!orderItem) {
    return;
  }

  const discount = payload >= 100 ? 100 : payload <= -100 ? -100 : payload;

  let baseValue = orderItem?.baseValue;

  let soldPrice = toDecimals(
    toDecimals(baseValue * (1 - discount / 100), configuration?.decimals ?? 2) *
      (orderItem?.packing?.packingQuantity ?? orderItem?.packing?.[0]?.packingQuantity ?? 1) *
      (orderItem?.packing?.priceFactor ?? orderItem?.packing?.[0]?.priceFactor ?? 1),
    configuration?.decimals ?? 2
  );

  let soldPriceWithPacking = soldPrice;
  let totalPriceWithPacking = soldPrice * orderItem?.soldQuantity;

  let packingPrice = soldPrice;

  /* if (orderItem?.packingInfos?.unit === 'CX' || orderItem?.packing?.packingType === 'CX') {
    soldPriceWithPacking = toDecimals(soldPrice, configuration.decimals ?? 2);

    totalPriceWithPacking = toDecimals(soldPriceWithPacking * orderItem?.soldQuantity, configuration.decimals ?? 2);
  } */

  let margin = yield getMargin(soldPrice, orderItem?.financialCost, configuration.decimals);

  let totalUnity = 0;

  if (orderItem?.typeOperation && orderItem?.unitQuantity !== orderItem?.unitQuantityBox) {
    totalUnity = calculateTypeOperation(soldPrice, orderItem?.typeOperation, orderItem?.unitQuantityBox, configuration);
  }

  const commission = calculateCommissionInfo({
    allowUserCommission: configuration?.allowUserCommission ?? false,
    commissions: commissions || [],
    promotionCommission: orderItem?.promotion?.comissionPromo ?? 0,
    discountPercentage: discount,
    soldPrice: soldPriceWithPacking || soldPrice,
    soldQuantity: orderItem?.soldQuantity || 0,
    decimals: configuration?.decimals || 2
  });

  let tributacao = null;
  if (configuration?.taxation) {
    tributacao = getTax({
      client,
      configuration,
      paymentPlan: orderDetails?.paymentPlan,
      product: orderItem,
      shippingType: orderDetails?.shippingType,
      soldPrice,
      soldQuantity: orderItem?.soldQuantity,
      tenantID: user.tenantID,
      // @ts-ignore
      tributacao: orderItem?.tributacao?.data,
      vendorDiscountPercentage: payload
    });
  }

  let unitaryValue = toDecimals(soldPrice / (orderItem?.packing?.packingQuantity || 1), configuration.decimals ?? 2);

  const newOrderItem = {
    ...orderItem,
    discountPercentage: discount,
    tributacao,
    totalUnity,
    packingInfos: {
      ...orderItem.packingInfos,
      unitaryValue,
      packingPrice
    },
    totalPriceWithPacking,
    margin,
    soldPrice: soldPriceWithPacking ? soldPriceWithPacking : soldPrice,
    soldPriceWithPacking,
    totalPrice: totalPriceWithPacking
      ? totalPriceWithPacking
      : toDecimals(orderItem.soldQuantity * soldPrice, configuration.decimals ?? 2),
    // @ts-ignore
    tributacao,
    commission,
    ...(discount > 0
      ? {
          discount: true,
          discountPercentage: toDecimals(discount, configuration.decimals ?? 2)
        }
      : discount < 0
      ? {
          discount: false,
          discountPercentage: toDecimals(discount, configuration.decimals ?? 2)
        }
      : {
          discount: false,
          discountPercentage: toDecimals(discount, configuration.decimals ?? 2)
        }),
    unitaryValue
  };

  if (id) {
    yield put(orderActions.updateProductRequest(newOrderItem));
  } else {
    yield put(orderActions.loadProductSuccess(newOrderItem));
  }
}

export function* updateTotalUnity({ payload }: any) {
  const { orderItem } = yield select((state: AppStateType) => state?.order?.form);
  const configuration: ConfigurationInterface = yield select(selectorConfiguration);
  const user: UserAuthInterface = yield select((state: AppStateType) => state?.auth.user);

  const _total = calculateTypeOperation(
    payload,
    orderItem?.typeOperation === 'M' ? 'D' : 'M',
    orderItem?.unitQuantityBox,
    configuration
  );

  const discount = calcPercDiscount(orderItem?.baseValue, _total, configuration?.decimals ?? 2);
  let margin = yield getMargin(_total, orderItem?.financialCost, configuration.decimals);

  if (
    configuration?.useMarginControlByColor &&
    configuration?.marginControlByColor &&
    (user?.useMarginControlByColor === undefined ? true : user?.useMarginControlByColor)
  ) {
    const marginCalcByColor = configuration?.marginControlByColor
      ? configuration?.marginControlByColor?.filter(
          margin => payload >= margin?.initialValue && payload <= margin?.finalValue
        )
      : [];

    if (marginCalcByColor?.length > 0 && orderItem?.price?.priceTableCode === marginCalcByColor[0]?.priceTableCode) {
      margin = {
        ...margin,
        color: marginCalcByColor[0]?.color,
        action: marginCalcByColor[0]?.action,
        priceTableCode: marginCalcByColor[0]?.priceTableCode
      };
    }
  }

  const parsedOrderItem = {
    ...orderItem,
    totalUnity: payload,
    soldPrice: _total,
    margin,
    discountPercentage: toDecimals(discount ?? 0, configuration?.decimals ?? 2),
    discount: discount > 0 ? true : false
  };

  yield put(orderActions?.loadProductSuccess(parsedOrderItem));
}

export function* setPacking({ payload }: any) {
  const { client, orderItem } = yield select((state: AppStateType) => state?.order?.form);
  const configuration: ConfigurationInterface = yield select(selectorConfiguration);
  const order = yield select((state: AppStateType) => state?.order?.form?.data);
  const user: UserAuthInterface = yield select((state: AppStateType) => state?.auth.user);

  let baseValue = orderItem?.baseValue ?? 0;
  let promotion = order?.promotion;

  let soldPriceWithPacking = orderItem?.soldPriceWithPacking;
  let totalPriceWithPacking = orderItem?.totalPriceWithPacking;

  let soldPrice = toDecimals(
    toDecimals(baseValue * ((100 - orderItem.discountPercentage) / 100), configuration.decimals ?? 2) *
      (payload?.packingQuantity ?? 1) *
      (payload?.priceFactor ?? 1),
    configuration.decimals ?? 2
  );
  const availableQuantity = toDecimals(orderItem?.availableQuantity / payload?.packingQuantity, 2);

  if (promotion) {
    soldPrice =
      (promotion?.fixedPrice ?? promotion?.promotionalPrice) * payload?.packingQuantity * (payload?.priceFactor ?? 1);
  }

  const maximumPrice = toDecimals(
    baseValue + (baseValue * configuration?.maximumSalePercentage) / 100,
    configuration?.decimals ?? 2
  );
  const totalPrice = toDecimals(soldPrice * orderItem.soldQuantity, configuration.decimals ?? 2);

  soldPriceWithPacking = soldPrice;
  totalPriceWithPacking = totalPrice;

  let tributacao = null;
  if (configuration?.taxation) {
    tributacao = getTax({
      client,
      configuration,
      paymentPlan: order?.orderDetails?.paymentPlan,
      product: orderItem as unknown as ProductSale,
      shippingType: order?.orderDetails?.shippingType,
      soldPrice,
      soldQuantity: orderItem.soldQuantity,
      tenantID: user.tenantID,
      tributacao: orderItem.tributacao?.data as unknown as TributacaoInterface,
      vendorDiscountPercentage: orderItem.discountPercentage ?? 0
    });
  }

  const unitaryValue = orderService.buildUnitaryValue({
    baseValue,
    discount: orderItem.discountPercentage ?? 0,
    precision: configuration.decimals ?? 2,
    promotion,
    priceFactor: payload?.priceFactor ?? 1
  });

  const newOrderItem: OrderItemCreateInterface = {
    ...orderItem,
    tributacao,
    packing: payload,
    packingInfos: {
      ...orderItem.packingInfos,
      unit: payload.packingUnit,
      type: payload.packingType,
      barCode: payload.barCode,
      packingPrice: baseValue,
      basePackingPrice: baseValue,
      availableQuantity,
      unitaryValue
    },
    barCode: payload?.barCode ?? orderItem?.barCode ?? '0',
    soldPrice,
    baseValue,
    maximumPrice,
    totalPrice,
    soldPriceWithPacking,
    totalPriceWithPacking,
    unitaryValue
  };

  yield put(orderActions.loadProductSuccess(newOrderItem));
}

export function* setPromotion({ payload, quantity }: any) {
  const configuration = yield select((state: AppStateType) => state?.configuration);
  const { orderItem }: { orderItem: OrderItemCreateInterface } = yield select(
    (state: AppStateType) => state?.order?.form
  );
  const order: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);

  const user: UserAuthInterface = yield select((state: AppStateType) => state?.auth.user);

  let soldPrice;
  let soldPriceWithPacking = null;
  let soldQuantity;
  let discountPercentage;
  let financialCost = payload?.financialCost;

  if (financialCost === 0 || financialCost === undefined) {
    const findFinancialCost = orderItem?.stocks?.find(
      stock => stock.subsidiaryExternalCode === order?.orderDetails?.subsidiary?.externalCode
    );

    financialCost = orderItem?.stock?.financialCost || findFinancialCost?.financialCost;
  }

  let newOrderItem: OrderItemCreateInterface;
  let isPromotionGroupItem = isPromotionGroupValidator(payload) ?? false;
  let isBelowMinimumPrice = orderItem?.isBelowMinimumPrice ?? false;

  if (payload) {
    soldPrice =
      (payload?.promotionalPrice ?? orderItem?.baseValue) *
      (orderItem?.packing?.packingQuantity ?? 1) *
      (orderItem?.packing?.priceFactor ?? 1);
    soldQuantity = payload?.productQuantityBonificationPercentage
      ? Math.floor(payload?.productQuantityBonificationPercentage * (quantity / 100))
      : quantity ?? payload?.initialQuantity;
    discountPercentage = toDecimals(payload.discountPercentage, configuration?.decimals ?? 2);

    // isBelowMinimumPrice = comparePrices(
    //   orderItem?.soldPrice,
    //   payload?.promotionalPrice || payload?.minimumPrice,
    //   configuration
    // );

    if (soldQuantity === null || soldQuantity === undefined) {
      soldQuantity = orderItem?.packing ? 1 : orderItem?.quantityMultiple ?? 1;
    }

    if (payload?.includeFinancialPercentage) {
      financialCost = financialCost + (financialCost * orderItem?.percentualRate) / 100;
    }

    if (orderItem.soldQuantity !== soldQuantity) {
      const { isConfirmed } = yield notification.confirm(
        formatMessage('commonLabelWarning'),
        formatMessage('orderWarningPromotionApplied', {
          promotionName: payload.promotionName,
          initialQuantity: payload.initialQuantity
        }),
        formatMessage('commonLabelOK'),
        formatMessage('commonLabelCancel'),
        true
      );

      if (quantity >= payload.initialQuantity && quantity <= payload.finalQuantity && isConfirmed) {
        soldQuantity = quantity;
      } else {
        soldQuantity = payload?.initialQuantity;
      }
    }
  } else {
    soldPrice =
      orderItem.baseValue * (orderItem?.packing?.packingQuantity ?? 1) * (orderItem?.packing?.priceFactor ?? 1);
    discountPercentage = 0;
    soldQuantity = quantity || orderItem?.packing ? 1 : orderItem?.quantityMultiple ?? 1;
  }

  /* if (orderItem?.packing?.packingUnit === 'CX' || orderItem?.packing?.packingType === 'CX') {
    //const price = payload?.promotionalPrice || orderItem?.baseValue;
    soldPriceWithPacking = toDecimals(soldPrice, configuration?.decimals ?? 2);

    // if (payload) {
    //   isBelowMinimumPrice = comparePrices(price, payload?.promotionalPrice || payload?.minimumPrice, configuration);
    // }

    if (!payload) {
      soldPrice = toDecimals(orderItem?.packingInfos?.basePackingPrice, configuration?.decimals ?? 2);
      // isBelowMinimumPrice = comparePrices(soldPrice, orderItem?.minimumPrice, configuration);
    }
  } */

  // Não estáva igual o app
  // const margin = yield getMargin(soldPrice, financialCost, configuration?.decimals);

  const totalDiscount = calculateTotalMaxDiscount({
    applyCombinedDiscount: configuration?.applyCombinedDiscount,
    applySequentialDiscountsForMinimumPrice: configuration?.applySequentialDiscountsForMinimumPrice,
    promotionDiscount: payload?.discountPercentage,
    maxDiscount: orderItem?.price?.maxDiscountPercentage ?? orderItem?.price?.discountPercentage ?? 0,
    baseValue: orderItem.baseValue,
    decimals: configuration?.decimals
  });

  const minimumPriceTable = toDecimals(
    (orderItem.baseValue * (100 - totalDiscount)) / 100,
    configuration.decimals ?? 2
  );

  const minimumPrice = [
    payload?.minimumPrice,
    minimumPriceTable,
    orderItem.baseValue * (1 - orderItem?.promotions?.[0]?.discountPercentage / 100)
  ].sort((a, b) => a - b)[0];

  // const margin = validateMargin({
  //   configuration,
  //   orderItem,
  //   orderItems,
  //   promotion: payload,
  //   tenantID,
  //   paymentPlan
  // });

  // Essa parte busca os itens bonificados da promoção e seta no orderItem

  let bonificationItems = [];

  if (payload?.bonification?.length > 0) {
    const filters = {
      externalCodes: payload?.bonification?.map(item => item?.productExternalCode).join(','),
      subsidiaryExternalCode: order.orderDetails.subsidiary?.externalCode,
      usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
      applyTax: order?.client?.applyTax ? order?.client?.applyTax : undefined,
      applyTax2: configuration?.taxation ? order?.client?.applyTax2 : undefined,
      applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : undefined,
      discountPercentage,
      decimals: configuration?.decimals ?? 2,
      subsidiaryStockExternalCode: order.orderDetails?.subsidiaryStock?.externalCode ?? null,
      comparesMinimumPriceWithST: configuration?.comparesMinimumPriceWithST,
      paymentPlanExternalCodes: order?.client?.usePaymentPlansGroup
        ? [
            order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
            order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
          ].join(',')
        : order?.orderDetails?.paymentPlan?.externalCode,
      priceTableCode: order?.orderDetails?.priceTableCode ?? null,
      clientExternalCode: order?.client?.externalCode ?? null,
      paymentPlanExternalCode: order?.client?.usePaymentPlansGroup
        ? [
            order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
            order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
          ].join(',')
        : order?.orderDetails?.paymentPlan?.externalCode
    };

    const { data: detailedResponses } = yield call(orderService.getDetailedProductBonificated, {
      filters
    });

    bonificationItems = detailedResponses;
  }

  soldPriceWithPacking = toDecimals(soldPrice, configuration?.decimals ?? 2);
  const packingInfos = orderItem.packingInfos;

  if (packingInfos) {
    packingInfos.packingPrice = soldPrice;
  }

  const margin = yield getMarginTotals({
    allowProfitabilityMarkup: configuration.allowProfitabilityMarkup,
    financialCost,
    total: toDecimals(soldPrice * soldQuantity, configuration?.decimals ?? 2),
    precision: configuration.decimals
  });

  let tributacao = null;
  if (configuration?.taxation) {
    tributacao = getTax({
      client: order?.client,
      configuration,
      paymentPlan: order?.orderDetails?.paymentPlan,
      product: orderItem as unknown as ProductSale,
      shippingType: order?.orderDetails?.shippingType,
      soldPrice,
      soldQuantity,
      tenantID: user.tenantID,
      tributacao: orderItem.tributacao?.data as unknown as TributacaoInterface,
      vendorDiscountPercentage: discountPercentage
    });
  }

  const unitaryValue = orderService.buildUnitaryValue({
    baseValue: orderItem.baseValue,
    discount: discountPercentage,
    precision: configuration.decimals ?? 2,
    promotion: payload,
    priceFactor: orderItem?.packing?.priceFactor ?? 1
  });

  newOrderItem = {
    ...orderItem,
    ...packingInfos,
    // @ts-ignore
    tributacao,
    availableQuantity: orderItem?.availableQuantity ?? packingInfos?.availableQuantity,
    financialCost,
    discount: discountPercentage !== 0,
    discountPercentage,
    isPromotion: !!payload,
    ...(payload
      ? {
          promotion: {
            ...payload,
            bonificationQuantityAndPercentage: payload?.bonification,
            bonificationItems
          }
        }
      : { promotion: undefined }),
    soldQuantity,
    minimumPrice,
    isPromotionGroup: isPromotionGroupItem,
    soldPriceWithPacking: toDecimals(soldPrice, configuration.decimals ?? 2),
    soldPriceWithoutPacking: toDecimals(soldPrice, configuration.decimals ?? 2),
    totalPriceWithPacking: soldPriceWithPacking * (soldQuantity ?? 1),
    soldPrice: toDecimals(soldPrice, configuration.decimals ?? 2),
    totalPrice: toDecimals(soldPrice * soldQuantity, configuration.decimals ?? 2),
    isBelowMinimumPrice,
    margin,
    unitaryValue
  };

  yield put(orderActions.loadProductSuccess(newOrderItem));

  if (payload?.isPromotionKit) {
    yield put(
      orderActions.getPromotionKit({
        payload: newOrderItem?.promotion,
        show: true
      })
    );
  }
}

export function* updateProduct({ payload, closeModal }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));
  try {
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
    const { orderItems, orderDetails, client, orderType, commissions } = yield select(
      (state: AppStateType) => state?.order?.form?.data
    );
    const user: UserAuthInterface = yield select((state: AppStateType) => state?.auth.user);
    const promotionStock = payload?.promotion?.batchCode;
    let soldPrice = payload?.soldPrice;

    if (promotionStock) {
      const itemStock = payload?.productBatch?.find(product => product.batchCode === promotionStock);
      if (itemStock) {
        if (payload.soldQuantity > itemStock.quantity) {
          yield notification.confirm(
            formatMessage('commonLabelWarning'),
            formatMessage('orderWarningProductBatchPromotionQuantity', {
              promotionName: payload?.promotion?.promotionName,
              batchCode: itemStock.batchCode,
              quantity: itemStock.quantity
            }),
            formatMessage('commonLabelOK'),
            '',
            false
          );
          return;
        }
      } else {
        notification.warning(`A promoção ${payload?.promotion?.promotionName} não é do lote do produto`);
        return;
      }
    }

    if (configuration.blockSaleWithoutPromotion && !payload.promotion) {
      yield notification.confirm(
        formatMessage('commonLabelWarning'),
        formatMessage('orderWarningProductWithoutPromotion'),
        formatMessage('commonLabelOK'),
        '',
        false
      );

      return;
    }

    if (configuration?.splitBonusOrder && payload?.bonificated && !payload?.orderType) {
      yield notification.confirm(
        formatMessage('commonLabelWarning'),
        'Informar o tipo do pedido quando o for bonificação. Verifique as atualizações e tente novamente!',
        formatMessage('commonLabelOK'),
        '',
        false
      );
      return;
    }

    let minimumPrice = toDecimals(
      payload?.baseValue *
        (1 - ((payload?.maxDiscountPercentage || payload?.promotions?.[0]?.discountPercentage) ?? 0) / 100),
      configuration?.decimals ?? 2
    );
    let priceDifference = payload?.promotion?.priceDifference ?? undefined;
    let maxDiscountPercentage = payload?.maxDiscountPercentage ?? 0;

    const promotionsMaxDiscountPercentage = payload?.promotions?.[0]?.discountPercentage ?? 0;
    const maxDiscountTotal = Math.max(maxDiscountPercentage, promotionsMaxDiscountPercentage);

    if (payload?.promotion) {
      minimumPrice =
        payload?.promotion?.fixedPrice ??
        payload.promotion?.minimumPrice ??
        payload?.promotion?.promotion?.fixedPrice ??
        payload?.promotion?.promotion?.minimumPrice ??
        minimumPrice;

      if (payload?.promotion?.typeBalance) {
        const totalDiscount = calculateTotalMaxDiscount({
          applyCombinedDiscount: configuration?.applyCombinedDiscount,
          applySequentialDiscountsForMinimumPrice: configuration?.applySequentialDiscountsForMinimumPrice,
          promotionDiscount: payload?.promotion?.discountPercentage,
          maxDiscount: maxDiscountPercentage,
          baseValue: payload.baseValue,
          decimals: configuration?.decimals
        });
        minimumPrice = toDecimals((payload.baseValue * (100 - totalDiscount)) / 100, configuration.decimals ?? 2);
      }

      priceDifference = getPriceDifference(payload, configuration.decimals);
    }

    let isPromotionGroupItem = isPromotionGroupValidator(payload?.promotion ?? payload?.promotion?.promotion) ?? false;

    let parsedPromotion = {
      ...(payload?.promotion?.promotion ? payload?.promotion?.promotion : payload?.promotion),
      isPromotionGroup: isPromotionGroupItem
    };

    const validate = validateMargin({
      configuration,
      orderItem: payload,
      orderItems,
      promotion: parsedPromotion,
      tenantID: user.tenantID,
      paymentPlan: orderDetails?.paymentPlan
    });

    const legalEntityOrLegalPerson = client?.governmentalCode
      ? client?.governmentalCode?.length > 14
        ? 'J'
        : 'F'
      : null;

    const { data: restrictions } = yield call(productService.getSalesRestriction, {
      activityExternalCode: client?.activityExternalCode ?? null,
      legalEntityOrLegalPerson,
      minSaleValue: payload?.totalPrice,
      barCode: payload?.barCode ?? null,
      brandExternalCode: payload?.brandExternalCode ?? null,
      clientExternalCode: client?.externalCode ?? null,
      departmentExternalCode: payload?.departmentExternalCode ?? null,
      orderTypeExternalCode: orderType?.externalCode ?? null,
      paymentOptionExternalCode: orderDetails?.paymentOption?.externalCode ?? null,
      paymentPlanExternalCode: orderDetails?.paymentPlan?.externalCode ?? null,
      priceTableCode: orderDetails?.priceTableCode ?? null,
      productExternalCode: payload?.externalCode ?? null,
      providerExternalCode: payload?.providerExternalCode ?? null,
      roleExternalCode: user?.roleExternalCode ?? null,
      sectionExternalCode: payload?.sectionExternalCode ?? null,
      squareExternalCode: client?.squareExternalCode ?? null,
      subsidiaryExternalCode: orderDetails?.subsidiary?.externalCode ?? null,
      userExternalCode: user?.externalCode ?? null,
      shipping: orderDetails?.shippingType === SHIPPING_TYPE.FREE ? 'F' : 'C'
    });

    if (
      restrictions?.length > 0 &&
      restrictions?.some(restriction => restriction?.minSaleValue === 0 || restriction?.minSaleValue === null)
    ) {
      const errorMessage = {
        message: restrictions[0].restrictionReason
          ? formatMessage('orderWarningProductRestrictionWithReason', {
              productExternalCode: payload?.externalCode,
              restrictionExternalCode: restrictions[0].externalCode,
              restrictionReason: restrictions[0].restrictionReason
            })
          : formatMessage('orderWarningProductRestrictionWithoutReason', {
              productExternalCode: payload?.externalCode,
              restrictionExternalCode: restrictions[0].externalCode
            })
      };

      throw errorMessage;
    }

    let totalPrice = payload?.totalPrice;
    let soldPriceWithPacking = payload?.soldPriceWithPacking || payload?.soldPriceWithPacking;
    let quantity = payload?.soldQuantity;
    let totalPriceWithPacking = payload?.totalPriceWithPacking || payload?.totalPriceWithPacking;

    const isBelowMinimumPrice = checkProductIsBelowMinimumPrice({
      isPromotionKit: payload?.promotion?.isPromotionKit,
      soldPrice: payload?.baseValue * (1 - payload?.discountPercentage / 100),
      minimumPrice,
      acceptNegativeBalance: configuration?.acceptNegativeBalance && user.accountBalance,
      decimals: configuration?.decimals
    });

    const commission = calculateCommissionInfo({
      allowUserCommission: configuration?.allowUserCommission ?? false,
      commissions: commissions || [],
      promotionCommission: payload?.promotion?.comissionPromo ?? 0,
      discountPercentage: payload?.discountPercentage || 0,
      soldPrice,
      soldQuantity: payload?.soldQuantity || 0,
      decimals: configuration?.decimals || 2
    });

    let promotionMaxDiscountPercentage = payload?.promotions?.[0]?.discountPercentage ?? 0;
    let totalDiscount = 0;

    let promotion = null;
    if (payload?.promotion) {
      promotionMaxDiscountPercentage =
        payload?.promotion?.maxDiscountPercentage ?? payload?.promotion?.discountPercentage ?? 0;

      minimumPrice = toDecimals(
        payload.promotion.fixedPrice ?? payload.promotion.minimumPrice,
        configuration.decimals ?? 2
      );

      priceDifference = getPriceDifference(payload, configuration.decimals ?? 2);
      if (payload?.promotion?.typeBalance) {
        totalDiscount = calculateTotalMaxDiscount({
          applyCombinedDiscount: configuration?.applyCombinedDiscount,
          applySequentialDiscountsForMinimumPrice: configuration?.applySequentialDiscountsForMinimumPrice,
          promotionDiscount: payload?.promotion?.discountPercentage,
          maxDiscount: maxDiscountPercentage,
          baseValue: payload.baseValue,
          decimals: configuration?.decimals
        });
        minimumPrice = toDecimals((payload.baseValue * (100 - totalDiscount)) / 100, configuration.decimals ?? 2);
      }

      promotion = { ...payload?.promotion };
      if (promotion) {
        promotion.priceDifference = priceDifference;
      }
    }

    const unitaryValue = orderService.buildUnitaryValue({
      baseValue: payload?.baseValue,
      discount: payload?.discountPercentage,
      precision: configuration?.decimals ?? 2,
      promotion: payload?.promotion
    });

    const newOrderItem = {
      ...payload,
      promotion,
      priceDifference,
      totalMarginPercentage: validate?.marginPercentage,
      totalMarginValue: validate?.marginValue,
      isBelowMinimumPrice,
      isAboveDiscountAllowed: checkProductIsAboveDiscountAllowed({
        discountPercentage: payload.discountPercentage,
        promotionTypeBalance: payload?.promotion?.typeBalance,
        totalDiscount,
        promotionMaxDiscountPercentage: payload?.promotion ? promotionMaxDiscountPercentage : 0,
        maxDiscountTotal,
        acceptNegativeBalance: configuration?.acceptNegativeBalance && user.accountBalance
      }),
      isDiscountApprovalRequired: configuration?.useDiscountControl
        ? payload?.promotion ?? payload?.promotion?.promotion
          ? payload?.discountPercentage > (payload?.promotion?.discountPercentage || 0) &&
            payload?.discountPercentage <=
              ((payload?.promotion?.maxDiscountPercentage || 0) ?? (payload?.promotion?.discountPercentage || 0))
          : payload?.discountPercentage > payload?.price?.discountPercentage &&
            payload?.discountPercentage <= (payload?.price?.maxDiscountPercentage ?? payload?.price?.discountPercentage)
        : false,
      soldPrice,
      totalPrice: toDecimals(totalPrice, configuration.decimals ?? 2),
      soldPriceWithPacking,
      totalPriceWithPacking: toDecimals(
        totalPriceWithPacking || soldPriceWithPacking * payload?.soldQuantity,
        configuration.decimals ?? 2
      ),
      commission,
      unitaryValue
    };

    yield put(orderActions.updateProductSuccess(newOrderItem, user?.tenantID, configuration?.decimals));
    yield put(
      orderActions.updateOrderMargin({
        marginPercentage: validate?.marginPercentage,
        marginValue: validate?.marginValue
      })
    );
    toast.close();

    if (closeModal) {
      yield put(
        orderActions.setShowModalProductDetail({
          show: false,
          data: null
        })
      );

      yield put(orderActions.clearOrderItem());
    }

    yield call(notification.success, formatMessage('orderWarningProductUpdated'));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function findMaxFrete(arr) {
  let totalValue = 0;

  if (arr.length > 1) {
    let max = arr[0];
    totalValue = arr.reduce((total, current) => total + current.departamentValue, 0);

    for (let i = 1; i < arr.length; i++) {
      let v = arr[i];
      max = v.departamentValue > max.departamentValue ? { ...v, totalValue } : { ...max, totalValue };
    }
    return max;
  } else {
    return { ...arr[0], totalValue };
  }
}

export function* setProductComplement({ payload }: any) {
  const { orderItem } = yield select((state: AppStateType) => state?.order?.form);

  const newOrderItem = {
    ...orderItem,
    productObservation: payload
  };

  yield put(orderActions.loadProductSuccess(newOrderItem));
}

export function calculateShipping(arrFrete, orderItems, orderType, selectedSubsidiary) {
  let ship = arrFrete.filter(item => item.subsidiaryExternalCode === selectedSubsidiary);

  if (ship.length === 0) {
    ship = arrFrete.filter(item => item?.subsidiaryExternalCode === '');
  }

  if (orderType === ORDER_TYPE.NORMAL && ship.length >= 1) {
    ship.forEach(item => (item.departamentValue = 0));

    if (orderItems?.length >= 1) {
      for (let orderItem of orderItems) {
        for (let x = 0; x < ship.length; x++) {
          let departmentExternalCode = [...ship[x].departmentExternalCode];

          if (departmentExternalCode.includes(orderItem?.departmentExternalCode?.toString())) {
            ship[x].departamentValue += orderItem.soldQuantity * orderItem.soldPrice;
          } else if (ship[x].departamentValue === undefined) {
            ship[x].departamentValue = 0;
          } else {
            ship[x].departamentValue += 0;
          }
        }
      }

      const ActualShip = findMaxFrete(ship);

      if (ActualShip.departamentValue === 0) {
        ActualShip.departamentValue = ActualShip.totalValue;
      }

      if (ActualShip.departamentValue >= ActualShip.minimumPrice) {
        const shippingValue = ActualShip.totalValue * ActualShip.shippingPercentage + ActualShip.deliveryFee;

        return {
          shippingValue,
          minimumValue: ActualShip.minimumPrice,
          days: ActualShip.deliveryDeadline,
          freteNotFound: false
        };
      } else {
        const shippingValue = ActualShip.shippingValue + ActualShip.deliveryFee;

        return {
          shippingValue,
          minimumValue: ActualShip.minimumPrice,
          days: ActualShip.deliveryDeadline,
          freteNotFound: false
        };
      }
    } else {
      for (let x = 0; x < ship.length; x++) {
        ship[x].departamentValue = 0;
      }

      return {
        shippingValue: ship[0].shippingValue,
        minimumValue: ship[0].minimumPrice,
        days: ship[0].deliveryDeadline,
        freteNotFound: true
      };
    }
  } else {
    return {
      shippingValue: 0,
      minimumValue: 0,
      days: 0,
      freteNotFound: true
    };
  }
}

export function* validateCreditExceeded() {
  const configuration: ConfigurationInterface = yield select(selectorConfiguration);
  const { orderItems, shipping, client } = yield select((state: AppStateType) => state?.order?.form?.data);
  const { reasons } = yield select((state: AppStateType) => state?.order?.form.selects);

  const total =
    orderItems?.reduce((acumulator, price) => Number(acumulator) + Number(price.totalPrice), 0) +
    Number(shipping?.cost ?? 0);
  const availableCredit = client?.availableCredit - total;

  const maxAllowedOverCreditPercent = configuration?.maxAllowedOverCreditPercent;

  if (maxAllowedOverCreditPercent != null) {
    const allowedOverdueCredit =
      client?.availableCredit + client?.availableCredit * (maxAllowedOverCreditPercent / 100);
    const overdueAvailableCredit = allowedOverdueCredit - total;
    if (overdueAvailableCredit <= 0) {
      return {
        reason: REASON_TYPE.MAX_CREDIT_PERCENTAGE_EXCEEDED,
        creditLimit: allowedOverdueCredit,
        availableCredit: overdueAvailableCredit
      };
    }
  }

  const blockCreditExceeded = availableCredit <= 0;

  try {
    const reason = blockCreditExceeded ? reasons.find(item => item.type === REASON_TYPE.CREDIT_EXCEEDED) : null;

    return reason
      ? {
          reason,
          creditLimit: client?.creditLimit,
          availableCredit
        }
      : null;
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* loadShipping() {
  try {
    const configuration: ConfigurationInterface = yield select(selectorConfiguration);
    const { orderDetails, orderItems, totalPrice, client } = yield select(
      (state: AppStateType) => state?.order?.form?.data
    );
    const squareExternalCode = client?.squareExternalCode;
    const { subsidiary, shippingType, orderType } = orderDetails;
    const selectedSubsidiary = subsidiary?.externalCode;
    const productsTotal = totalPrice;
    const isShippingEditable = configuration?.allowShippingEdit && orderDetails?.shippingFee > 0;

    let payload;

    if (orderType !== ORDER_TYPE.NORMAL) {
      payload = {
        shippingValue: 0,
        minimumValue: 0,
        days: 1,
        freteNotFound: false,
        cost: 0
      };

      return yield put(orderActions.getShippingSuccess(payload, configuration?.decimals));
    }

    const { data } = yield call(api.get, '/offline/domain-shipping');
    let arrFrete = data.filter(item => item.squareExternalCode === squareExternalCode);

    if (shippingType === SHIPPING_TYPE.PAID) {
      let costShip: any = calculateShipping(arrFrete, orderItems, orderType, selectedSubsidiary);

      costShip = {
        ...costShip,
        cost: isShippingEditable
          ? orderDetails.shippingFee
          : costShip.minimumValue > productsTotal
          ? costShip.shippingValue
          : 0
      };
      payload = costShip;
    } else {
      payload = {
        shippingValue: 0,
        minimumValue: 0,
        days: 1,
        freteNotFound: false,
        cost: 0
      };
    }

    yield put(orderActions.getShippingSuccess(payload, configuration?.decimals));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* runCurrentAccount() {
  const user = yield select((state: AppStateType) => state?.auth?.user);
  const order = yield select((state: AppStateType) => state?.order?.form?.data);
  if (user?.accountBalance) {
    return {
      total: order?.balance?.difference ?? 0,
      isNegative: (order?.balance?.finalBalance ?? 0) < 0
    };
  } else {
    return {
      total: 0,
      isNegative: false
    };
  }
}

export function* calculateBalance(payload) {
  try {
    const configuration: ConfigurationInterface = yield select(selectorConfiguration);
    const user = yield select((state: AppStateType) => state.auth.user);
    const { orderItems, orderType } = yield select((state: AppStateType) => state?.order?.form?.data);
    const decimals = 2;

    if (!user?.accountBalance) {
      return;
    }

    let itemsToCalculate = [...(orderItems ?? [])];
    let updatedItem = null;
    if (payload?.payload) {
      const index = itemsToCalculate.findIndex(i => i._id === payload.payload._id);
      if (index >= 0) {
        itemsToCalculate[index] = payload.payload;
      } else {
        itemsToCalculate.push(payload.payload);
      }

      updatedItem = payload.payload;
    }

    const calculateItemBalance = item => {
      const baseValue = item.baseValue ?? 0;
      const quantity = item.soldQuantity ?? 1;
      const appliedDiscountPct = item.discountPercentage ?? 0;
      const promotion = item.promotion;

      if (
        configuration?.useAccountBalanceOnBonificationOrder &&
        (orderType.type === ORDER_TYPE.BONIFICATION || orderType.type === ORDER_TYPE.BONIFICATION_FREE_TAX)
      ) {
        const soldPrice = item.soldPrice ?? 0;
        const totalWithoutTribute = soldPrice * quantity;
        return -totalWithoutTribute;
      }

      let itemDifference = 0;

      if (!promotion) {
        const discountValue = (baseValue * appliedDiscountPct) / 100;
        itemDifference -= discountValue * quantity;
        if (itemDifference > 0 && configuration?.skipCreditAccountBalance) {
          return 0;
        }
        return itemDifference;
      }

      if (!promotion.typeBalance) return 0;

      const promoDiscountPct = promotion.discountPercentage ?? 0;
      const promoPrice = baseValue - (baseValue * promoDiscountPct) / 100;

      if (appliedDiscountPct > promoDiscountPct && promotion.debitBalance) {
        const extraPct = appliedDiscountPct - promoDiscountPct;
        const extraValue = ((baseValue * extraPct) / 100) * quantity;
        itemDifference -= extraValue;
      } else if (appliedDiscountPct < promoDiscountPct && promotion.creditBalance) {
        const lessPct = promoDiscountPct - appliedDiscountPct;
        const gainValue = ((baseValue * lessPct) / 100) * quantity;
        itemDifference += gainValue;
      }
      if (itemDifference > 0 && configuration?.skipCreditAccountBalance) {
        return 0;
      }
      return itemDifference;
    };

    const totalDifference = itemsToCalculate.reduce((acc, item) => acc + calculateItemBalance(item), 0);

    const itemDifference = updatedItem ? calculateItemBalance(updatedItem) : 0;

    const currentValue = toDecimals(totalDifference, decimals);
    const finalBalance = toDecimals((user.currentBalanceAmount ?? 0) + currentValue, decimals);
    yield put(
      orderActions.updateBalanceSuccess({
        difference: currentValue,
        finalBalance,
        itemDifference: toDecimals(itemDifference, decimals)
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getBonificatedTotal() {
  const { orderItems } = yield select((state: AppStateType) => state?.order?.form?.data);

  const bonificatedItems = orderItems?.filter(item => item?.bonificated === true);

  if (bonificatedItems.length > 0) {
    const total = bonificatedItems.reduce((a, b) => a + b?.totalPrice, 0);

    return -total;
  } else {
    return null;
  }
}

export function* validadeOrder() {
  try {
    const order: OrderCreateInterface = yield select((state: AppStateType) => state?.order?.form?.data);
    const configuration = yield select(selectorConfiguration);
    const user: UserAuthInterface = yield select((state: AppStateType) => state?.auth?.user);

    const { orderDetails, client, orderType }: OrderCreateInterface = yield select(
      (state: AppStateType) => state?.order?.form?.data
    );

    let somePendencies = {
      isBlock: false,
      messages: {},
      notices: {},
      types: []
    };

    let bonificatedTotal = yield getBonificatedTotal();

    if (bonificatedTotal === null) bonificatedTotal = 0;

    if (
      configuration?.maxLimitBalance !== null &&
      (!configuration?.acceptNegativeBalance || configuration?.useNegativeSaleWithCurrentAccountLimit) &&
      user?.accountBalance
    ) {
      const prices =
        order?.orderItems?.reduce((acumulator, price) => acumulator + price.soldPrice * price.soldQuantity, 0) ?? 0;

      const balanceOrder = prices + bonificatedTotal + user?.currentBalanceAmount;

      if (configuration?.maxLimitBalance >= balanceOrder) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          notices: {
            ...somePendencies.notices,
            maxLimitBalance: formatMessage('orderCheckoutMaxLimitBalance', {
              price: formatNumber(configuration?.maxLimitBalance, { style: 'currency' })
            })
          },
          types: [...somePendencies.types, 'maxLimitBalance']
        });
      }
    }

    //Checa se tem alguma filial retira selecionada para gerar um aviso
    const stockIsChanged =
      order?.orderDetails?.subsidiaryStock &&
      order?.orderDetails?.subsidiaryStock?.externalCode !== order?.orderDetails?.subsidiary?.externalCode;
    if (stockIsChanged) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: false,
        notices: {
          ...somePendencies.notices,
          stockIsChanged: formatMessage('orderCheckoutStockIsChanged', {
            subsidiary: order?.orderDetails?.subsidiaryStock?.name
          })
        }
      });
    }

    //Checa se o pedido tem controle de carga para gerar um aviso
    const loadControl = configuration?.useLoadControl && order?.load?.loadType?._id !== '0';
    if (loadControl) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: false,
        notices: {
          ...somePendencies.notices,
          stockIsChanged: formatMessage('orderCheckoutLoadControl')
        },
        types: [...somePendencies.types, 'useLoadControl']
      });
    }

    //Checa se o pedido tem frete pago e quanto falta para deixá-lo gratis gerando um aviso
    const shippingPriceRemaing = order?.shipping?.cost && order?.shipping?.minimumValue > order?.totalPrice;
    if (shippingPriceRemaing) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: false,
        notices: {
          ...somePendencies.notices,
          shippingPriceRemaing: formatMessage('orderCheckoutShippingPriceRemaing', {
            price: formatNumber(order?.shipping?.minimumValue - order?.totalPrice, { style: 'currency' })
          })
        }
      });
    }

    //Checa se a margem total do pedido está abaixo do mínimo
    let minimumMargin = configuration?.minimumMargin;
    let productWithoutBonificated = toDecimals(order?.totalPrice - bonificatedTotal * -1, configuration?.decimals);

    const marginCalc = configuration?.marginControl
      ? configuration?.marginControl?.filter(
          margin => productWithoutBonificated >= margin.initialValue && productWithoutBonificated <= margin.finalValue
        )
      : [];

    if (marginCalc?.length > 0) {
      minimumMargin = marginCalc[0].marginValue;
    }

    const financialCostTotal = order?.orderItems?.reduce(
      (acumulator, current) =>
        toDecimals(acumulator + current?.financialCost * Number(current?.soldQuantity), configuration?.decimals ?? 2),
      0
    );

    let marginControl = {
      minimumMargin,
      marginValue: productWithoutBonificated - financialCostTotal,
      marginPercentage:
        productWithoutBonificated > 0
          ? ((productWithoutBonificated - financialCostTotal) / productWithoutBonificated) * 100
          : 0,
      marginIsBellowMargin:
        ((productWithoutBonificated - financialCostTotal) / productWithoutBonificated) * 100 < minimumMargin
    };

    let marginIsBellowMargin = false;

    if (configuration?.useProfitControl) {
      marginIsBellowMargin = marginControl.marginIsBellowMargin;
    }

    if (marginIsBellowMargin) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: true,
        messages: {
          ...somePendencies.messages,
          marginIsBellowMargin: formatMessage('orderCheckoutMarginIsBellowMargin', {
            percentage: marginControl.minimumMargin
          })
        },
        types: [...somePendencies.types, 'useProfitControl']
      });
    }

    //Checa se existe algum produto abaixo do preço mínimo no carrinho e válida se o vendedor possui motor de negócios
    const hasItemsBelowMininumPrice = order?.orderItems?.filter(
      (orderItem: OrderItemCreateInterface) => orderItem?.isBelowMinimumPrice
    );
    const hasItemsAboveDiscountAllowed = order?.orderItems?.filter(
      (orderItem: OrderItemCreateInterface) => orderItem?.isAboveDiscountAllowed
    );
    const useNegotiationEngine = configuration?.useNegotiationEngine;
    const acceptBlockedSale = configuration?.acceptBlockedSale;
    const acceptNegativeBalance = configuration?.acceptNegativeBalance;
    const useNegativeSaleWithCurrentAccountLimit = configuration?.useNegativeSaleWithCurrentAccountLimit;
    const havePromotion = !!order?.orderItems?.filter(
      (orderItem: OrderItemCreateInterface) =>
        orderItem?.promotion?.externalCode || orderItem?.promotion?.codpromocaomed
    );

    const havePromotionKit = !!order?.orderItems?.filter(
      (orderItem: OrderItemCreateInterface) => orderItem?.promotion?.isPromotionKit
    );
    const userAccountBallance = {
      accountBalance: user?.accountBalance,
      currentBalanceAmount: user?.currentBalanceAmount
    };

    let currentAccount = {
      total: 0,
      isNegative: false
    };
    currentAccount = yield runCurrentAccount();

    let balanceOrder = userAccountBallance?.currentBalanceAmount + currentAccount?.total + bonificatedTotal;

    if (hasItemsAboveDiscountAllowed.length && !acceptNegativeBalance) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: true,
        messages: {
          ...somePendencies.messages,
          hasItemsAboveDiscountAllowed: formatMessage('orderCheckoutHasItemsAboveDiscountAllowed')
        },
        types: [...somePendencies.types, 'hasItemsAboveDiscountAllowed']
      });
    }

    if (!!hasItemsBelowMininumPrice.length) {
      if (useNegotiationEngine && !userAccountBallance?.accountBalance) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            hasItemsBelowMininumPrice: formatMessage('orderCheckoutHasItemsBelowMininumPriceNegotiation')
          },
          types: [...somePendencies.types, 'hasItemsBelowMininumPriceNegotiation']
        });
      } else if (!useNegotiationEngine && !userAccountBallance?.accountBalance) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            hasItemsBelowMininumPrice: formatMessage(
              acceptBlockedSale
                ? 'orderCheckoutHasItemsBelowMininumPrice'
                : 'orderCheckoutHasItemsBelowMininumPriceAcceptBlockedSale'
            )
          },
          types: [...somePendencies.types, 'hasItemsBelowMininumPrice']
        });
      } else if (
        (!acceptNegativeBalance || useNegativeSaleWithCurrentAccountLimit) &&
        userAccountBallance?.accountBalance &&
        balanceOrder <= (configuration?.maxLimitBalance || 0)
      ) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages
          },
          types: [...somePendencies.types]
        });
      }

      if (!configuration?.acceptBlockedSale || !acceptNegativeBalance) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            hasItemsBelowMininumPrice: formatMessage('orderCheckoutHasItemsBelowMininumPriceAcceptBlockedSale')
          },
          types: [...somePendencies.types, 'hasItemsBelowMininumPriceAcceptBlockedSale']
        });
      }
    }

    //Checa se o total da venda está abaixo do valor mínimo em configuration
    if (configuration?.minimumOrderValue) {
      const minimumOrderValue = order?.totalPrice < configuration?.minimumOrderValue && !order?.complementary;

      if (minimumOrderValue) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            minimumOrderValue: formatMessage('orderCheckoutOrderMinimumValue', {
              minimumPrice: formatNumber(configuration?.minimumOrderValue, {
                style: 'currency'
              })
            })
          },
          types: [...somePendencies.types, 'minimumOrderValue']
        });
      }
    }

    //Checa se existem mais produtos além do que há no combo/kit
    const invalidCombo = checkBlockComboKit(order?.orderItems);

    if (invalidCombo) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: true,
        messages: {
          ...somePendencies.messages,
          requireAdditionalProductToKit: formatMessage('orderCheckoutRequiredAdditionalProductToKit')
        },
        types: [...somePendencies.types, 'invalidCombo']
      });
    }

    //Checa se o total da venda está abaixo do valor mínimo do tipo de pedido ou do plano de pagamento
    if (
      order?.orderType?.minimumValue > order?.orderDetails?.paymentPlan?.orderMinValue ||
      order?.client?.usePaymentPlansGroup
    ) {
      const minimumOrderTypeValue = order?.totalPrice < order?.orderType?.minimumValue;

      if (minimumOrderTypeValue) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            minimumOrderTypeValue: formatMessage('orderCheckoutMinimumOrderTypeValue', {
              string: formatNumber(order?.orderType?.minimumValue, {
                style: 'currency'
              })
            })
          },
          types: [...somePendencies.types, 'minimumOrderTypeValue']
        });
      }
    } else {
      const minimumPaymentPlanValue =
        order?.totalPrice < order?.orderDetails?.paymentPlan?.orderMinValue && !order?.complementary;

      if (minimumPaymentPlanValue) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            minimumPaymentPlanValue: formatMessage('orderCheckoutMinimumPaymentPlanValue', {
              string: formatNumber(order?.orderDetails?.paymentPlan?.orderMinValue, {
                style: 'currency'
              })
            })
          },
          types: [...somePendencies.types, 'minimumPaymentPlanValue']
        });
      }
    }

    //Checa se o total da venda está acima do valor máximo do tipo de pedido
    if (order?.orderType?.maximumValue !== 0) {
      const maximumOrderTypeValue = order?.totalPrice > order?.orderType?.maximumValue;

      if (maximumOrderTypeValue) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            maximumOrderTypeValue: formatMessage('orderCheckoutMaximumOrderTypeValue', {
              string: formatNumber(order?.orderType?.maximumValue, {
                style: 'currency'
              })
            })
          },
          types: [...somePendencies.types, 'maximumOrderTypeValue']
        });
      }
    }

    //Checa se o cliente tem o limite de crédito excedido
    const blockCreditExceeded = yield validateCreditExceeded();
    if (blockCreditExceeded && blockCreditExceeded.reason === REASON_TYPE.MAX_CREDIT_PERCENTAGE_EXCEEDED) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: true,
        messages: {
          ...somePendencies.messages,
          blockCreditExceeded: formatMessage('orderCheckoutBlockCreditOverdueExceededNewOrder', {
            percentage: `${configuration?.maxAllowedOverCreditPercent}`,
            creditLimit: formatNumber(blockCreditExceeded?.creditLimit ?? 0, {
              style: 'currency'
            }),
            availableCredit: formatNumber(order?.client?.availableCredit ?? 0, {
              style: 'currency'
            }),
            remainCredit: formatNumber(blockCreditExceeded?.availableCredit * -1, { style: 'currency' })
          })
        },
        types: [...somePendencies.types, 'blockMaxCreditPercentegeExceeded']
      });
    } else if (blockCreditExceeded && blockCreditExceeded?.reason?.actions?.orders === REASON_ACTIONS.DENY) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: true,
        messages: {
          ...somePendencies.messages,
          blockCreditExceeded: formatMessage('orderCheckoutBlockCreditExceededNewOrder', {
            reason: blockCreditExceeded?.reason?.description,
            creditLimit: formatNumber(blockCreditExceeded?.creditLimit ?? 0, {
              style: 'currency'
            }),
            availableCredit: formatNumber(order?.client?.availableCredit ?? 0, {
              style: 'currency'
            }),
            remainCredit: formatNumber(blockCreditExceeded?.availableCredit * -1 ?? 0, { style: 'currency' })
          })
        },
        types: [...somePendencies.types, 'blockCreditExceededNewOrder']
      });
    } else if (
      blockCreditExceeded &&
      blockCreditExceeded?.reason?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED &&
      order?.orderDetails?.paymentPlan?.saleType !== SALE_TYPE.CASH
    ) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: true,
        messages: {
          ...somePendencies.messages,
          blockCreditExceeded: formatMessage('orderCheckoutBlockCreditExceededAnlyCach', {
            reason: blockCreditExceeded?.reason?.description,
            creditLimit: formatNumber(blockCreditExceeded?.creditLimit ?? 0, {
              style: 'currency'
            }),
            availableCredit: formatNumber(order?.client?.availableCredit ?? 0, {
              style: 'currency'
            }),
            remainCredit: formatNumber(blockCreditExceeded?.availableCredit * -1 ?? 0, { style: 'currency' })
          })
        },
        types: [...somePendencies.types, 'blockCreditExceededAnlyCach']
      });
    }

    //Checa se existem itens abaixo do preço mínimo e também o saldo do vendedor para pedidos no ERP
    balanceOrder = userAccountBallance?.currentBalanceAmount + currentAccount?.total;

    if (userAccountBallance?.accountBalance && hasItemsBelowMininumPrice?.length > 0) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: false,
        messages: {
          ...somePendencies.messages,
          balanceOrderMinimumValue: formatMessage('orderCheckoutBalanceOrderMinimumValueERP')
        },
        types: [...somePendencies.types, 'balanceOrderMinimumValue']
      });
    } else if (
      (!acceptNegativeBalance || useNegativeSaleWithCurrentAccountLimit) &&
      balanceOrder < configuration?.maxLimitBalance
    ) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: false,
        messages: {
          ...somePendencies.messages,
          balanceOrderInsufficient: formatMessage('orderCheckoutBalanceOrderInsufficientERP')
        },
        types: [...somePendencies.types, 'balanceOrderInsufficient']
      });
    }

    //Checa se o vendedor possui saldo para fazer o pedido
    const verifyBallance =
      currentAccount?.total < 0 &&
      currentAccount?.isNegative &&
      currentAccount?.total + user?.currentBalanceAmount < (configuration?.maxLimitBalance || 0);
    if (user?.accountBalance && verifyBallance && (!acceptNegativeBalance || useNegativeSaleWithCurrentAccountLimit)) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: true,
        messages: {
          ...somePendencies.messages,
          balanceAmountInsufficient: formatMessage('orderCheckoutBalanceAmountInsufficient', {
            amount: formatNumber(user?.currentBalanceAmount ?? 0, {
              style: 'currency'
            })
          })
        },
        types: [...somePendencies.types, 'balanceAmountInsufficient']
      });
    }

    //Checa se tem algum produto com quantidade vendida maior que adisponível conforme o parêmetro acceptSaleWithoutStock
    const productsWithoutStock = order.orderItems?.filter(
      orderItem => orderItem.soldQuantity * (orderItem?.packing?.packingQuantity ?? 1) > orderItem.availableQuantity
    );

    if (productsWithoutStock.length) {
      productsWithoutStock.forEach((item, index) => {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            [`productWithoutStock${index}`]: formatMessage('orderCheckoutWithoutStock', {
              externalCode: item?.externalCode,
              productName: item?.name,
              availableQuantity: item?.availableQuantity,
              soldQuantity: item?.soldQuantity * (item?.packing?.packingQuantity ?? 1)
            })
          },
          types: [...somePendencies.types, 'acceptSaleWithoutStock']
        });
      });
    }

    //Checa se tem algum produto que utiliza promotion abaixo do valor mínimo
    const orderItemsWithPromotions: PromotionWithItemsInterface[] = yield select(combinePromotions());

    if (orderItemsWithPromotions.length) {
      orderItemsWithPromotions.forEach((item, index) => {
        if (!item.isAboveMinimumValueOfPromotion) {
          let message = {};
          message = {
            [`hasItemsBelowMininumPriceInPromotion${index}`]: formatMessage(
              'orderCheckoutHasItemsBelowMininumPriceInPromotion',
              {
                promoID: item?.promotion?.promotionMedCode ?? item?.promotion?.externalCode,
                promoName: item?.promotion?.name,
                minimumPrice: formatNumber(item?.promotion?.minimumPromotionValue ?? 0, {
                  style: 'currency'
                }),
                remainingPrice: formatNumber((item?.promotion?.minimumPromotionValue ?? 0) - item?.totalPrice ?? 0, {
                  style: 'currency'
                })
              }
            )
          };

          somePendencies = updatePendencies(somePendencies, {
            isBlock: true,
            messages: {
              ...somePendencies.messages,
              ...message
            },
            types: [...somePendencies.types, 'hasItemsBelowMininumPriceInPromotion']
          });
        }

        if (!item.isWithinQuantityOfPromotion && !havePromotionKit) {
          let message = {};
          message = {
            [`hasItemsWithoutQuantityInPromotion${index}`]: formatMessage(
              'orderCheckoutHasItemsWithoutQuantityInPromotion',
              {
                promoID: item?.promotion?.promotionMedCode ?? item?.promotion?.externalCode,
                promoName: item?.promotion?.name,
                totalQuantity: item?.totalQuantity,
                initialQuantity: item?.promotion?.initialQuantity ?? '-',
                finalQuantity: item?.promotion?.finalQuantity ?? '-'
              }
            )
          };

          somePendencies = updatePendencies(somePendencies, {
            isBlock: true,
            messages: {
              ...somePendencies.messages,
              ...message
            },
            types: [...somePendencies.types, 'hasItemsWithoutQuantityInPromotion']
          });
        }
      });

      /* somePendencies = updatePendencies(somePendencies, {
        ...somePendencies,
        messages: {
          ...somePendencies.messages,
          ...invalidPromos
        },
        types: somePendencies.types
      }); */
    }

    //Checa se o total mínimo de produtos com grupo de plano de pagamento é obedecido
    if (order?.client?.usePaymentPlansGroup) {
      let totalsGroup = {
        paymentPlan1: 0,
        paymentPlan2: 0
      };

      let invalidGroup = {
        paymentPlan1: false,
        paymentPlan2: false
      };

      order?.orderItems?.forEach(item => {
        if (item?.paymentPlansGroup === '1') {
          totalsGroup.paymentPlan1 += item.totalPrice;
        } else if (item?.paymentPlansGroup === '2') {
          totalsGroup.paymentPlan2 += item.totalPrice;
        }
      });

      if (totalsGroup.paymentPlan1 > 0) {
        invalidGroup.paymentPlan1 =
          totalsGroup.paymentPlan1 < order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.orderMinValue;
      }

      if (totalsGroup.paymentPlan2 > 0) {
        invalidGroup.paymentPlan2 =
          totalsGroup.paymentPlan2 < order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.orderMinValue;
      }

      if (invalidGroup.paymentPlan1) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            minimumValuePaymentPlan1: formatMessage('orderCheckoutMinimumValuePaymentPlan', {
              name: order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.name,
              minimumPrice: formatNumber(order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.orderMinValue ?? 0, {
                style: 'currency'
              })
            })
          },
          types: [...somePendencies.types, 'minimumValuePaymentPlan']
        });
      }

      if (invalidGroup.paymentPlan2) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            minimumValuePaymentPlan2: formatMessage('orderCheckoutMinimumValuePaymentPlan', {
              name: order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.name,
              minimumPrice: formatNumber(order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.orderMinValue ?? 0, {
                style: 'currency'
              })
            })
          },
          types: [...somePendencies.types, 'minimumValuePaymentPlan']
        });
      }
    }

    if (!configuration?.acceptBlockedSale && !configuration?.useDiscountControl && !havePromotion) {
      const hasItemsWithHigherDiscountPercentage = order?.orderItems?.filter(
        orderItem => orderItem?.discountPercentage > orderItem?.maxDiscountPercentage
      );

      if (hasItemsWithHigherDiscountPercentage.length > 0) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            hasItemsWithHigherDiscountPercentage: formatMessage('orderCheckoutHasItemsWithHigherDiscountPercentage')
          },
          types: [...somePendencies.types, 'hasItemsWithHigherDiscountPercentage']
        });
      }
    }

    if (!configuration?.acceptBlockedSale && configuration?.useDiscountControl && !havePromotion) {
      if (hasItemsAboveDiscountAllowed) {
        somePendencies = updatePendencies(somePendencies, {
          isBlock: true,
          messages: {
            ...somePendencies.messages,
            hasItemsWithHigherDiscountPercentage: formatMessage(
              'orderCheckoutHasItemsBelowMininumPriceAcceptBlockedSale'
            )
          },
          types: [...somePendencies.types, 'hasItemsBelowMininumPriceAcceptBlockedSale']
        });
      }
    }

    //Checa se tem items com quantidade zerada
    const hasItemsWithZeroQuantity = order?.orderItems?.filter(orderItem => orderItem?.soldQuantity === 0);

    if (hasItemsWithZeroQuantity.length > 0) {
      somePendencies = updatePendencies(somePendencies, {
        isBlock: true,
        messages: {
          ...somePendencies.messages,
          hasItemsWithZeroQuantity: formatMessage('orderCheckoutHasItemsWithZeroQuantity')
        },
        types: [...somePendencies.types, 'hasItemsWithZeroQuantity']
      });
    }

    const promotionsWithRequiredItems = getPendingPromotions(order?.orderItems);
    if (promotionsWithRequiredItems?.length > 0) {
      promotionsWithRequiredItems.forEach((promo, index) => {
        promo?.required?.forEach((required, x) => {
          let message = {};
          message = {
            [`hasItemsRequiredByPromotion${index}${x}`]: formatMessage('orderCheckoutHasItemsRequiredByPromotion', {
              promoID: promo?.promotionMedCode ?? promo?.externalCode ?? promo?.aggregatorExternalCode,
              promoName: promo?.promotionName,
              externalCode: required.productExternalCode,
              minQuantity: required.minQuantity
            })
          };
          somePendencies = updatePendencies(somePendencies, {
            isBlock: true,
            messages: {
              ...somePendencies.messages,
              ...message
            },
            types: [...somePendencies.types, 'hasItemsRequiredByPromotion']
          });
        });
      });
    }

    yield call(getRestrictionsRequest, {
      closeModal: false
    });
    yield put(orderActions.validateOrderSuccess(somePendencies));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* sendOrder({ payload }: any) {
  yield put(orderActions.setShowModalOrder(true));
  try {
    const order: OrderCreateInterface = yield select(state => state.order.form.data);
    const user: UserInterface = yield select((state: AppStateType) => state.auth.user);
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

    const { slug, searchParams } = payload;

    yield delay(1000);

    let realmID = `${user?.externalCode}_${new Date().getTime().toString()}`;
    const isBudget = order?.fromBudget && order.budget?.isBudget;
    const isOrderToEdit = slug === ORDER_TYPE.BONIFICATION ? null : order?._id ?? null;
    let _id = order?._id ?? undefined;

    const allowPaymentLink = configuration?.allowPaymentLink;

    //Budget
    if (isBudget && !order.isCopy && order?.realmID) {
      realmID = order?.realmID as string;
    }

    //Reopen Order WaitingLoad
    // if (order?.orderDetails?.reopen) {
    //   realmID = order?.realmID.toString();
    // }

    const orderVinculatedExternalNumber = searchParams.includes('orderVinculatedExternalNumber')
      ? searchParams.split('orderVinculatedExternalNumber=')[1]
      : null;
    const isChild = searchParams.includes('parentInternalCode') ? searchParams.split('parentInternalCode=')[1] : null;

    const payloadOrder: OrderPostInterface = yield call(
      gerateOrder,
      { ...order, _id },
      realmID,
      orderVinculatedExternalNumber
    );
    const hasBlock = yield call(validateProductListBonification, {
      orderItems: payloadOrder?.orderItems,
      orderTypeProps: { type: order?.orderDetails?.orderType },
      subsidiary: order?.orderDetails?.subsidiary,
      removeOnRecalculate: true
    });

    if (hasBlock) {
      yield put(
        orderActions.updateModal({
          type: 'warning',
          data: {
            title: 'Verifique os itens do pedido',
            message: 'Itens do pedido estão bloqueados, verifique os itens do pedido e tente novamente.',
            preventSendDashboard: true
          }
        })
      );
      return;
    }

    let orderTypeSend = ORDER_TYPE_SEND.ORDER;

    let budgetEditSend = !!order?.fromBudget && !!order.originalBudget?.isBudget;

    if (
      payloadOrder.orderStatus === ORDER_STATUS.WAITING_APPROVAL ||
      payloadOrder.orderStatus === ORDER_STATUS.WAITING_LOAD
    ) {
      budgetEditSend = true;
    }

    if (payloadOrder?.budget?.isBudget) {
      orderTypeSend = ORDER_TYPE_SEND.BUDGET;
    }

    if (payloadOrder?.orderDetails?.paymentOptionExternalCode === 'LINK' && allowPaymentLink) {
      orderTypeSend = ORDER_TYPE_SEND.PAYMENT_LINK;
    } else if (configuration?.useLoadControl && payloadOrder?.load && payloadOrder?.load?.typeId !== '0') {
      orderTypeSend = ORDER_TYPE_SEND.LOAD;
    }

    // const hasItemsBelowMininumPrice = payloadOrder?.orderItems.filter(x => x?.isBelowMinimumPrice);

    const hasItemsDiscountApprovalRequired = payloadOrder?.orderItems?.filter(x => x?.isDiscountApprovalRequired);

    if (configuration?.useDiscountControl && hasItemsDiscountApprovalRequired.length > 0) {
      orderTypeSend = ORDER_TYPE_SEND.WAITING_APPROVAL;
    }

    if (configuration?.useLoadControl && order?.load && order?.load?.loadType?._id !== '0') {
      orderTypeSend = ORDER_TYPE_SEND.LOAD;
    }

    if (
      configuration?.allowVinculatedBonification &&
      payloadOrder?.orderDetails?.orderType === ORDER_TYPE.BONIFICATION &&
      !payloadOrder?.budget?.isBudget
    ) {
      budgetEditSend = false;
      orderTypeSend = ORDER_TYPE_SEND.ORDER;
    }

    if (
      configuration?.allowVinculatedBonification &&
      configuration?.allowBonificationOnPendingOrders &&
      payloadOrder?.orderDetails?.bonificationApprovalSwitch &&
      !payloadOrder?.budget?.isBudget
    ) {
      orderTypeSend = ORDER_TYPE_SEND.WAITING_APPROVAL;
    }

    if (
      configuration?.useMarginControlByColor &&
      order?.totalsMargin &&
      (user?.useMarginControlByColor === undefined ? true : user?.useMarginControlByColor)
    ) {
      const marginControlColors = order?.marginControlColors;

      if (marginControlColors?.length > 0) {
        const totalMarginPercentage =
          order?.totalsMargin?.totalMarginPercentage ?? order?.totalsMargin?.marginPercentage ?? 0;

        const marginCalcByColor = configuration?.marginControlByColor?.filter(
          margin =>
            totalMarginPercentage >= margin?.initialValue &&
            totalMarginPercentage <= margin?.finalValue &&
            (margin?.priceTableCode?.length > 0
              ? margin?.priceTableCode?.filter(margin => margin === order?.orderDetails?.priceTableCode)
              : true)
        );

        if (marginCalcByColor[0]?.action === 'deny') {
          notification.warning(formatMessage('orderModalMarginControlByColorDeniedMessage'));
          yield put(orderActions.setShowModalOrder(false));

          return;
        }

        if (marginCalcByColor[0]?.action === 'waitingApproval') {
          orderTypeSend = ORDER_TYPE_SEND.WAITING_APPROVAL;
        }
      }
    }

    yield put(
      orderActions.updateModal({
        type: 'loading',
        data: {
          title: order.budget?.isBudget
            ? formatMessage('orderModalSavingBudgetMessage')
            : formatMessage('orderModalSavingOrderMessage'),
          message: formatMessage('orderModalLoadingMessage')
        }
      })
    );

    try {
      let client;

      if (
        order?.sendOrderInfo?.isSend ||
        (payloadOrder?.orderDetails?.paymentOptionExternalCode === 'LINK' && allowPaymentLink)
      ) {
        try {
          client = yield call(api.put, `/client/${order?.client?.externalCode}`, {
            email: order?.sendOrderInfo?.email ?? '',
            cellphoneNumber: order?.sendOrderInfo?.cellphoneNumber ?? ''
          });
        } catch (error: any) {
          yield put(
            orderActions.updateModal({
              type: 'error',
              data: {
                title: formatMessage('orderModalCellphoneErrorMessage'),
                message: normalizeError(error)
              }
            })
          );
          return;
        }
      }

      let paymentPlanNumberOfInstallments = order?.orderDetails?.paymentPlan;

      if (order?.client?.usePaymentPlansGroup) {
        paymentPlanNumberOfInstallments =
          order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.numberOfInstallments >
          order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.numberOfInstallments
            ? order?.orderDetails?.paymentPlanGroup.paymentPlan1
            : order?.orderDetails?.paymentPlanGroup.paymentPlan2;
      }

      let data = null;

      if (payloadOrder?.orderDetails?.paymentOptionExternalCode === 'LINK' && allowPaymentLink) {
        const { data: orderData } = yield call(orderService.sendOrderPayment, orderTypeSend, {
          realmID,
          order: payloadOrder,
          sendOrderInPDF: order?.sendOrderInfo?.isSend,
          cellphoneNumber: order?.sendOrderInfo?.cellphoneNumber,
          email: order?.sendOrderInfo?.email,
          numberOfInstallments: paymentPlanNumberOfInstallments?.numberOfInstallments
        });

        data = orderData;
      } else {
        const { data: orderData } = yield call(orderService.sendOrder, orderTypeSend, budgetEditSend, {
          realmID,
          order: payloadOrder,
          sendOrderInPDF: order?.sendOrderInfo?.isSend,
          orderId: isOrderToEdit
        });

        data = orderData;
      }

      const orderIsArray = Array.isArray(data);

      const orderNumber = orderIsArray ? data[0]?.orderNumber : data?.orderNumber;
      const link = orderIsArray ? data[0]?.shortUrl ?? '' : data?.shortUrl ?? '';
      const orderID = orderIsArray
        ? data[0]?._id
        : payloadOrder?.orderDetails?.paymentOptionExternalCode === 'LINK'
        ? data?.order?._id
        : data?._id;

      localStorage?.removeItem(ORDER_RECOVERY_KEY);

      if (order?.sendOrderInfo?.isSend) {
        try {
          yield call(orderService.orderPdf, orderID, order?.sendOrderInfo?.isSend ?? true, client?.data);
        } catch (error: any) {
          const parsedError = {
            error: JSON.stringify(error),
            order: payloadOrder,
            createdAt: new Intl.DateTimeFormat('pt-BR', {
              timeZone: 'America/Sao_Paulo',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            }).format(new Date())
          };

          localStorage?.setItem(ERROR_ON_SEND_ORDER?.PDF, JSON.stringify(parsedError));

          yield put(
            orderActions.updateModal({
              type: 'finished',
              data: {
                title: formatMessage('orderModalErrorSendPdfMessage'),
                orderNumber,
                orderID,
                message: normalizeError(error)
              }
            })
          );
          return;
        }
      }

      if (order?.orderDetails?.paymentOption?.type === 'PIX' && configuration?.allowPaymentPix) {
        const { data } = yield call(orderService.generatePix, orderID);

        const parsedData = data as PixPaymentProps;

        return yield put(
          orderActions.updateModal({
            type: 'finished',
            data: {
              title: 'PIX',
              orderNumber,
              orderID,
              message: 'PIX',
              pix: parsedData
            }
          })
        );
      }
      // CASO PRECISE VOLTAR O MODAL DE PEDIDO BONIFICADO, ESTA É A LÓGICA:
      // if (
      //   configuration?.allowVinculatedBonification &&
      //   configuration?.allowBonificationOnPendingOrders &&
      //   payloadOrder?.orderDetails?.orderType !== ORDER_TYPE.BONIFICATION &&
      //   (!payloadOrder?.budget?.isBudget || payloadOrder?.budget?.effectedSale)
      // ) {
      //   const { isConfirmed } = yield call(
      //     notification.confirm,
      //     formatMessage('orderModalConfirmBonificationTitle'),
      //     formatMessage('orderModalConfirmBonificationMessage'),
      //     formatMessage('commonLabelYes'),
      //     formatMessage('commonLabelNo')
      //   );

      //   if (data && isConfirmed) {
      //     notification.success(formatMessage('orderModalOrderSend'));
      //     yield call(history.push, `/order/${orderID}/bonification/step1?parentInternalCode=${orderNumber}`);
      //   }
      // }

      // if (
      //   configuration?.allowVinculatedBonification &&
      //   !configuration?.allowBonificationOnPendingOrders &&
      //   payloadOrder?.orderDetails?.orderType !== ORDER_TYPE.BONIFICATION &&
      //   (!payloadOrder?.budget?.isBudget || payloadOrder?.budget?.effectedSale)
      // ) {
      //   const { isConfirmed } = yield call(
      //     notification.confirm,
      //     formatMessage('orderModalConfirmBonificationTitle'),
      //     formatMessage('orderModalConfirmBonificationMessage'),
      //     formatMessage('commonLabelYes'),
      //     formatMessage('commonLabelNo')
      //   );

      //   if (data && isConfirmed) {
      //     notification.success(formatMessage('orderModalOrderSend'));
      //     yield call(history.push, `/order/${orderID}/bonification/step1?orderVinculatedExternalNumber=${orderNumber}`);
      //   }
      // }

      if (orderTypeSend === ORDER_TYPE_SEND.PAYMENT_LINK) {
        yield put(
          orderActions.updateModal({
            type: 'finished',
            data: {
              title: formatMessage('orderModalOrderSafeMessage'),
              orderNumber,
              orderID,
              message: order?.sendOrderInfo?.isSend
                ? formatMessage('orderModalSentOrderLinkMessage')
                : formatMessage('orderModalSentLinkMessage'),
              link
            }
          })
        );
      } else if (orderTypeSend === ORDER_TYPE_SEND.ORDER || orderTypeSend === ORDER_TYPE_SEND.BUDGET) {
        const isBasket = order?.orderType?.type === 'basket';
        const successMessage = order?.sendOrderInfo?.isSend
          ? formatMessage('orderModalSuccessMessage', {
              order: order.budget?.isBudget
                ? getOrderTypeSend(ORDER_TYPE_SEND.BUDGET)
                : getOrderTypeSend(ORDER_TYPE_SEND.ORDER)
            })
          : '';
        const basketMessage = isBasket ? formatMessage('orderModalOrderSafeBasketMessage') : '';

        yield put(
          orderActions.updateModal({
            type: 'finished',
            isBudget: order.budget?.isBudget,
            data: {
              title: order.budget?.isBudget
                ? formatMessage('orderModalBudgetSafeMessage')
                : formatMessage('orderModalOrderSafeMessage'),
              orderNumber,
              orderID,
              message: `${successMessage} \n\n ${basketMessage}`.trim()
            },
            isBasket
          })
        );
      } else if (orderTypeSend === ORDER_TYPE_SEND.LOAD) {
        yield put(
          orderActions.updateModal({
            type: 'finished',
            data: {
              title: formatMessage('orderModalWaitingLoadMessage'),
              orderNumber,
              orderID,
              message: order?.sendOrderInfo?.isSend ? formatMessage('orderModalSentEmailMessage') : ''
            }
          })
        );
      } else if (orderTypeSend === ORDER_TYPE_SEND.WAITING_APPROVAL) {
        yield put(
          orderActions.updateModal({
            type: 'finished',
            data: {
              title: formatMessage('orderModalWaitingApprovalMessage'),
              orderNumber,
              orderID,
              message: order?.sendOrderInfo?.isSend ? formatMessage('orderModalSentEmailMessage') : '',
              isChild: !!isChild
            }
          })
        );
      }
    } catch (error: any) {
      const parsedError = {
        error: JSON.stringify(error),
        order: payloadOrder,
        createdAt: new Intl.DateTimeFormat('pt-BR', {
          timeZone: 'America/Sao_Paulo',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(new Date())
      };

      localStorage?.setItem(ERROR_ON_SEND_ORDER?.ORDER, JSON.stringify(parsedError));

      yield put(
        orderActions.updateModal({
          type: 'error',
          data: {
            title: formatMessage('orderModalErrorMessage'),
            message: normalizeError(error)
          }
        })
      );
    }
  } catch (error) {
    yield put(orderActions.setShowModalOrder(false));
  }
}

function* validatePasswordModalReason({ payload }: any) {
  const { data: reason } = yield select((state: AppStateType) => state.order.form.showModalBlockReason);
  const user: UserInterface = yield select((state: AppStateType) => state.auth.user);

  const { password } = payload;

  if (
    reason?.token?.active &&
    (reason?.token?.userExternalCode?.length === 0 ||
      reason?.token?.userExternalCode?.find(item => item === user?.externalCode))
  ) {
    if (reason?.token?.password !== password) {
      notification.warning(formatMessage('orderModalPasswordNotMatchMessage'));
      return;
    } else {
      if (compareAsc(new Date(), new Date(reason?.token?.passwordExpirationDate)) === 1) {
        notification.warning(formatMessage('orderModalPasswordExpiryPasswordMessage'));
        return;
      }

      notification.success(formatMessage('orderModalSuccessPasswordMessage'));

      yield put(
        orderActions.setShowModalReason({
          data: null,
          show: false
        })
      );
    }
  }
}

function* gerateOrder(order: OrderCreateInterface, realmID: string, orderVinculatedExternalNumber: number = undefined) {
  const user: UserInterface = yield select((state: AppStateType) => state.auth.user);
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

  const usePaymentPlansGroup = order?.client?.usePaymentPlansGroup ?? false;

  let paymentPlanNumberOfInstallments = order?.orderDetails?.paymentPlan;

  if (usePaymentPlansGroup) {
    paymentPlanNumberOfInstallments =
      order?.orderDetails?.paymentPlanGroup.paymentPlan1?.numberOfInstallments >
      order?.orderDetails?.paymentPlanGroup.paymentPlan2?.numberOfInstallments
        ? order?.orderDetails?.paymentPlanGroup.paymentPlan1
        : order?.orderDetails?.paymentPlanGroup.paymentPlan2;
  }

  const paymentLink =
    order?.orderDetails?.paymentOption?.externalCode === 'LINK' && configuration?.allowPaymentLink
      ? {
          email: order?.sendOrderInfo?.email,
          cellphoneNumber: order?.sendOrderInfo?.cellphoneNumber,
          numberOfInstallments: paymentPlanNumberOfInstallments
        }
      : null;

  const today = new Date();
  const expirationBudget = configuration?.expirationBudget / 24;
  const hasItemsDiscountApprovalRequired = order?.orderItems?.filter(x => x.isDiscountApprovalRequired);
  const bonificationApproval = configuration?.allowBonificationOnPendingOrders ? order?.parentInternalCode : false;

  let orderStatus = order?.orderStatus ?? ORDER_STATUS.PROCESSING;

  if (order?.budget?.isBudget) {
    orderStatus = ORDER_STATUS.BUDGET;
  } else {
    orderStatus = ORDER_STATUS.PROCESSING;
  }

  if (configuration?.useLoadControl && order?.load && order?.load?.loadType?._id !== '0') {
    orderStatus = ORDER_STATUS.WAITING_LOAD;
  }

  if (configuration?.useDiscountControl && hasItemsDiscountApprovalRequired?.length > 0) {
    orderStatus = ORDER_STATUS.WAITING_APPROVAL;
  }

  if (bonificationApproval && configuration?.allowBonificationOnPendingOrders) {
    orderStatus = ORDER_STATUS.WAITING_APPROVAL;
  }

  const payload: OrderPostInterface = {
    realmID,
    ...(order?.complementary
      ? {
          originalOrderNumber: order?.originalOrderNumber ?? null,
          originalOrderExternalNumber: order?.originalOrderExternalNumber ?? null,
          originalOrderItems: order?.originalOrderItems ?? []
        }
      : {}),
    clientExternalCode: order.client?.externalCode,
    orderNumber: order?.orderNumber ?? null,
    userExternalCode: user.externalCode,
    senderExternalCode: user?.userRole === USER_ROLE.TELEMARKETING ? user.externalCode ?? null : null,
    ...(order.originalBudget?.isBudget &&
      order.fromBudget && {
        _id: order._id
      }),
    budget: {
      ...order.budget,
      isBudget: order?.budget?.isBudget || order?.fromBudget ? true : false,
      expirationDate: new Date(today.setDate(today.getDate() + expirationBudget)),
      effectedSale: order.fromBudget && !order.budget?.isBudget
    },
    orderVinculatedExternalNumber:
      configuration?.allowVinculatedBonification && orderVinculatedExternalNumber
        ? orderVinculatedExternalNumber
        : Number(order?.orderVinculatedExternalNumber)
        ? Number(order?.orderVinculatedExternalNumber)
        : null,
    orderStatus,
    internalCode: String(new Date().getTime()),
    parentInternalCode:
      configuration?.allowVinculatedBonification &&
      configuration?.allowBonificationOnPendingOrders &&
      order?.parentInternalCode
        ? order?.parentInternalCode
        : null,
    orderDetails: {
      totalPrice: toDecimals(
        order?.orderItems?.reduce((acc, item) => acc + item?.soldPrice, 0),
        configuration.decimals ?? 2
      ),
      reopen: false,
      imported: false,
      creationDate: order?.orderDetails?.creationDate,
      finishDate: new Date(),
      orderType: order.orderDetails.orderType,
      orderTypeExternalCode: order.orderType.externalCode,
      deliveryExternalCode: order.orderDetails.deliveryExternalCode,
      orderInterest: order.client?.usePaymentPlansGroup
        ? order.orderDetails.paymentPlanGroup?.paymentPlan1?.financialPercentageRate
        : order.orderDetails.paymentPlan?.financialPercentageRate,
      shippingFee: order?.shipping?.cost || 0,
      shippingType: order.orderDetails.shippingType,
      priceTableCode: order.orderDetails.priceTableCode,
      subsidiaryExternalCode: order.orderDetails.subsidiary?.externalCode,
      subsidiaryStock: order.orderDetails.subsidiaryStock?.externalCode,
      variableInstallment: order?.orderDetails?.variableInstallment,
      //Payment
      paymentOptionExternalCode: order.orderDetails.paymentOption?.externalCode,
      usePaymentPlansGroup,
      ...(usePaymentPlansGroup
        ? {
            paymentPlanGroup1ExternalCode: order.orderDetails.paymentPlanGroup?.paymentPlan1?.externalCode,
            paymentPlanGroup2ExternalCode: order.orderDetails.paymentPlanGroup?.paymentPlan2?.externalCode,
            paymentPlanExternalCode: order.orderDetails.paymentPlanGroup?.paymentPlan1?.externalCode
          }
        : {
            paymentPlanExternalCode: order.orderDetails.paymentPlan?.externalCode
          }),
      estimatedDeliveryDate: order?.estimatedDeliveryDate?.date,
      // order?.estimatedDeliveryDate?.hasChanged
      //   ? order?.estimatedDeliveryDate?.date
      //   : order?.estimatedDeliveryDate?.default,
      ...(!!order?.orderDetails?.orderObservation?.length && {
        orderObservation: order?.orderDetails?.orderObservation
      }),
      ...(!!order?.orderDetails?.deliveryObservation?.length && {
        deliveryObservation: order?.orderDetails?.deliveryObservation
      }),
      ...(!!order?.orderDetails?.clientOrderNumber?.length && {
        clientOrderNumber: order?.orderDetails?.clientOrderNumber
      }),
      deliveryAddressCode: order?.orderDetails?.deliveryAddressCode?.addressCode ?? null,
      billingDate: order?.orderDetails?.billingDate ? new Date(order?.orderDetails?.billingDate).toISOString() : null,
      bonificationApprovalSwitch: order?.parentInternalCode ? true : order?.orderDetails?.bonificationApprovalSwitch,
      ...paymentLink,
      paymentOptionType:
        order?.orderDetails?.paymentOption?.type === PAYMENT_OPTION_TYPE.PIX ? PAYMENT_OPTION_TYPE.PIX : undefined,
      promoterExternalCode: order?.client?.promoterExternalCode ?? null,
      providerDeliveryExternalCode: order?.orderDetails?.providerDeliveryExternalCode?.externalCode ?? null,
      origin: 'web',
      ...(user?.userRole === USER_ROLE.TELEMARKETING && {
        senderName: user?.name ?? null,
        userName: order?.orderDetails?.vendor?.name ?? null
      }),
      originalUser: user?.originalUserName ?? null,
      originalUserEmail: user?.originalUserEmail ?? null,
      bonificationTypeValue: order?.orderDetails?.bonificationTypeValue,
      otherExpenses: Number(order?.orderDetails?.otherExpenses) || 0,
      clientGovernmentalCode: order?.orderDetails?.clientGovernmentalCode ?? null
    },
    load:
      configuration?.useLoadControl && order?.load?.loadType && order?.load?.loadType?._id !== '0'
        ? {
            typeId: order?.load?.loadType?._id,
            typeCode: order?.load?.loadType?.code
          }
        : null,
    orderItems: order.orderItems.map(({ promotions, ...item }, index) => {
      const { observation, ...productWithoutObservation } = item || {};

      return {
        ...item,
        ...productWithoutObservation,
        requiredApproval: item.isDiscountApprovalRequired,
        itemSequence: index + 1,
        discount: item.discountPercentage > 0 || item.discountPercentage < 0,
        discountPercentage: item.discountPercentage ?? 0,
        barCode: item?.packing?.barCode ?? item?.barCode ?? '0',
        tributacao: item?.tributacao,
        package: item?.packing?.barCode ? item?.packing : null,
        productExternalCode: item.externalCode,
        userExternalCode: order?.orderDetails?.vendor?.externalCode ?? user.externalCode,
        tabulatedPrice: item?.baseValue,
        isPromotionKit: item?.promotion?.isPromotionKit,
        promotion: item?.promotion
          ? {
              promotionMedCode: item.promotion?.codpromocaomed,
              promotionExternalCode: item.promotion?.externalCode,
              priceDifference: item.promotion?.priceDifference,
              promotionName: item.promotion?.promotionName,
              promotion: item?.promotion,
              promotionCode: item?.promotion?.code,
              promotionGroupCode: item?.promotion?.promotionGroupCode,
              isPromotionGroup: !!item?.promotion?.promotionGroupCode,
              isPromotionKit: item?.promotion?.isPromotionKit,
              totalSoldPriceKit: item?.promotion?.totalSoldPriceKit ?? 0,
              promotionKitQuantity: item?.promotion?.promotionKitQuantity ?? 0,
              promotionOriginalTotalPrice: item?.promotion?.promotionOriginalTotalPrice ?? 0,
              promotionalPrice: item?.promotion?.promotionalPrice ?? 0,
              batchCode: item?.promotion.batchCode
            }
          : undefined,
        soldPrice: toDecimals(item.soldPrice, configuration.decimals ?? 2),
        totalPrice: toDecimals(item.totalPrice, configuration.decimals ?? 2),
        soldPriceWithPacking: toDecimals(item.soldPrice, configuration.decimals ?? 2),
        totalPriceWithPacking: toDecimals(item?.totalPriceWithPacking || item.totalPrice, configuration.decimals ?? 2),
        priorityPolice: item?.priorityPolice ?? false,
        subsidiaryStockExternalCode: item?.subsidiaryStock?.externalCode ?? null,
        unitaryValue: toDecimals(item?.unitaryValue, configuration.decimals ?? 2)
      } as OrderItemPostInterface;
    })
  };
  return payload;
}

export function* getPromotionKit({ payload, show }: any) {
  const order: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);
  const { onlyWithStock, onlyWithPromotion, clientMix }: ProductStateListInterface = yield select(
    (state: AppStateType) => state.product.list
  );
  const { orderDetails, client }: OrderCreateInterface = yield select(
    (state: AppStateType) => state?.order?.form?.data
  );
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
  const { data: promotionKit } = yield select((state: AppStateType) => state.order.form.showModalPromotionKit);

  if (!show) {
    yield put(
      orderActions.setShowModalPromotionKit({
        show: false,
        data: null
      })
    );

    return;
  }

  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    if (cancel !== undefined) cancel();
    const cancelToken = new CancelToken(function executor(c) {
      cancel = c;
    });

    yield put(orderActions.setLoadingData(true));

    const filters = {
      clientExternalCode: order?.client?.externalCode ?? null,
      paymentPlanExternalCode: order?.client?.usePaymentPlansGroup
        ? [
            order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
            order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
          ].join(',')
        : order?.orderDetails?.paymentPlan?.externalCode,
      subsidiaryExternalCode: order?.orderDetails?.subsidiary?.externalCode ?? null,
      governmentalCode: order?.client?.governmentalCode ?? null,
      applyTax: order?.client?.applyTax ? order?.client?.applyTax : null,
      applyTax2: configuration?.taxation ? order?.client?.applyTax2 : null,
      applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : null,
      finalConsumer: order?.client?.finalConsumer ?? null,
      usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
      clientActivityExternalCode: order?.client?.activityExternalCode ?? null,
      subsidiaryStockExternalCode: order?.orderDetails?.subsidiaryStock?.externalCode ?? null,
      priceTableCode: order?.orderDetails?.priceTableCode ?? null,
      onlyWithStock,
      onlyWithPromotion,
      clientMix,
      loadType: order?.load?.loadType?.code ?? null,
      shippingType: order?.orderDetails?.shippingType ?? null,
      clientGroupExternalCode: order?.client?.clientGroupExternalCode ?? null,
      productPaymentPlansGroup: order?.client?.paymentPlanGroup1ExternalCode
        ? order?.client?.paymentPlanGroup1ExternalCode
        : order?.client?.paymentPlanGroup2ExternalCode
        ? order?.client?.paymentPlanGroup2ExternalCode
        : null,
      useMedPromotions: configuration?.useMedPromotions ?? null
    };

    const productsKitPayload = payload?.productExternalCodes?.map(item => item?.productExternalCode).join(',');

    const { data: products } = yield call(
      productService.oldGetQuery,
      {
        fieldsToShow: 'externalCode,name,pictures,paymentPlansGroup,brand,stocks,prices,tributacao',
        queryString: '',
        productExternalCodes: productsKitPayload,
        filters,
        skip: 0,
        limit: 50
      } as any,
      {
        cancelToken
      }
    );

    if (!products?.length) {
      return notification.warning(formatMessage('Produto(s) não encontrado(s)'));
    }

    const { totalSoldPriceKit, productsInKit, promotionKitQuantity } = getTotalsKit(products, payload);

    let parsedProducts: any = [];
    const priceTableCode = orderDetails?.priceTableCode ?? null;

    for (const item of productsInKit) {
      const _id = item?._id ? item?._id : item?._id;

      const { isRecommendation, isSponsored, tabulatedPrice, stock, price, availableQuantity, externalCode } = item;

      const productBaseValue = item?.baseValue;

      let baseValue = productBaseValue;
      let discount = false;
      let discountPercentage = payload?.discountPercentage ?? 0;
      let soldQuantity = item?.quantityOfProductInKit * promotionKitQuantity;

      const { data: product } = yield call(productService.getById, {
        _id,
        subsidiaryExternalCode: orderDetails.subsidiary?.externalCode,
        productExternalCode: externalCode,
        usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
        applyTax: order?.client?.applyTax ? order?.client?.applyTax : undefined,
        applyTax2: configuration?.taxation ? order?.client?.applyTax2 : undefined,
        applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : undefined,
        taxation: configuration?.taxation,
        discountPercentage,
        decimals: configuration?.decimals ?? 2,
        subsidiaryStockExternalCode: orderDetails?.subsidiaryStock?.externalCode ?? null,
        comparesMinimumPriceWithST: configuration?.comparesMinimumPriceWithST,
        paymentPlanExternalCodes: order?.client?.usePaymentPlansGroup
          ? [
              order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
              order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
            ].join(',')
          : order?.orderDetails?.paymentPlan?.externalCode,
        priceTableCode,
        clientDiscountPercentage: client?.discountPercentage ?? 0,
        governmentalCode: client?.governmentalCode,
        finalConsumer: client?.finalConsumer ?? null,
        state: client?.commercialInfo?.state
      });

      let availableQuantityProduct = availableQuantity ?? 0;

      const tributacao = item?.tributacao;
      const packing = product?.packing ? product?.packing.find(pack => pack?.packingUnit === 'UN') : null;

      yield call(getRestrictionsRequest, {
        closeModal: true
      });

      const { data: quota } = yield call(productService.getProductQuota, {
        clientExternalCode: client?.externalCode ?? null,
        productExternalCode: product?.externalCode ?? null,
        subsidiaryExternalCode: orderDetails?.subsidiary?.externalCode ?? null
      });

      let soldPrice = item?.soldPrice ? item?.soldPrice : product?.soldPrice ?? product?.baseValue;

      const totalPrice = toDecimals(soldPrice * soldQuantity, configuration.decimals ?? 2);

      const maximumPrice = toDecimals(
        baseValue + (baseValue * configuration?.maximumSalePercentage) / 100,
        configuration?.decimals ?? 2
      );

      let maxDiscountPercentage = price?.maxDiscountPercentage ?? 0;

      const minimumPrice = toDecimals(
        baseValue - (baseValue * maxDiscountPercentage) / 100,
        configuration?.decimals ?? 2
      );

      const paymentPlanSelected = client?.usePaymentPlansGroup
        ? orderDetails?.paymentPlanGroup?.[`paymentPlan${product?.paymentPlansGroup}`]
        : orderDetails?.paymentPlan;

      const percentualRate = paymentPlanSelected?.financialPercentageRate ?? 0;
      const financialCost = item?.price?.includeFinancialPercentage
        ? toDecimals(
            item?.price?.financialCost + (item?.price?.financialCost * percentualRate) / 100,
            configuration?.decimals ?? 2
          )
        : stock?.financialCost;

      const margin = yield getMargin(soldPrice, financialCost, configuration.decimals);

      const totalUnity =
        product?.typeOperation && product?.unitQuantity !== product?.unitQuantityBox
          ? calculateTypeOperation(
              Number(productBaseValue),
              product.typeOperation === 'M' ? 'M' : 'D',
              product.unitQuantityBox,
              configuration
            )
          : 0;

      const totalPriceProductKit = item?.fixedPrice ? item?.fixedPrice * soldQuantity : soldPrice * soldQuantity;

      parsedProducts = [
        ...parsedProducts,
        {
          ...item,
          promotion: {
            ...promotionKit,
            totalSoldPriceKit,
            promotionKitQuantity
          },
          ...product,
          quota,
          price,
          totalUnity,
          stock: {
            ...stock,
            financialCost
          },
          margin,
          tributacao,
          packingInfos: {
            unit: product.unit,
            type: product.unit,
            availableQuantity,
            barCode: packing?.barCode ?? product?.barCode ?? '0',
            packingPrice: baseValue,
            basePackingPrice: baseValue,
            unitaryValue: productBaseValue
          },
          packing,
          barCode: packing?.barCode ?? product?.barCode ?? '0',
          soldQuantity,
          soldPrice,
          totalPriceProductKit,
          totalPrice,
          boxPrice: product?.typeOperation ? 0 : null,
          tabulatedPrice,
          baseValue,
          financialCost,
          percentualRate,
          discount,
          minimumPrice,
          maximumPrice,
          availableQuantity: availableQuantityProduct,
          discountPercentage,
          maxDiscountPercentage,
          bonificated: false,
          priorityPolice: false,
          isRecommendation: isRecommendation ? true : false,
          isSponsored: isSponsored ? true : false,
          isPromotion: false,
          isBelowMinimumPrice: false,
          isDiscountApprovalRequired: false,
          isPromotionGroup: false
        }
      ];
    }

    const parsedPayload = {
      ...payload,
      productsInKit: parsedProducts
    };

    yield put(
      orderActions.setShowModalPromotionKit({
        show: true,
        data: {
          ...parsedPayload,
          productsInKit: parsedProducts,
          totalSoldPriceKit,
          promotionKitQuantity
        }
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(orderActions.setLoadingData(false));
    toast.close();
  }
}

export function* setPromotionKitAddCart({ payload }: any) {
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
  const { data: promotionKit } = yield select((state: AppStateType) => state.order.form.showModalPromotionKit);
  const { orderItems } = yield select((state: AppStateType) => state.order.form.data);

  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  const { products, totalSoldPriceKit, promotionKitQuantity, promotionOriginalTotalPrice } = payload;

  const kitAlreadyInCart = products?.filter(product =>
    orderItems?.find(
      item =>
        item?.externalCode === product?.externalCode &&
        item?.promotion?.isPromotionKit === true &&
        (item?.promotion?.codpromocaomed
          ? item?.promotion?.codpromocaomed === promotionKit?.codpromocaomed
          : item?.promotion?.externalCode === promotionKit?.externalCode)
    )
  );

  if (kitAlreadyInCart?.length > 0) {
    return notification.warning(
      formatMessage('orderWarningPromotionKitAlreadyAdded', {
        promotionKitName: promotionKit?.promotionName,
        promotionKitExternalCode: promotionKit?.codpromocaomed
          ? promotionKit?.codpromocaomed
          : promotionKit?.externalCode
      })
    );
  }

  const newOrderItems = products?.map(product => ({
    ...product,
    quantity: promotionKitQuantity * product?.quantityOfProductInKit,
    soldQuantity: promotionKitQuantity * product?.quantityOfProductInKit
  }));

  let productsForCart = [];

  for (const item of newOrderItems) {
    const baseValue = item?.baseValue;
    let maxDiscountPercentage = item?.price?.maxDiscountPercentage ?? 0;

    const maximumPrice = toDecimals(
      baseValue + (baseValue * configuration?.maximumSalePercentage) / 100,
      configuration?.decimals ?? 2
    );

    const minimumPrice = toDecimals(
      baseValue - (baseValue * maxDiscountPercentage) / 100,
      configuration?.decimals ?? 2
    );

    productsForCart = [
      ...productsForCart,
      {
        ...item,
        availableQuantity: item?.availableQuantity,
        maxDiscountPercentage,
        minimumPrice,
        maximumPrice,
        totalPrice: toDecimals(
          promotionKitQuantity * item?.quantityOfProductInKit * item?.soldPrice,
          configuration.decimals ?? 2
        ),
        promotion: {
          ...promotionKit,
          promotionKitQuantity,
          totalSoldPriceKit,
          promotionOriginalTotalPrice
        }
      }
    ];
  }

  try {
    const addProductsInCart = productsForCart.map(item => call(addProductCart, { orderItem: item }));

    yield put(
      orderActions.setShowModalPromotionKit({
        show: false,
        data: null
      })
    );

    toast.close();

    yield all(addProductsInCart);
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* updatePromotionKit({ payload }: any) {
  try {
    const { orderItems } = yield select((state: AppStateType) => state.order.form.data);
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

    const { quantity, promotionKit } = payload;

    if (quantity === 0) {
      return notification.warning(formatMessage('orderWarningPromotionEmpty'));
    }

    const newOrderItems = orderItems?.reduce((acc, product) => {
      if (
        product?.promotion?.isPromotionKit &&
        (product?.promotion?.externalCode === promotionKit.externalCode ||
          product?.promotion?.aggregatorExternalCode === promotionKit.aggregatorExternalCode)
      ) {
        const updatedProduct = {
          ...product,
          quantity: quantity * product?.quantityOfProductInKit,
          soldQuantity: quantity * product?.quantityOfProductInKit,

          promotion: {
            ...product?.promotion,
            promotionKitQuantity: quantity,
            totalSoldPriceKit: quantity * product?.promotion?.promotionOriginalTotalPrice
          }
        };
        acc.push(updatedProduct);
      } else {
        acc.push(product);
      }
      return acc;
    }, []);

    let productsForCart = [];

    for (const item of newOrderItems) {
      if (
        item?.promotion?.isPromotionKit &&
        (item?.promotion?.externalCode === promotionKit.externalCode ||
          item?.promotion?.aggregatorExternalCode === promotionKit.aggregatorExternalCode)
      ) {
        const baseValue = item?.baseValue;
        let maxDiscountPercentage = item?.price?.maxDiscountPercentage ?? 0;

        const maximumPrice = toDecimals(
          baseValue + (baseValue * configuration?.maximumSalePercentage) / 100,
          configuration?.decimals ?? 2
        );

        const minimumPrice = toDecimals(
          baseValue - (baseValue * maxDiscountPercentage) / 100,
          configuration?.decimals ?? 2
        );
        const quantityOfProcutInKit = item?.quantityOfProductInKit ?? item?.product?.quantityOfProductInKit;
        productsForCart = [
          ...productsForCart,
          {
            ...item,
            availableQuantity: item?.availableQuantity,
            maxDiscountPercentage,
            minimumPrice,
            maximumPrice,
            totalPrice: item?.quantityOfProductInKit
              ? toDecimals(quantity * item?.quantityOfProductInKit * baseValue, configuration.decimals ?? 2)
              : toDecimals(item?.quantityOfProductInKit, configuration.decimals ?? 2)
              ? toDecimals(quantity * item?.quantityOfProductInKit * baseValue, configuration.decimals ?? 2)
              : 0,
            promotion: {
              ...item?.promotion,
              promotionKitQuantity: quantity,
              totalSoldPriceKit: quantity * promotionKit?.promotion?.promotionOriginalTotalPrice
            }
          }
        ];
      }
    }

    try {
      const updateProductsInCart = productsForCart.map(item => call(updateProduct, { payload: item }));
      yield all(updateProductsInCart);
    } catch (error) {
      yield put({ type: ISA_ERROR, error });
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getPromotionKitListModal({ payload }: any) {
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
  const order: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);
  const {
    skip,
    limit,
    show,
    data: promotionKitsReducer
  } = yield select((state: AppStateType) => state.order.form.showModalPromotionKitList);

  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    yield put(orderActions.setLoadingData(true));
    const filters = {
      clientExternalCode: order?.client?.externalCode ?? null,
      paymentPlanExternalCode: order?.client?.usePaymentPlansGroup
        ? [
            order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
            order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
          ].join(',')
        : order?.orderDetails?.paymentPlan?.externalCode,
      subsidiaryExternalCode: order?.orderDetails?.subsidiary?.externalCode ?? null,
      governmentalCode: order?.client?.governmentalCode ?? null,
      applyTax: order?.client?.applyTax ? order?.client?.applyTax : null,
      applyTax2: configuration?.taxation ? order?.client?.applyTax2 : null,
      applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : null,
      finalConsumer: order?.client?.finalConsumer ?? null,
      usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
      clientActivityExternalCode: order?.client?.activityExternalCode ?? null,
      subsidiaryStockExternalCode: order?.orderDetails?.subsidiaryStock?.externalCode ?? null,
      priceTableCode: order?.orderDetails?.priceTableCode ?? null,
      loadType: order?.load?.loadType?.code ?? null,
      shippingType: order?.orderDetails?.shippingType ?? null,
      clientGroupExternalCode: order?.client?.clientGroupExternalCode ?? null,
      productPaymentPlansGroup: order?.client?.paymentPlanGroup1ExternalCode
        ? order?.client?.paymentPlanGroup1ExternalCode
        : order?.client?.paymentPlanGroup2ExternalCode
        ? order?.client?.paymentPlanGroup2ExternalCode
        : null,
      queryString: payload?.queryString ?? '',
      orderBy: 'promotionName',
      orderAsc: true,
      useMedPromotions: configuration?.useMedPromotions ?? null
    };

    const pagination = {
      skip: payload?.skip === 0 ? 0 : payload?.skip ? payload?.skip : skip,
      limit: payload?.limit ? payload?.limit : limit
    };

    const { data: promotionKits } = yield call(productService.getPromotionKitList, {
      ...filters,
      ...pagination
    });

    const { data: promotionKitCount } = yield call(productService.getPromotionKitListCount, {
      ...filters
    });

    if (
      (payload?.queryString && payload?.skip === 0) ||
      (payload?.queryString === '' && payload?.skip === 0) ||
      (payload?.queryString === undefined && payload?.skip === 0)
    ) {
      yield put(
        orderActions.setShowModalPromotionKitListSuccess({
          data: promotionKits,
          skip: pagination?.skip,
          limit: pagination?.limit,
          hasMore: promotionKits?.length > 0 && promotionKits?.length >= 10 ? true : false,
          show,
          count: promotionKitCount?.count
        })
      );
      return;
    }

    yield put(
      orderActions.setShowModalPromotionKitListSuccess({
        data: promotionKitsReducer?.length > 0 ? [...(promotionKitsReducer ?? []), ...promotionKits] : promotionKits,
        skip: pagination?.skip,
        limit: pagination?.limit,
        hasMore: promotionKits?.length > 0 && promotionKits?.length >= 10 ? true : false,
        show,
        count: promotionKitCount?.count
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
    yield put(orderActions.setLoadingData(false));
  }
}

export function* getAlternativeProduct({ productExternalCode }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const order: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);

    const { onlyWithStock, onlyWithPromotion, orderBy, clientMix }: ProductStateListInterface = yield select(
      (state: AppStateType) => state.product.list
    );

    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

    const filters = {
      clientExternalCode: order?.client?.externalCode ?? null,
      subsidiaryExternalCode: order?.orderDetails?.subsidiary?.externalCode ?? null,
      priceTableCode: order?.orderDetails?.priceTableCode ?? null,
      clientActivityExternalCode: order?.client?.activityExternalCode ?? null,
      governmentalCode: order?.client?.governmentalCode ?? null,
      applyTax: order?.client?.applyTax ? order?.client?.applyTax : null,
      applyTax2: configuration?.taxation ? order?.client?.applyTax2 : null,
      applyTaxST: configuration?.taxation ? order?.client?.applyTaxST : null,
      finalConsumer: order?.client?.finalConsumer ?? null,
      subsidiaryStockExternalCode: order?.orderDetails?.subsidiaryStock?.externalCode ?? null,
      usePaymentPlansGroup: order?.client?.usePaymentPlansGroup,
      paymentPlanExternalCode: order?.client?.usePaymentPlansGroup
        ? [
            order?.orderDetails?.paymentPlanGroup?.paymentPlan1?.externalCode,
            order?.orderDetails?.paymentPlanGroup?.paymentPlan2?.externalCode
          ].join(',')
        : order?.orderDetails?.paymentPlan?.externalCode,
      onlyWithStock,
      onlyWithPromotion,
      clientMix,
      orderBy: orderBy[0],
      orderAsc: orderBy[1],
      taxation: configuration?.taxation,
      limit: 100,
      skip: 0
    };

    const { data: alternativeProducts } = yield call(productService.getProductAlternatives, {
      productExternalCode,
      ...filters
    });

    yield put(orderActions.setProductAlternativesSuccess(alternativeProducts));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* splitOrderValidation() {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  const order: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);
  const { bonificationApproval }: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

  try {
    const parsedOrder = {
      clientExternalCode: order?.client?.externalCode,
      userExternalCode: order?.userExternalCode,
      orderDetails: {
        subsidiaryExternalCode: order?.orderDetails?.subsidiary?.externalCode,
        subsidiaryStockExternalCode: order?.orderDetails?.subsidiaryStock?.externalCode
      },
      orderItems: order?.orderItems?.map(item => ({
        productExternalCode: item?.externalCode,
        soldQuantity: item?.soldQuantity,
        soldPrice: item?.soldPrice,
        sectionExternalCode: item?.sectionExternalCode,
        bonificated: item?.bonificated,
        vlTributos: item?.tributacao?.vlTributos
      }))
    };

    const { data } = yield call(orderService.getOrderSplitValidation, {
      order: parsedOrder,
      bonificationApproval
    });

    if (data?.splitReport?.length === 0) {
      return yield put(orderActions.sendSplitOrderSuccess([]));
    }

    yield put(orderActions.sendSplitOrderSuccess(data?.splitReport));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}
export function* applyDiscountToAllItems({ payload: discountPercentage }: any) {
  try {
    notification.loading(formatMessage('commonLoadingMessage'));
    const configuration: ConfigurationInterface = yield select(selectorConfiguration);
    const { orderItems }: OrderCreateInterface = yield select((state: AppStateType) => state?.order?.form?.data);
    const { tenantID } = yield select((state: AppStateType) => state?.auth.user);

    const updatedOrderItems = yield all(
      orderItems.map(orderItem =>
        call(orderService.getSoldPrice, discountPercentage, orderItem, configuration.decimals)
      )
    );

    yield put(
      orderActions.applyDiscountToAllItemsSuccess({ items: updatedOrderItems, tenantID }, configuration?.decimals)
    );

    notification.success('Desconto aplicado a todos os itens!');
  } catch (error: any) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getRestrictionsRequest(payload: any) {
  const { closeModal } = payload;

  const order: OrderCreateInterface = yield select((state: AppStateType) => state.order.form.data);
  const { client, orderDetails, orderType } = order;
  const user: UserInterface = yield select((state: AppStateType) => state.auth.user);

  try {
    const legalEntityOrLegalPerson = client?.governmentalCode
      ? client?.governmentalCode?.length > 14
        ? 'J'
        : 'F'
      : null;

    const { data: restrictions } = yield call(productService.getSalesRestriction, {
      activityExternalCode: client?.activityExternalCode ?? null,
      legalEntityOrLegalPerson,
      minSaleValue: 0,
      barCode: null,
      brandExternalCode: null,
      clientExternalCode: client?.externalCode ?? null,
      departmentExternalCode: null,
      orderTypeExternalCode: orderType?.externalCode ?? null,
      paymentOptionExternalCode: orderDetails?.paymentOption?.externalCode ?? null,
      paymentPlanExternalCode: orderDetails?.paymentPlan?.externalCode ?? null,
      priceTableCode: orderDetails?.priceTableCode ?? null,
      productExternalCode: null,
      providerExternalCode: null,
      roleExternalCode: user?.roleExternalCode ?? null,
      sectionExternalCode: null,
      squareExternalCode: client?.squareExternalCode ?? null,
      subsidiaryExternalCode: orderDetails?.subsidiary?.externalCode ?? null,
      userExternalCode: user?.externalCode ?? null,
      shipping: orderDetails?.shippingType === SHIPPING_TYPE.FREE ? 'F' : 'C'
    });

    if (
      restrictions?.length > 0 &&
      restrictions?.some(restriction => restriction?.minSaleValue === 0 || restriction?.minSaleValue === null)
    ) {
      const errorMessage = {
        message: restrictions[0].restrictionReason
          ? formatMessage('orderWarningRestrictionWithReason', {
              restrictionExternalCode: restrictions[0].externalCode,
              restrictionReason: restrictions[0].restrictionReason
            })
          : formatMessage('orderWarningRestrictionWithoutReason', {
              restrictionExternalCode: restrictions[0].externalCode
            })
      };

      if (closeModal) {
        yield put(
          orderActions.setShowModalProductRequest({
            show: false
          })
        );
      }

      throw errorMessage;
    }

    return { restrictions };
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* setSubsidiaryProductStock({ payload }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  const orderItem = yield select((state: AppStateType) => state.order.form.orderItem);
  try {
    if (orderItem._id) {
      yield call(loadProduct, {
        payload: {
          ...orderItem,
          subsidiaryStockExternalCode: payload.externalCode,
          subsidiaryStock: payload,
          id: orderItem?._id
        }
      });
    } else {
      yield call(loadProduct, {
        payload: {
          ...orderItem,
          subsidiaryStockExternalCode: payload.externalCode,
          subsidiaryStock: payload
        },
        id: orderItem?._id
      });
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast?.close();
  }
}

export function* getSubsidiaryStockRestrictions({ payload }: any) {
  const { subsidiaryExternalCode, subsidiaryStockExternalCode, clientState } = payload;
  try {
    if (subsidiaryExternalCode && subsidiaryStockExternalCode && clientState) {
      const { data } = yield call(domainService.getSubsidiaryRestrictions, {
        subsidiaryExternalCode,
        subsidiaryStockExternalCode,
        clientState
      });
      const restrictions = data[0];
      yield put(orderActions.setSubsidiaryStockRestrictions(restrictions));
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* updateUnitaryValueRequest({ payload }: any) {
  try {
    const { orderItem }: { orderItem: OrderItemCreateInterface } = yield select(
      (state: AppStateType) => state?.order?.form
    );

    const { orderDetails, client }: OrderCreateInterface = yield select(
      (state: AppStateType) => state?.order?.form?.data
    );

    const user: UserInterface = yield select((state: AppStateType) => state?.auth?.user);

    const configuration: ConfigurationInterface = yield select(selectorConfiguration);

    const unitaryValue = Math.max(payload, 0);

    const discountPercentage = orderService.buildDiscountPercentage({
      baseValue: orderItem?.baseValue,
      precision: configuration?.decimals ?? 2,
      unitaryValue
    });

    const newSoldPrice = orderService.buildSoldPrice({
      packingQuantity: orderItem?.packing?.packingQuantity,
      precision: configuration?.decimals ?? 2,
      unitaryValue
    });

    const newTotalPrice = orderService.buildTotalPrice({
      soldQuantity: orderItem?.soldQuantity,
      precision: configuration?.decimals ?? 2,
      soldPrice: newSoldPrice
    });

    let tributacao = null;
    if (configuration?.taxation) {
      tributacao = getTax({
        client,
        configuration,
        paymentPlan: orderDetails?.paymentPlan,
        product: orderItem as unknown as ProductSale & OrderItemCreateInterface,
        shippingType: orderDetails?.shippingType,
        soldPrice: newSoldPrice,
        soldQuantity: orderItem?.soldQuantity,
        tenantID: user.tenantID,
        // @ts-ignore
        tributacao: orderItem?.tributacao?.data,
        vendorDiscountPercentage: discountPercentage
      });
    }

    const newOrderItem = {
      ...orderItem,
      tributacao,
      unitaryValue,
      soldPrice: newSoldPrice,
      discountPercentage,
      totalPrice: newTotalPrice,
      totalPriceWithPacking: newTotalPrice
    };

    yield put(orderActions.loadProductSuccess(newOrderItem));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export default all([
  takeLatest(INITIALIZE_REQUEST, initialize),
  takeLatest(SET_CLIENT_REQUEST, setClient),
  takeLatest(VALIDATE_PASSWORD_MODAL_REASON, validatePasswordModalReason),
  takeLatest(SET_ORDER_TYPE_REQUEST, setOrderType),
  takeLatest(SET_ORDER_BONIFICATION_REQUEST, setOrderBonification),
  takeLatest(SET_SHIPPING_TYPE_REQUEST, setShippingType),
  takeLatest(GET_SHIPPING_REQUEST, loadShipping),
  takeLatest(SET_SUBSIDIARY_REQUEST, setSubsidiary),
  takeLatest(SET_SUBSIDIARY_STOCK_REQUEST, setSubsidiaryStock),
  takeLatest(SET_PAYMENT_OPTION_REQUEST, setPaymentOption),
  takeLatest(SET_PAYMENT_PLAN_REQUEST, setPaymentPlan),
  takeLatest(SET_PAYMENT_PLAN_GROUP_REQUEST, setPaymentPlanGroup),
  takeLatest(SET_LOAD_TYPE_REQUEST, setLoadType),
  takeLatest(ADD_PRODUCT_CART_REQUEST, addProductCart),
  takeLatest(REMOVE_PRODUCT_CART_REQUEST, removeProductCart),
  takeLatest(LOAD_PRODUCT_REQUEST, loadProduct),
  takeLatest(UPDATE_PRODUCT_REQUEST, updateProduct),
  takeLatest(SET_PRODUCT_COMPLEMENT, setProductComplement),
  takeLatest(VALIDATE_ORDER_REQUEST, validadeOrder),
  takeLatest(SET_BONIFICATED_REQUEST, setBonificated),
  takeLatest(SET_ORDER_TYPE_BONIFICATED_REQUEST, setOrderTypeBonificated),
  takeLatest(UPDATE_QUANTITY_REQUEST, updateQuantity),
  takeLatest(UPDATE_SOLDPRICE_REQUEST, updateSoldPrice),
  takeLatest(UPDATE_DISCOUNT_REQUEST, updateDiscount),
  takeLatest(APPLY_DISCOUNT_TO_ALL_ITEMS_REQUEST, applyDiscountToAllItems),
  takeLatest(UPDATE_QUANTITY_TOTAL_UNIT_REQUEST, updateTotalUnity),
  takeLatest(SET_PRODUCT_PACKING, setPacking),
  takeLatest(SET_PRODUCT_PROMOTION, setPromotion),
  takeLatest(GET_PRODUCT_PROMOTION_KIT_REQUEST, getPromotionKit),
  takeLatest(ADD_PROMOTION_KIT_REQUEST, setPromotionKitAddCart),
  takeLatest(UPDATE_PROMOTION_KIT_REQUEST, updatePromotionKit),
  takeLatest(GET_PROMOTION_KIT_LIST_REQUEST, getPromotionKitListModal),
  takeLatest(GET_ALTERNATIVE_PRODUCT_REQUEST, getAlternativeProduct),
  takeLatest(SET_SHOW_MODAL_PRODUCT_REQUEST, getProductModal),
  takeLatest(SET_SUBSIDIARY_STOCK_RESTRICTIONS_REQUEST, getSubsidiaryStockRestrictions),
  takeLatest(SPLIT_ORDER_VALIDATION_REQUEST, splitOrderValidation),
  takeLatest(SEND_ORDER, sendOrder),
  takeLatest(CALCULATE_BALANCE_REQUEST, calculateBalance),
  takeLatest(GET_RESTRICTIONS_REQUEST, getRestrictionsRequest),
  takeLatest(SET_SUBSIDIARY_PRODUCT_STOCK_REQUEST, setSubsidiaryProductStock),
  takeLatest(UPDATE_UNITARY_VALUE_REQUEST, updateUnitaryValueRequest)
]);
